import React, { useState, useEffect, useRef, useMemo, useLayoutEffect, useCallback } from 'react';
import { View, Text, ScrollView, TouchableOpacity, TouchableWithoutFeedback, Image, ImageBackground, TextInput, Alert, StyleSheet, Platform, ActivityIndicator, Modal, LayoutAnimation, UIManager, Animated } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { getFirestore, doc, updateDoc, arrayUnion, getDoc, increment, deleteDoc, setDoc, serverTimestamp, query, collection, getDocs, orderBy, where, addDoc, onSnapshot } from 'firebase/firestore';
import {
  LIVE_COMMUNITY_STATUS,
  LIVE_COMMUNITY_SOURCE,
  isSignedInAccount,
  isPublicLiveCommunityReport,
  sortLiveReportsNewestFirst,
} from '../utils/liveTechSpecs';
import { resolveInterestDisplayItems } from '../utils/interestUtils';
import { CardInterestsDisplay } from '../components/CardInterestsDisplay';
import ReportCardChalkboard from '../components/venue/ReportCardChalkboard';
import { subscribeApprovedVenueInterestTagDocs, mergeApprovedVenueInterestLabels } from '../utils/venueInterestTagsService';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { getAuth, signInAnonymously } from 'firebase/auth';
import { SearchBar } from '../components/SearchBar';
import { GradeCard } from '../components/GradeCard';
import { CategoryButton } from '../components/CategoryButton';
import { globalStyles } from '../styles/globalStyles';
import { venueStyles } from '../styles/venueStyles';
import { useVenues } from '../components/VenueContext';
import { DiscussionBoard } from '../components/discussion/DiscussionBoard';
import { homeStyles } from '../styles/HomeStyles';
import { VenueReportForm } from '../components/VenueReportForm';
import { extraGradeDisplayFields } from '../utils/extraGradeLines';
import { BackgroundVideo } from '../components/BackgroundVideo';
import { colors } from '../styles/theme';
import { useTheme } from '../contexts/ThemeContext';
import { BREAKPOINTS, getResponsiveValue } from '../styles/breakpoints';
import { Ionicons } from '@expo/vector-icons';
import { extractAddressFromMapEmbed, extractCoordinatesFromMapEmbed, extractGooglePlaceRefFromMapEmbed, formatEmbedResolveStatus, resolveEmbedCoordinatesForSave } from '../utils/mapEmbedUtils';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MAPBOX_ACCESS_TOKEN } from '../config/mapbox-config';
import { CustomModal } from '../components/Modal';
import { Toast } from '../components/Toast';
import { FullscreenMapModal } from '../components/FullscreenMapModal';
import { RESOURCE_CATEGORIES, getResourceCategoryById, getResourceCategoryLabel, getResourceCategoryIconSize } from '../constants/resourceCategories';
import {
  MERCH_RESOURCE_CATEGORY,
  resourceCategoryIdFromLabel,
  subscribeResourceCategoryCatalog,
} from '../utils/resourceCategoryCatalog';
import { getPagePaletteIfEnabled } from '../constants/pageColors';
import { useUpdatedColorScheme } from '../contexts/FeatureFlagsContext';
import { useAppLayout } from '../contexts/AppLayoutContext';

// Set Mapbox access token
mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

import { useSystemStyles } from '../hooks/useSystemStyles';
import { findRelevantManuals } from '../utils/findRelevantManuals';
import { formatExternalUrl } from '../utils/formatExternalUrl';
import { isRealUser } from '../utils/mapAccess';
import { ensureVenueInUserUploads, ensureVenueInRoomNotesFolder, addRoomEqSubmissionLibraryFile, isVenueSaved, saveVenueToSavedVenues, removeVenueFromSavedVenues } from '../utils/fileManagerUtils';
import { useAdmin } from '../contexts/AdminContext';
import { usePopup } from '../contexts/PopupContext';
import * as DocumentPicker from 'expo-document-picker';
import ProfileTabbedMediaManager from '../components/ProfileTabbedMediaManager';
import { RoomEqForm } from '../components/RoomEqForm';
import { RoomEqViewerModal } from '../components/RoomEqViewerModal';
import { RoomEqPhoto } from '../components/RoomEqPhoto';
import { PhotoSlotsUpload } from '../components/PhotoSlotsUpload';
import { AutoGrowTextInput } from '../components/AutoGrowTextInput';
import { useUser } from '../components/LoginPersistence';
import { useTourPlannerNavVisible } from '../contexts/FeatureFlagsContext';
import { useInterestCatalog } from '../hooks/useInterestCatalog';
import { MediaManagerStyles as mediaManagerStyles } from '../styles/MediaManagerStyles';

const getRoomEqMediaType = (url) => {
  if (!url || typeof url !== 'string') return 'image';
  const ext = url.split('.').pop()?.toLowerCase()?.split('?')[0] || '';
  if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(ext)) return 'video';
  return 'image';
};

const getLetterGrade = (grade) => {
  if (!grade) return '';

  // If it's already a letter grade
  if (typeof grade === 'string') {
    const validGrade = grade.toUpperCase();
    if (['A', 'B', 'C', 'D', 'F'].includes(validGrade)) {
      return validGrade;
    }
  }

  // If it's a numeric grade
  if (typeof grade === 'number' || !isNaN(Number(grade))) {
    const numericGrade = Number(grade);
    if (numericGrade >= 4.5) return 'A';
    if (numericGrade >= 3.5) return 'B';
    if (numericGrade >= 2.5) return 'C';
    if (numericGrade >= 1.5) return 'D';
    if (numericGrade >= 1) return 'F';
  }

  return '';
};

/**
 * Spec sheets the admin editor starts from. A venue that only has the legacy
 * specSheetLink starts with that link as a URL entry, so saving keeps it (REQ-101).
 */
export function initialEditableSpecSheets(venue) {
  if (Array.isArray(venue?.specSheets)) return [...venue.specSheets];
  const legacy = typeof venue?.specSheetLink === 'string' ? venue.specSheetLink.trim() : '';
  return legacy ? [{ name: "Venue's Spec Link", specType: 'url', url: legacy }] : [];
}

/** Aligns stored userType with VenueReportForm / MyReports. */
const normalizeVenueReportUserType = (userType) => {
  const t = (userType || '').toLowerCase();
  if (t === 'touring_crew') return 'touring';
  return t;
};

/** Technical fields (VenueReportForm tech specs + capacity + power). Power sits right under Stage (REQ-092). */
const VENUE_REPORT_TECH_SPEC_FIELDS = [
  { label: 'Capacity', field: 'capacity' },
  { label: 'ADA Accessibility for Artist', field: 'adaAccessibilityArtist' },
  { label: 'Parking', field: 'parking' },
  { label: 'Shore Power', field: 'shorePower' },
  { label: 'Backstage', field: 'backstage' },
  { label: 'Merch', field: 'merch' },
  { label: 'Stage', field: 'stage' },
  { label: 'Power', field: 'power' },
  { label: 'Backline', field: 'backline' },
  { label: 'Video', field: 'video' },
  { label: 'Monitors', field: 'monitors' },
  { label: 'PA System', field: 'pa' },
  { label: 'FOH', field: 'foh' },
  { label: 'Lighting Control', field: 'lightingControl' },
  { label: 'Lighting Fixtures', field: 'lightingFixtures' },
];

const SPEC_SHEET_NAV_OFFSET = 84;
const SPEC_SHEET_NAV_OFFSET_EDIT = 112;
const SPEC_SHEET_PANEL_PADDING = 12;
const SPEC_SHEET_HEADER_LEFT_WIDTH = 134;
const SPEC_SHEET_PANEL_DARK_BG = '#152238';
const SPEC_NAV_BLUE = '#4e7de0';
// Spec sheet values in the venue page's blue (REQ-092). The page blue (#3D7FE6) is
// too light for body text on the panel, so each mode uses a shade of it that keeps
// AA contrast: 5.2:1 on the light panel (#E9E9E9), 6.6:1 on the dark panel (#152238).
const SPEC_VALUE_BLUE_LIGHT = '#2A5DB8';
const SPEC_VALUE_BLUE_DARK = '#7EA6F5';
const SPEC_NAV_ICON_GRAY = 'rgba(107, 114, 128, 0.55)';

const EDUCATION_TOOLBOX_CATEGORY = getResourceCategoryById('education');

const readImageDimensions = (file) => new Promise((resolve) => {
  if (!file) {
    resolve({ width: null, height: null });
    return;
  }
  if (typeof window !== 'undefined' && window.Image) {
    const url = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({ width: null, height: null });
    };
    img.src = url;
    return;
  }
  const uri = file.uri || file;
  if (uri && Image?.getSize) {
    Image.getSize(
      uri,
      (width, height) => resolve({ width, height }),
      () => resolve({ width: null, height: null })
    );
    return;
  }
  resolve({ width: null, height: null });
});

const ResourceCoverImage = ({ url, width, height, backgroundColor = '#f0f0f0' }) => {
  const aspectRatio = width && height ? width / height : 2.5;
  return (
    <View style={{ width: '100%', aspectRatio, backgroundColor, overflow: 'hidden' }}>
      <Image source={{ uri: url }} style={{ width: '100%', height: '100%' }} resizeMode="contain" />
    </View>
  );
};

const HexagonIcon = ({ size = 17, color = '#e5e7eb' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden="true">
    <path d="M12 2.5 21 7.5v9L12 21.5 3 16.5v-9L12 2.5z" />
  </svg>
);

const SPEC_NAV_LINKS_BG = '#f5f5f0';
const SPEC_NAV_RESOURCES_BG = '#21A06D';
const SPEC_ICON_BTN_SIZE = 34;
/** Links & Manuals buttons */
const SPEC_LINK_BUTTON_BG = '#F9E258';
const MANUAL_LINK_BUTTON_BG = '#21A06D';
const LINK_BUTTON_TEXT = '#292C35';
/** Lauren 2026-10-21: Links & Manuals go back to outlined (white fill, colour as the border) so the text reads. */
const outlinedLink = (color) => ({ backgroundColor: '#ffffff', borderWidth: 2, borderColor: color === SPEC_LINK_BUTTON_BG ? '#E0C431' : color });

const FOOTER_FLAG_CATEGORIES = RESOURCE_CATEGORIES.map(({ id, label }) => ({ id, label }));
/** Environment + Education publish immediately (no admin review). */
const VENUE_INSTANT_SPEC_FLAG_CATEGORIES = FOOTER_FLAG_CATEGORIES.filter(
  (cat) => cat.id === 'environment' || cat.id === 'education'
);
const VENUE_INSTANT_SPEC_FLAG_CATEGORY_IDS = new Set(
  VENUE_INSTANT_SPEC_FLAG_CATEGORIES.map((cat) => cat.id)
);

function isInstantSpecFlagCategory(categoryId) {
  return VENUE_INSTANT_SPEC_FLAG_CATEGORY_IDS.has(categoryId);
}

const getVenueReportRatingFields = (userType) => {
  const u = normalizeVenueReportUserType(userType);
  if (u === 'technical') return [];
  if (u === 'artist') {
    return [
      { label: 'Musical Equipment', field: 'musicalEquipmentGrade' },
      { label: 'Stage Safety', field: 'stageSafetyGrade' },
      { label: 'Hospitality', field: 'hospitality' },
      { label: 'Backstage', field: 'backstage' },
      { label: 'Venue Promotion', field: 'venuePromotion' },
      { label: 'Audio Staff', field: 'audioStaff' },
      { label: 'Lighting Staff', field: 'lightingStaff' },
      { label: 'Pay', field: 'pay' },
    ];
  }
  if (u === 'employee') {
    return [
      { label: 'Audio Equipment', field: 'audioEquipment' },
      { label: 'Lighting Equipment', field: 'lightingEquipment' },
      { label: 'Workplace Cleanliness', field: 'workplaceCleaniness' },
      { label: 'Overall Safety', field: 'overallSafety' },
      { label: 'Pay structure vs other venues', field: 'payComparison' },
    ];
  }
  if (u === 'touring') {
    return [
      { label: 'Venue Management', field: 'venueManagement' },
      { label: 'Venue Staff', field: 'venueStaff' },
      { label: 'Audio Staff', field: 'audioStaff' },
      { label: 'Lighting Staff', field: 'lightingStaff' },
      { label: 'Audio Equipment', field: 'audioEquipment' },
      { label: 'Lighting Equipment', field: 'lightingEquipment' },
      { label: 'Overall Safety', field: 'overallSafety' },
      { label: 'City Walkability', field: 'cityWalkability' },
    ];
  }
  return [
    { label: 'Safety', field: 'safetyRating' },
    { label: 'Equipment', field: 'equipmentRating' },
    { label: 'Staff', field: 'staffRating' },
    { label: 'Working Conditions', field: 'workingConditionsRating' },
  ];
};

const getVenueReportTextDetailFields = (userType) => {
  const u = normalizeVenueReportUserType(userType);
  if (u === 'artist') {
    return [
      { label: 'Date or year of performance', field: 'dateOfPerformance' },
      { label: 'Band name', field: 'bandName' },
      { label: 'Show promoter', field: 'promoter' },
      { label: 'Ticket price', field: 'ticketPrice' },
      { label: 'Total tickets sold', field: 'totalTicketsSold' },
      { label: 'Payout structure', field: 'payoutStructure' },
      { label: "Band take-home (tickets)", field: 'bandTakeHomeAmount' },
      { label: 'Venue merch cut', field: 'venueMerchCut' },
      { label: 'Musical equipment provided', field: 'musicalEquipment' },
      { label: 'Stage safety notes', field: 'stageSafety' },
    ];
  }
  if (u === 'employee') {
    return [
      { label: 'Dates worked', field: 'datesWorked' },
      { label: 'Job title', field: 'jobTitle' },
      { label: 'Pay type', field: 'payType' },
      { label: 'Pay amount', field: 'payAmount' },
      { label: 'Overtime policy', field: 'overtimeType' },
      { label: 'Overtime hours threshold', field: 'overtimeHours' },
      { label: 'Overtime (other)', field: 'overtimeOther' },
      { label: 'Union name', field: 'unionName' },
    ];
  }
  if (u === 'touring') {
    return [
      { label: 'Job title', field: 'jobTitle' },
      { label: 'Favorite nearby spots', field: 'favoriteShop' },
      { label: 'Day rate', field: 'dayRate' },
    ];
  }
  if (u === 'other') {
    return [{ label: 'Role', field: 'role' }];
  }
  return [];
};

const formatReportDetailValue = (value) => {
  if (value === undefined || value === null) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return '';
  return String(value).trim();
};

/** Single stored letter A–F (artist backstage grade must not appear as tech text). */
const isSingleLetterGrade = (value) => {
  const s = String(value ?? '').trim();
  return /^[A-F]$/i.test(s);
};

const isArtistBackstageGradeNotTechText = (userNorm, field, value) =>
  userNorm === 'artist' && field === 'backstage' && isSingleLetterGrade(value);

const getVenueReportRatingValue = (report, field) => {
  if (!report) return undefined;
  if (field === 'workplaceCleaniness') {
    return report.workplaceCleaniness ?? report.workplaceCleanliness;
  }
  return report[field];
};

/** Feature tags shown on the venue card; filterValue is the matching VenueList feature filter. */
const FEATURE_BADGES = [
  { key: 'backline', label: 'Backline', filterValue: 'hasBackline' },
  { key: 'parking', label: 'Parking', filterValue: 'hasParking' },
  { key: 'rehearsal', label: 'Rehearsal', filterValue: 'hasRehearsal' },
  { key: 'pinball', label: 'Pinball!', filterValue: 'hasPinball' },
  { key: 'merch100', label: '100% Merch', filterValue: 'merch100' },
  { key: 'stageFloor', label: 'Floor Stage', filterValue: 'stageFloor' },
  { key: 'shower', label: 'Shower', filterValue: 'hasShower' },
  { key: 'laundry', label: 'Laundry', filterValue: 'hasLaundry' },
];

const hasFeature = (venue, feature) => {
  if (!venue) return false;
  switch (feature) {
    case 'backline':
      return venue.backline?.toLowerCase().includes('yes') || (venue.backline?.length > 0 && !venue.backline?.toLowerCase().includes('no'));
    case 'parking':
      return venue.parking?.toLowerCase().includes('yes') || (venue.parking?.length > 0 && !venue.parking?.toLowerCase().includes('no'));
    case 'rehearsal':
      return venue.backstage?.toLowerCase().includes('rehearsal');
    case 'pinball':
      return venue.backstage?.toLowerCase().includes('pinball') || venue.notes?.toLowerCase().includes('pinball');
    case 'merch100':
      return venue.merch?.toLowerCase().includes('100%');
    case 'stageFloor':
      return venue.stage?.toLowerCase().includes('floor');
    case 'shower':
      return venue.backstage?.toLowerCase().includes('shower');
    case 'laundry':
      return venue.backstage?.toLowerCase().includes('laundry');
    default:
      return false;
  }
};

const calculateAverageGrade = (ratings) => {
  const gradePoints = {
    'A': 5,
    'B': 4,
    'C': 3,
    'D': 2,
    'F': 1
  };

  const validRatings = Object.values(ratings).filter(rating => {
    if (typeof rating === 'string' && ['A', 'B', 'C', 'D', 'F'].includes(rating.toUpperCase())) {
      return true;
    }
    if (typeof rating === 'number' || !isNaN(Number(rating))) {
      const num = Number(rating);
      return num >= 1 && num <= 5;
    }
    return false;
  });

  if (validRatings.length === 0) return null;

  const sum = validRatings.reduce((acc, rating) => {
    if (typeof rating === 'string') {
      return acc + gradePoints[rating.toUpperCase()];
    }
    return acc + Number(rating);
  }, 0);

  const average = sum / validRatings.length;

  // Convert numeric average back to letter grade
  if (average >= 4.5) return 'A';
  if (average >= 3.5) return 'B';
  if (average >= 2.5) return 'C';
  if (average >= 1.5) return 'D';
  return 'F';
};

export function VenueScreen({ route, navigation }) {
  const { venueId, fromSubmitReport = false, scrollToDiscussion = false } = route.params || {};
  const { venues } = useVenues();
  const { theme, isDarkMode } = useTheme();
  const updatedColorScheme = useUpdatedColorScheme();
  const venuePalette = getPagePaletteIfEnabled('venue', isDarkMode, updatedColorScheme);
  const { setPageSchemeOverride } = useAppLayout();

  useEffect(() => {
    if (updatedColorScheme) {
      setPageSchemeOverride('venue');
    } else {
      setPageSchemeOverride(null);
    }
    return () => setPageSchemeOverride(null);
  }, [updatedColorScheme, setPageSchemeOverride]);
  const [venueData, setVenueData] = useState(null);
  const [discussionData, setDiscussionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [currentView, setCurrentView] = useState('manuals');
  const [activeSection, setActiveSection] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCategories, setShowCategories] = useState(false);
  const [showReportForm, setShowReportForm] = useState(fromSubmitReport);
  const [isSaved, setIsSaved] = useState(false);
  const [isSubmitHovered, setIsSubmitHovered] = useState(false);
  const [isSaveHovered, setIsSaveHovered] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [userType, setUserType] = useState(null);
  const [addToTourHovered, setAddToTourHovered] = useState(false);
  const [votes, setVotes] = useState({ up: 0, down: 0 });
  const [titleVotes, setTitleVotes] = useState({ up: 0, down: 0 });
  const [specsVotes, setSpecsVotes] = useState({ up: 0, down: 0 });
  const [linksVotes, setLinksVotes] = useState({ up: 0, down: 0 });
  const [userVote, setUserVote] = useState(null);
  const [userTitleVote, setUserTitleVote] = useState(null);
  const [userSpecsVote, setUserSpecsVote] = useState(null);
  const [userLinksVote, setUserLinksVote] = useState(null);
  const [messages, setMessages] = useState([]);
  const [hoveredButton, setHoveredButton] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [activeDiscussionTab, setActiveDiscussionTab] = useState('discussions');
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [showFolderSelectModal, setShowFolderSelectModal] = useState(false);
  const [folderDropdownOpen, setFolderDropdownOpen] = useState(false);
  const [folderModalOpenWithCreate, setFolderModalOpenWithCreate] = useState(false);
  /** Library tab for the folder picker modal; only the tour folder flows open it ('tours' = My Tours root). */
  const [folderSelectModalLibraryTab, setFolderSelectModalLibraryTab] = useState('tours');
  const { user: userData, setUser } = useUser();
  const showTourPlannerNav = useTourPlannerNavVisible(userData);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isEditingManuals, setIsEditingManuals] = useState(false);
  const [editableManuals, setEditableManuals] = useState([]);
  const [showManualSelector, setShowManualSelector] = useState(false);
  const [manualSearchQuery, setManualSearchQuery] = useState('');
  const [availableManuals, setAvailableManuals] = useState([]);
  const [loadingManuals, setLoadingManuals] = useState(false);
  const [isEditingSpecSheets, setIsEditingSpecSheets] = useState(false);
  const [editableSpecSheets, setEditableSpecSheets] = useState([]);
  const [uploadingSpecIndex, setUploadingSpecIndex] = useState(null);
  const [savingManuals, setSavingManuals] = useState(false);
  const [savingSpecSheets, setSavingSpecSheets] = useState(false);
  const [approvedReports, setApprovedReports] = useState([]);
  const [specsTab, setSpecsTab] = useState('vrc'); // 'vrc' | 'live'
  const [liveSpecsReports, setLiveSpecsReports] = useState([]);
  const [liveSpecsIndex, setLiveSpecsIndex] = useState(0);
  const [liveSpecsDraft, setLiveSpecsDraft] = useState({});
  const [liveSpecsCreateMode, setLiveSpecsCreateMode] = useState(false);
  // Edit an existing LIVE report the current user owns (distinct from create-new).
  const [liveSpecsEditingExisting, setLiveSpecsEditingExisting] = useState(false);
  const [liveSpecsSubmitting, setLiveSpecsSubmitting] = useState(false);
  const [liveSpecsDeleting, setLiveSpecsDeleting] = useState(false);
  const [showReportsModal, setShowReportsModal] = useState(false);
  const [mobileOptionsToggle, setMobileOptionsToggle] = useState(null); // 'share' | 'view' | null
  const shareExpandedAnim = useRef(new Animated.Value(0)).current;
  const viewExpandedAnim = useRef(new Animated.Value(0)).current;
  const [chatroomOpen, setChatroomOpen] = useState(false);
  const [showRoomEqModal, setShowRoomEqModal] = useState(false);
  const [shareEqActiveTab, setShareEqActiveTab] = useState('EQ');
  const [rfTextItems, setRfTextItems] = useState(['', '', '', '']);
  const [rfPhotos, setRfPhotos] = useState([{ file: null, preview: null, description: '' }]);
  const [addTabTitle, setAddTabTitle] = useState('');
  const [addTabType, setAddTabType] = useState(null);
  const [addTabConfirmed, setAddTabConfirmed] = useState(false);
  const [addTabRfTextItems, setAddTabRfTextItems] = useState(['', '']);
  const [addTabRfPhotos, setAddTabRfPhotos] = useState([{ file: null, preview: null, description: '' }]);
  const [rfSaving, setRfSaving] = useState(false);
  const [addTabRfSaving, setAddTabRfSaving] = useState(false);
  const [roomEqViewerSlides, setRoomEqViewerSlides] = useState([]);
  const [publicRoomEqs, setPublicRoomEqs] = useState([]);
  const [roomEqViewerVisible, setRoomEqViewerVisible] = useState(false);
  const [roomEqViewerIndex, setRoomEqViewerIndex] = useState(0);
  const [roomEqViewerMode, setRoomEqViewerMode] = useState('slides'); // 'slides' | 'submission'
  const [roomEqViewerSubmissions, setRoomEqViewerSubmissions] = useState([]); // all docs with type 'roomEqSubmission'
  const [roomEqViewerSubmissionIndex, setRoomEqViewerSubmissionIndex] = useState(0);
  const [roomEqViewerActiveTab, setRoomEqViewerActiveTab] = useState('EQ');
  const [showNoRoomEqPopup, setShowNoRoomEqPopup] = useState(false);
  const roomEqFormRef = useRef(null);
  const addTabEqFormRef = useRef(null);
  const [saveAllLoading, setSaveAllLoading] = useState(false);
  const [mapFullscreen, setMapFullscreen] = useState(false);
  // Lauren (2026-11): new Room EQ shares default to not public; the owner opts in.
  const [roomEqIsPublic, setRoomEqIsPublic] = useState(false);
  const [editSubmissionId, setEditSubmissionId] = useState(null);
  const [resourceModalStep, setResourceModalStep] = useState(null);
  const [resourceSubmitting, setResourceSubmitting] = useState(false);
  const [approvedResources, setApprovedResources] = useState([]);
  const [resourceCategoryCatalog, setResourceCategoryCatalog] = useState([
    ...RESOURCE_CATEGORIES,
    MERCH_RESOURCE_CATEGORY,
  ]);
  const venueResourceCategories = resourceCategoryCatalog.filter(
    (category) => category.id !== MERCH_RESOURCE_CATEGORY.id
  );
  const [resourceCustomCategory, setResourceCustomCategory] = useState('');
  useEffect(() => {
    return subscribeResourceCategoryCatalog(
      getFirestore(),
      setResourceCategoryCatalog,
      (error) => console.error('Error loading resource categories:', error),
    );
  }, []);
  const [resourceForm, setResourceForm] = useState({
    name: '',
    url: '',
    description: '',
    scope: 'venue',
    category: '',
    mapEmbedLink: '',
  });
  const [resourcePhoto, setResourcePhoto] = useState({ file: null, preview: null, width: null, height: null });
  const [resourceEmbedStatus, setResourceEmbedStatus] = useState({ state: 'idle', text: '' });
  const resourceEmbedResolveTimer = useRef(null);
  const [toast, setToast] = useState(null);
  const [showSpecFlagModal, setShowSpecFlagModal] = useState(false);
  const [specFlagCategory, setSpecFlagCategory] = useState('education');
  const [specFlagNotes, setSpecFlagNotes] = useState('');
  const [specFlagSubmitting, setSpecFlagSubmitting] = useState(false);
  const [showVenueInterestsModal, setShowVenueInterestsModal] = useState(false);
  const [venueInterestSelection, setVenueInterestSelection] = useState([]);
  const [venueInterestsSubmitting, setVenueInterestsSubmitting] = useState(false);
  const [showVenueDocumentUploadModal, setShowVenueDocumentUploadModal] = useState(false);
  const [venueDocumentFiles, setVenueDocumentFiles] = useState([]);
  const [venueDocumentSubmitting, setVenueDocumentSubmitting] = useState(false);
  const [venueDocumentDropActive, setVenueDocumentDropActive] = useState(false);
  const venueDocumentInputRef = useRef(null);
  const [venueArchiveReason, setVenueArchiveReason] = useState(null);
  const [venueArchiveDetails, setVenueArchiveDetails] = useState('');
  const [venueArchiveSubmitting, setVenueArchiveSubmitting] = useState(false);
  const [hoveredArchiveReason, setHoveredArchiveReason] = useState(null);
  const [approvedVenueInterestDocs, setApprovedVenueInterestDocs] = useState([]);
  const { interests: interestCatalog } = useInterestCatalog();
  const approvedVenueInterestItems = useMemo(
    () => resolveInterestDisplayItems(
      mergeApprovedVenueInterestLabels(approvedVenueInterestDocs),
      interestCatalog
    ),
    [approvedVenueInterestDocs, interestCatalog]
  );

  // Inline "Add a Location" (no navigation to TourPlanner)
  const [showAddLocationPanel, setShowAddLocationPanel] = useState(false);
  const [addLocationCoords, setAddLocationCoords] = useState(null);
  const [addLocationFormData, setAddLocationFormData] = useState({
    name: '',
    notes: '',
    category: 'other',
    address: '',
    isPublic: false
  });
  const [addressSearchQuery, setAddressSearchQuery] = useState('');
  const [addressSearchResults, setAddressSearchResults] = useState([]);
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);
  const addLocationSearchDebounceRef = useRef(null);

  const LOCATION_CATEGORIES = [
    { id: 'venue', label: 'Venue', icon: '🎤' },
    { id: 'hotel', label: 'Hotel/Lodging', icon: '🏨' },
    { id: 'restaurant', label: 'Restaurant/Food', icon: '🍽️' },
    { id: 'gas', label: 'Gas Station', icon: '⛽' },
    { id: 'parking', label: 'Parking', icon: '🅿️' },
    { id: 'meeting', label: 'Meeting Point', icon: '📍' },
    { id: 'airport', label: 'Airport', icon: '✈️' },
    { id: 'store', label: 'Store/Shop', icon: '🛒' },
    { id: 'other', label: 'Other', icon: '📌' }
  ];

  // Enable LayoutAnimation on Android for expand/collapse
  useEffect(() => {
    if (Platform.OS === 'android' && UIManager.setLayoutAnimationEnabledExperimental) {
      UIManager.setLayoutAnimationEnabledExperimental(true);
    }
  }, []);

  const MOBILE_OPTIONS_ANIM_DURATION = 280;
  useEffect(() => {
    Animated.parallel([
      Animated.timing(shareExpandedAnim, {
        toValue: mobileOptionsToggle === 'share' ? 1 : 0,
        duration: MOBILE_OPTIONS_ANIM_DURATION,
        useNativeDriver: true,
      }),
      Animated.timing(viewExpandedAnim, {
        toValue: mobileOptionsToggle === 'view' ? 1 : 0,
        duration: MOBILE_OPTIONS_ANIM_DURATION,
        useNativeDriver: true,
      }),
    ]).start();
  }, [mobileOptionsToggle, shareExpandedAnim, viewExpandedAnim]);

  const [venueMapStyle, setVenueMapStyle] = useState('streets-v11');
  const [venueMapContainerReady, setVenueMapContainerReady] = useState(false);
  const venueMapContainerRef = useRef(null);
  const venueMapRef = useRef(null);
  const [subcategoryGrades, setSubcategoryGrades] = useState({
    money: null,
    safety: null,
    hospitality: null
  });

  const auth = getAuth();
  const { getStyle } = useSystemStyles();
  const { showConfirmation } = usePopup();

  const ensureVenueContributorAuth = useCallback(async () => {
    if (auth.currentUser) return true;
    try {
      await signInAnonymously(auth);
      return Boolean(auth.currentUser);
    } catch (error) {
      console.error('Anonymous sign-in failed for venue contributor action', error);
      setToast({ message: 'Could not verify your session. Please try again.', type: 'error' });
      return false;
    }
  }, [auth]);

  const specSheetInnerTextColor = isDarkMode ? '#f0f4ff' : theme.text.primary;
  const specSheetValueColor = isDarkMode ? SPEC_VALUE_BLUE_DARK : SPEC_VALUE_BLUE_LIGHT;
  const specSheetInnerMutedColor = isDarkMode ? 'rgba(240,244,255,0.72)' : theme.text.muted;
  const specSheetEmptyValueColor = isDarkMode ? 'rgba(240,244,255,0.42)' : 'rgba(0,0,0,0.5)';
  const specSheetDividerColor = isDarkMode ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.1)';
  const venueAccentColor = getStyle('venueScreenStyles', 'venueName', isMobile ? 'mobile' : 'web')?.color || '#fbcd9d';
  const specSheetPanelStyle = [
    venueStyles.leftColumn,
    {
      backdropFilter: 'none',
      ...(isDarkMode
        ? { backgroundColor: SPEC_SHEET_PANEL_DARK_BG, borderColor: 'rgba(255,255,255,0.18)' }
        : { backgroundColor: '#E9E9E9' }),
    },
  ];

  // --- Inline Add Location (no TourPlanner navigation) ---
  const searchAddress = async (query) => {
    if (!query?.trim()) {
      setAddressSearchResults([]);
      return;
    }
    setIsSearchingAddress(true);
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query.trim())}.json?` +
        `access_token=${MAPBOX_ACCESS_TOKEN}&` +
        `types=address,poi,place,locality,neighborhood,region&` +
        `limit=8&` +
        `language=en`
      );
      if (!response.ok) throw new Error('Geocoding request failed');
      const data = await response.json();
      const results = (data.features || []).map(feature => ({
        id: feature.id,
        name: feature.text || feature.place_name || 'Unnamed',
        fullAddress: feature.place_name,
        coordinates: { lng: feature.center[0], lat: feature.center[1] },
        type: (feature.place_type && feature.place_type[0]) || 'place',
        context: feature.context || []
      }));
      setAddressSearchResults(results);
    } catch (error) {
      console.error('Error searching address:', error);
      setAddressSearchResults([]);
    } finally {
      setIsSearchingAddress(false);
    }
  };

  const handleAddressSearchChange = (text) => {
    setAddressSearchQuery(text);
    if (addLocationSearchDebounceRef.current) clearTimeout(addLocationSearchDebounceRef.current);
    addLocationSearchDebounceRef.current = setTimeout(() => searchAddress(text), 300);
  };

  const selectAddressResult = (result) => {
    setAddLocationFormData(prev => ({
      ...prev,
      name: prev.name || result.name,
      address: result.fullAddress
    }));
    setAddLocationCoords(result.coordinates);
    setAddressSearchResults([]);
    setAddressSearchQuery(result.fullAddress);
  };

  const openAddLocationPanel = () => {
    setShowAddLocationPanel(true);
    setAddLocationCoords(venueMapCenter ? { lng: venueMapCenter[0], lat: venueMapCenter[1] } : null);
    setAddLocationFormData({
      name: venueData?.name ? `${venueData.name} (custom)` : '',
      notes: '',
      category: 'venue',
      address: venueData?.fullAddress || venueData?.city && venueData?.state ? `${venueData.city}, ${venueData.state}` : '',
      isPublic: false
    });
    setAddressSearchQuery('');
    setAddressSearchResults([]);
  };

  const closeAddLocationPanel = () => {
    setShowAddLocationPanel(false);
    setAddLocationCoords(null);
    setAddLocationFormData({ name: '', notes: '', category: 'other', address: '', isPublic: false });
    setAddressSearchQuery('');
    setAddressSearchResults([]);
  };

  const saveNewLocationInline = async () => {
    if (!addLocationFormData.name.trim()) {
      Alert.alert('Name Required', 'Please enter a name for your location.');
      return;
    }
    if (!addLocationCoords) {
      Alert.alert('Location Required', 'Search for an address to set the location.');
      return;
    }
    if (!isRealUser(auth.currentUser)) {
      Alert.alert('Sign In Required', 'Please sign in to add custom locations.');
      return;
    }
    try {
      const db = getFirestore();
      const locationData = {
        userId: auth.currentUser.uid,
        userName: userData?.name || userData?.email || auth.currentUser.email || 'Anonymous',
        name: addLocationFormData.name.trim(),
        notes: (addLocationFormData.notes || '').trim(),
        category: addLocationFormData.category || 'other',
        address: (addLocationFormData.address || '').trim(),
        latitude: addLocationCoords.lat,
        longitude: addLocationCoords.lng,
        isPublic: !!addLocationFormData.isPublic,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      await addDoc(collection(db, 'userLocations'), locationData);
      closeAddLocationPanel();
      Alert.alert('Success', 'Location added successfully!');
    } catch (error) {
      console.error('Error adding user location:', error);
      Alert.alert('Error', 'Failed to add location. Please try again.');
    }
  };

  const handleLocationSearch = () => {
    if (userLocation) {
      setUserLocation(null);
      return;
    }

    if (!navigator.geolocation) {
      Alert.alert('Error', 'Geolocation is not supported by your browser');
      return;
    }

    setIsGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const response = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${position.coords.latitude}&longitude=${position.coords.longitude}&localityLanguage=en`
          );
          const data = await response.json();

          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });

          if (searchQuery.length > 0) {
            pushVenueSearch({
              searchQuery: searchQuery,
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              nearMe: true
            });
          }
        } catch (error) {
          console.error('Error getting location details:', error);
          Alert.alert('Error', 'Error getting your location details. Please try again.');
        }
        setIsGettingLocation(false);
      },
      (error) => {
        console.error('Error getting location:', error);
        Alert.alert('Error', 'Error accessing location. Please allow location access and try again.');
        setIsGettingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0
      }
    );
  };


  const renderManualSection = (manuals) => {
    // If admin is editing manuals, show the editor
    if (isAdmin && isEditingManuals) {
      return renderManualEditor();
    }

    if (manuals.length === 0) {
      return (
        <View>
          <Text style={[venueStyles.noContentText, { color: specSheetInnerMutedColor }]}>No equipment manuals found</Text>
          {isAdmin && (
            <TouchableOpacity
              style={[venueStyles.manualButton, { backgroundColor: '#2980b9', marginTop: 10 }]}
              onPress={handleStartEditingManuals}
            >
              <Text style={venueStyles.manualButtonText}>+ Add Manuals (Admin)</Text>
            </TouchableOpacity>
          )}
        </View>
      );
    }

    return (
      <View>
        {isAdmin && (
          <View style={{ flexDirection: 'row', justifyContent: 'flex-end', marginBottom: 10 }}>
            <TouchableOpacity
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                padding: 8,
                borderRadius: 5,
                alignItems: 'center',
              }}
              onPress={handleStartEditingManuals}
            >
              <Text style={{ color: '#3D7FE6', fontSize: 12, fontWeight: 'bold' }}>EDIT MANUALS</Text>
            </TouchableOpacity>
          </View>
        )}
        {manuals.map((manual, index) => {
          return (
            <TouchableOpacity
              key={index}
              style={[venueStyles.manualButton, { marginBottom: 10, ...outlinedLink(MANUAL_LINK_BUTTON_BG) }]}
              onPress={() => {
                const url = formatExternalUrl(manual.url);
                if (url) window.open(url, '_blank');
              }}
            >
              <Text style={[venueStyles.manualButtonText, { color: LINK_BUTTON_TEXT }]}>{manual.name}</Text>
            </TouchableOpacity>
          );
        })}
      </View>
    );
  };

  const renderManualEditor = () => {
    return (
      <View style={{ marginBottom: 20, backgroundColor: 'black', borderRadius: 10, padding: 10 }}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
          <Text style={{ color: '#fff', fontSize: 16, fontWeight: 'bold' }}>Edit Manuals (Admin)</Text>
          <View style={{ flexDirection: 'row', gap: 10 }}>
            <TouchableOpacity
              style={{
                backgroundColor: '#95a5a6',
                padding: 8,
                borderRadius: 5,
                minWidth: 80,
                alignItems: 'center'
              }}
              onPress={handleCancelEditingManuals}
            >
              <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 12 }}>CANCEL</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={{
                backgroundColor: '#2980b9',
                padding: 8,
                borderRadius: 5,
                minWidth: 80,
                alignItems: 'center',
                flexDirection: 'row',
                justifyContent: 'center',
                gap: 5
              }}
              onPress={handleSaveManuals}
              disabled={savingManuals}
            >
              {savingManuals ? (
                <>
                  <ActivityIndicator size="small" color="#fff" />
                  <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 12 }}>SAVING...</Text>
                </>
              ) : (
                <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 12 }}>SAVE</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>

        {editableManuals.map((manual, index) => (
          <View
            key={index}
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: 8,
              padding: 15,
              marginBottom: 10
            }}
          >
            <View style={{ flexDirection: 'row', alignItems: "center", gap: 10 }}>
              <View style={{ width: "25%" }}>
                <Text style={{ color: '#fff', marginBottom: 5, fontSize: 12 }}>Name:</Text>
                <TextInput
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 5,
                    padding: 10,
                    color: '#fff',
                    fontSize: 14
                  }}
                  value={manual.name}
                  onChangeText={(text) => handleUpdateManual(index, 'name', text)}
                  placeholder="Enter manual name"
                  placeholderTextColor="rgba(255, 255, 255, 0.4)"
                />
              </View>

              <View style={{ width: "60%" }}>
                <Text style={{ color: '#fff', marginBottom: 5, fontSize: 12 }}>URL:</Text>
                <TextInput
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 5,
                    padding: 10,
                    color: '#fff',
                    fontSize: 14
                  }}
                  value={manual.url}
                  onChangeText={(text) => handleUpdateManual(index, 'url', text)}
                  placeholder="Enter manual URL"
                  placeholderTextColor="rgba(255, 255, 255, 0.4)"
                />
              </View>

              <View style={{ flexDirection: 'row', height: 50, gap: 8, width: "15%", alignItems: "center", justifyContent: "center" }}>
                <TouchableOpacity
                  style={{
                    opacity: index === 0 ? 0.5 : 1,
                    padding: 6,
                    backgroundColor: index === 0 ? 'transparent' : 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 4
                  }}
                  onPress={() => handleMoveManual(index, 'up')}
                  disabled={index === 0}
                >
                  <Text style={{ color: '#fff', fontSize: 16 }}>↑</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={{
                    opacity: index === editableManuals.length - 1 ? 0.5 : 1,
                    padding: 6,
                    backgroundColor: index === editableManuals.length - 1 ? 'transparent' : 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 4
                  }}
                  onPress={() => handleMoveManual(index, 'down')}
                  disabled={index === editableManuals.length - 1}
                >
                  <Text style={{ color: '#fff', fontSize: 16 }}>↓</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={{
                    backgroundColor: '#e74c3c',
                    borderRadius: 4,
                    padding: 6
                  }}
                  onPress={() => handleDeleteManual(index)}
                >
                  <Text style={{ color: '#fff', fontSize: 12 }}>✕</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        ))}

        <View style={{ flexDirection: 'row', gap: 10, marginTop: 10 }}>
          <TouchableOpacity
            style={{
              backgroundColor: '#2980b9',
              padding: 10,
              borderRadius: 5,
              alignItems: 'center',
              flex: 1
            }}
            onPress={handleAddManual}
          >
            <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 12 }}>ADD NEW MANUAL</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={{
              backgroundColor: '#27ae60',
              padding: 10,
              borderRadius: 5,
              alignItems: 'center',
              flex: 1
            }}
            onPress={() => setShowManualSelector(true)}
          >
            <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 12 }}>SELECT EXISTING MANUAL</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  const renderSpecSheetsEditor = () => {
    return (
      <View style={{ width: '100%', marginBottom: 20, backgroundColor: 'black', borderRadius: 10, padding: 10 }}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
          <Text style={{ color: '#fff', fontSize: 16, fontWeight: 'bold' }}>Edit Spec Sheets (Admin)</Text>
          <View style={{ flexDirection: 'row', gap: 10 }}>
            <TouchableOpacity
              style={{
                backgroundColor: '#95a5a6',
                padding: 8,
                borderRadius: 5,
                minWidth: 80,
                alignItems: 'center'
              }}
              onPress={handleCancelEditingSpecSheets}
            >
              <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 12 }}>CANCEL</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={{
                backgroundColor: '#2980b9',
                padding: 8,
                borderRadius: 5,
                minWidth: 80,
                alignItems: 'center',
                flexDirection: 'row',
                justifyContent: 'center',
                gap: 5
              }}
              onPress={handleSaveSpecSheets}
              disabled={savingSpecSheets}
            >
              {savingSpecSheets ? (
                <>
                  <ActivityIndicator size="small" color="#fff" />
                  <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 12 }}>SAVING...</Text>
                </>
              ) : (
                <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 12 }}>SAVE</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>

        {editableSpecSheets.map((spec, index) => (
          <View
            key={index}
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: 8,
              padding: 15,
              marginBottom: 10
            }}
          >
            <View style={{ marginBottom: 10 }}>
              <Text style={{ color: '#fff', marginBottom: 5, fontSize: 12 }}>Type:</Text>
              <View style={{ flexDirection: 'row', gap: 10 }}>
                <TouchableOpacity
                  style={{
                    flex: 1,
                    padding: 10,
                    borderRadius: 5,
                    backgroundColor: spec.specType === 'url' ? '#2980b9' : 'rgba(255, 255, 255, 0.1)',
                    alignItems: 'center'
                  }}
                  onPress={() => handleUpdateSpecSheet(index, 'specType', 'url')}
                >
                  <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 12 }}>URL</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={{
                    flex: 1,
                    padding: 10,
                    borderRadius: 5,
                    backgroundColor: spec.specType === 'file' ? '#2980b9' : 'rgba(255, 255, 255, 0.1)',
                    alignItems: 'center'
                  }}
                  onPress={() => handleUpdateSpecSheet(index, 'specType', 'file')}
                >
                  <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 12 }}>FILE</Text>
                </TouchableOpacity>
              </View>
            </View>

            <View style={{ flexDirection: 'row', alignItems: "center", gap: 10 }}>
              <View style={{ width: "25%" }}>
                <Text style={{ color: '#fff', marginBottom: 5, fontSize: 12 }}>Name:</Text>
                <TextInput
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 5,
                    padding: 10,
                    color: '#fff',
                    fontSize: 14
                  }}
                  value={spec.name}
                  onChangeText={(text) => handleUpdateSpecSheet(index, 'name', text)}
                  placeholder="Enter spec sheet name"
                  placeholderTextColor="rgba(255, 255, 255, 0.4)"
                />
              </View>

              {spec.specType === 'url' ? (
                <View style={{ width: "60%" }}>
                  <Text style={{ color: '#fff', marginBottom: 5, fontSize: 12 }}>URL:</Text>
                  <TextInput
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      borderRadius: 5,
                      padding: 10,
                      color: '#fff',
                      fontSize: 14
                    }}
                    value={spec.url}
                    onChangeText={(text) => handleUpdateSpecSheet(index, 'url', text)}
                    placeholder="Enter spec sheet URL"
                    placeholderTextColor="rgba(255, 255, 255, 0.4)"
                  />
                </View>
              ) : (
                <View style={{ width: "60%" }}>
                  <Text style={{ color: '#fff', marginBottom: 5, fontSize: 12 }}>File:</Text>
                  {spec.url ? (
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                      <Text style={{ color: '#4caf50', flex: 1, fontSize: 12 }} numberOfLines={1}>
                        {spec.fileName || 'File uploaded'}
                      </Text>
                      <TouchableOpacity
                        style={{
                          backgroundColor: '#27ae60',
                          padding: 8,
                          borderRadius: 5
                        }}
                        onPress={() => handleUploadSpecFile(index)}
                      >
                        <Text style={{ color: '#fff', fontSize: 12 }}>CHANGE</Text>
                      </TouchableOpacity>
                    </View>
                  ) : (
                    <TouchableOpacity
                      style={{
                        backgroundColor: '#27ae60',
                        padding: 10,
                        borderRadius: 5,
                        alignItems: 'center',
                        flexDirection: 'row',
                        justifyContent: 'center',
                        gap: 5
                      }}
                      onPress={() => handleUploadSpecFile(index)}
                      disabled={uploadingSpecIndex === index}
                    >
                      {uploadingSpecIndex === index ? (
                        <>
                          <ActivityIndicator size="small" color="#fff" />
                          <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 12 }}>UPLOADING...</Text>
                        </>
                      ) : (
                        <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 12 }}>UPLOAD FILE</Text>
                      )}
                    </TouchableOpacity>
                  )}
                </View>
              )}

              <View style={{ flexDirection: 'row', height: 50, gap: 8, width: "15%", alignItems: "center", justifyContent: "center" }}>
                <TouchableOpacity
                  style={{
                    opacity: index === 0 ? 0.5 : 1,
                    padding: 6,
                    backgroundColor: index === 0 ? 'transparent' : 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 4
                  }}
                  onPress={() => handleMoveSpecSheet(index, 'up')}
                  disabled={index === 0}
                >
                  <Text style={{ color: '#fff', fontSize: 16 }}>↑</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={{
                    opacity: index === editableSpecSheets.length - 1 ? 0.5 : 1,
                    padding: 6,
                    backgroundColor: index === editableSpecSheets.length - 1 ? 'transparent' : 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 4
                  }}
                  onPress={() => handleMoveSpecSheet(index, 'down')}
                  disabled={index === editableSpecSheets.length - 1}
                >
                  <Text style={{ color: '#fff', fontSize: 16 }}>↓</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={{
                    backgroundColor: '#e74c3c',
                    borderRadius: 4,
                    padding: 6
                  }}
                  onPress={() => handleDeleteSpecSheet(index)}
                >
                  <Text style={{ color: '#fff', fontSize: 12 }}>✕</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        ))}

        <TouchableOpacity
          style={{
            backgroundColor: '#2980b9',
            padding: 10,
            borderRadius: 5,
            alignItems: 'center',
            marginTop: 10
          }}
          onPress={handleAddSpecSheet}
        >
          <Text style={{ color: '#fff', fontWeight: 'bold' }}>ADD NEW SPEC SHEET</Text>
        </TouchableOpacity>
      </View>
    );
  };

  useEffect(() => {
    const loadVenueData = async () => {
      try {
        // Get venue data directly from Firestore to ensure we have the latest data
        const db = getFirestore();
        const venueRef = doc(db, 'venues', venueId);
        const venueDoc = await getDoc(venueRef);

        if (venueDoc.exists()) {
          const venueData = venueDoc.data();
          console.log('Venue Data from Firestore:', venueData);
          console.log('Approved Data:', venueData.approved);

          // Parse venue_media if it exists
          if (venueData.venue_media) {
            try {
              const mediaData = JSON.parse(venueData.venue_media);
              venueData.photos = mediaData.photos;
              venueData.backgroundImage = mediaData.backgroundImage;
            } catch (error) {
              console.error('Error parsing venue media:', error);
            }
          }

          // Extract address from map embed link if available
          if (venueData.mapEmbedLink && (!venueData.fullAddress || venueData.fullAddress.trim() === '')) {
            venueData.fullAddress = extractAddressFromMapEmbed(venueData.mapEmbedLink);
          }

          setVenueData(venueData);

          // Check if venue is saved for current user
          if (auth.currentUser) {
            const userRef = doc(db, 'users', auth.currentUser.uid);
            const userDoc = await getDoc(userRef);
            if (userDoc.exists()) {
              const userData = userDoc.data();
              setIsSaved(isVenueSaved(userData, venueId));
              setUserType(userData.userType || 'user');
              setIsAdmin(userData.userType === 'admin');
            }
          }

          if (!showReportForm) {
            // Only load discussion data if not showing report form
            setDiscussionData({
              discussions: venueData.discussions || [],
              reports: venueData.reports || []
            });
          }
        }
      } catch (error) {
        console.error('Error loading venue data:', error);
      }
      setLoading(false);
    };

    if (venueId) {
      loadVenueData();
    }
  }, [venueId, showReportForm, auth.currentUser]);

  useEffect(() => {
    if (!venueId) return undefined;
    const venueRef = doc(getFirestore(), 'venues', venueId);
    return onSnapshot(
      venueRef,
      (snapshot) => {
        if (!snapshot.exists()) return;
        const uploads = Array.isArray(snapshot.data().uploads) ? snapshot.data().uploads : [];
        setVenueData((previous) => previous ? { ...previous, uploads } : previous);
      },
      (error) => {
        console.error('Error listening for approved venue uploads:', error);
      }
    );
  }, [venueId]);

  useEffect(() => {
    if (!venueId || !venueData) return;

    const loadApprovedResources = async () => {
      const db = getFirestore();
      const state = (venueData.state || '').trim();
      const city = (venueData.city || '').trim();
      try {
        const venueSnap = await getDocs(query(
          collection(db, 'resource_submissions'),
          where('status', '==', 'approved'),
          where('venueId', '==', venueId)
        ));

        let stateResources = [];
        if (state) {
          const stateSnap = await getDocs(query(
            collection(db, 'resource_submissions'),
            where('status', '==', 'approved'),
            where('scope', '==', 'state')
          ));
          stateResources = stateSnap.docs
            .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
            .filter((resource) =>
              (resource.venueState || '').trim().toLowerCase() === state.toLowerCase()
            );
        }

        let cityResources = [];
        if (state && city) {
          const citySnap = await getDocs(query(
            collection(db, 'resource_submissions'),
            where('status', '==', 'approved'),
            where('scope', '==', 'city')
          ));
          cityResources = citySnap.docs
            .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
            .filter((resource) =>
              (resource.venueCity || '').trim().toLowerCase() === city.toLowerCase() &&
              (resource.venueState || '').trim().toLowerCase() === state.toLowerCase()
            );
        }

        const seen = new Set();
        const resources = [];
        [...venueSnap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })), ...cityResources, ...stateResources]
          .forEach((resource) => {
            if (seen.has(resource.id)) return;
            seen.add(resource.id);
            resources.push(resource);
          });

        resources.sort((a, b) => {
          const aDate = a.approvedAt?.toDate?.() || a.createdAt?.toDate?.() || 0;
          const bDate = b.approvedAt?.toDate?.() || b.createdAt?.toDate?.() || 0;
          return bDate - aDate;
        });
        setApprovedResources(resources);
      } catch (error) {
        console.error('Error loading approved resources:', error);
      }
    };

    loadApprovedResources();
  }, [venueId, venueData?.state, venueData?.city]);

  // Venue map center: prefer stored lat/lng, else from map embed
  const venueMapCenter = useMemo(() => {
    if (!venueData) return null;
    const lat = venueData.latitude != null ? parseFloat(venueData.latitude) : null;
    const lng = venueData.longitude != null ? parseFloat(venueData.longitude) : null;
    if (lat != null && lng != null && !isNaN(lat) && !isNaN(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
      return [lng, lat];
    }
    const fromEmbed = venueData.mapEmbedLink && extractCoordinatesFromMapEmbed(venueData.mapEmbedLink);
    if (fromEmbed && fromEmbed.lat != null && fromEmbed.lng != null) {
      return [fromEmbed.lng, fromEmbed.lat];
    }
    return [-98.5795, 39.8283]; // fallback: center of US
  }, [venueData]);

  // Initialize venue Mapbox map in the map card (web only). Same pattern as TourPlanner: init when container ref is set.
  useLayoutEffect(() => {
    if (Platform.OS !== 'web' || !mapboxgl || !MAPBOX_ACCESS_TOKEN || !venueMapCenter || !venueMapContainerReady) return;
    const container = venueMapContainerRef.current;
    if (!container || venueMapRef.current) return;

    try {
      mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;
      venueMapRef.current = new mapboxgl.Map({
        container,
        style: `mapbox://styles/mapbox/${venueMapStyle}`,
        center: venueMapCenter,
        zoom: 14,
        maxZoom: 22,
        trackResize: true,
      });
      const map = venueMapRef.current;
      map.on('load', () => {
        map.addControl(new mapboxgl.NavigationControl(), 'top-right');
        if (venueData?.name) {
          new mapboxgl.Marker().setLngLat(venueMapCenter).addTo(map);
        }
      });
    } catch (err) {
      console.error('Venue map init error:', err);
    }

    return () => {
      if (venueMapRef.current) {
        try { venueMapRef.current.remove(); } catch (e) { /* ignore */ }
        venueMapRef.current = null;
      }
    };
  }, [venueMapCenter, venueMapStyle, venueMapContainerReady]);

  // Fetch approved reports and calculate subcategory grades
  useEffect(() => {
    const loadApprovedReports = async () => {
      if (!venueId) return;

      try {
        const db = getFirestore();
        const reportsQuery = query(
          collection(db, 'venue_reports'),
          where('venueId', '==', venueId),
          where('status', '==', 'approved')
        );
        const reportsSnapshot = await getDocs(reportsQuery);
        const reports = reportsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setApprovedReports(reports);

        const gradedReports = reports.filter((report) => {
          if (report?.isTechSpecsOnly === true) return false;
          const rawLower = String(report?.userType || '').toLowerCase();
          return rawLower !== 'technical';
        });

        // Calculate subcategory grades
        const moneyGrades = [];
        const safetyGrades = [];
        const hospitalityGrades = [];

        gradedReports.forEach(report => {
          const userType = report.userType?.toLowerCase() || 'other';

          // Calculate $$$ grade - average across all user types
          if (userType === 'artist') {
            // ARTIST: Venue's Promo of show, Were you paid on time
            const moneyFields = {
              venuePromotion: report.venuePromotion,
              pay: report.pay
            };
            const moneyGrade = calculateAverageGrade(moneyFields);
            if (moneyGrade) moneyGrades.push(moneyGrade);
          } else if (userType === 'employee') {
            // EMPLOYEE: Wage and Payment Structure
            const moneyFields = {
              payComparison: report.payComparison
            };
            const moneyGrade = calculateAverageGrade(moneyFields);
            if (moneyGrade) moneyGrades.push(moneyGrade);
          } else if (userType === 'touring') {
            // TOUR CREW: City Walkability
            const moneyFields = {
              cityWalkability: report.cityWalkability
            };
            const moneyGrade = calculateAverageGrade(moneyFields);
            if (moneyGrade) moneyGrades.push(moneyGrade);
          }

          // Calculate Safety grade - average across all user types
          if (userType === 'artist') {
            // ARTIST: Safety Q (stageSafetyGrade)
            const safetyFields = {
              stageSafetyGrade: report.stageSafetyGrade
            };
            const safetyGrade = calculateAverageGrade(safetyFields);
            if (safetyGrade) safetyGrades.push(safetyGrade);
          } else if (userType === 'employee') {
            // EMPLOYEE: Safety Q (overallSafety)
            const safetyFields = {
              overallSafety: report.overallSafety
            };
            const safetyGrade = calculateAverageGrade(safetyFields);
            if (safetyGrade) safetyGrades.push(safetyGrade);
          } else if (userType === 'touring') {
            // TOUR: Safety Q (overallSafety)
            const safetyFields = {
              overallSafety: report.overallSafety
            };
            const safetyGrade = calculateAverageGrade(safetyFields);
            if (safetyGrade) safetyGrades.push(safetyGrade);
          }

          // Calculate Hospitality grade - all remaining grades (excluding $$$ and Safety fields)
          let hospitalityFields = {};
          if (userType === 'artist') {
            // All remaining: hospitality, backstage, audioStaff, lightingStaff, musicalEquipmentGrade
            hospitalityFields = {
              hospitality: report.hospitality,
              backstage: report.backstage,
              audioStaff: report.audioStaff,
              lightingStaff: report.lightingStaff,
              musicalEquipmentGrade: report.musicalEquipmentGrade
            };
          } else if (userType === 'employee') {
            // All remaining: audioEquipment, lightingEquipment, workplaceCleaniness
            hospitalityFields = {
              audioEquipment: report.audioEquipment,
              lightingEquipment: report.lightingEquipment,
              workplaceCleaniness: report.workplaceCleaniness
            };
          } else if (userType === 'touring') {
            // All remaining: venueManagement, venueStaff, audioStaff, lightingStaff, audioEquipment, lightingEquipment
            hospitalityFields = {
              venueManagement: report.venueManagement,
              venueStaff: report.venueStaff,
              audioStaff: report.audioStaff,
              lightingStaff: report.lightingStaff,
              audioEquipment: report.audioEquipment,
              lightingEquipment: report.lightingEquipment
            };
          }
          const hospitalityGrade = calculateAverageGrade(hospitalityFields);
          if (hospitalityGrade) hospitalityGrades.push(hospitalityGrade);
        });

        // Calculate final averages for each subcategory
        const finalMoneyGrade = moneyGrades.length > 0
          ? calculateAverageGrade(moneyGrades.reduce((obj, grade, index) => {
            obj[`grade${index + 1}`] = grade;
            return obj;
          }, {}))
          : null;

        const finalSafetyGrade = safetyGrades.length > 0
          ? calculateAverageGrade(safetyGrades.reduce((obj, grade, index) => {
            obj[`grade${index + 1}`] = grade;
            return obj;
          }, {}))
          : null;

        const finalHospitalityGrade = hospitalityGrades.length > 0
          ? calculateAverageGrade(hospitalityGrades.reduce((obj, grade, index) => {
            obj[`grade${index + 1}`] = grade;
            return obj;
          }, {}))
          : null;

        setSubcategoryGrades({
          money: finalMoneyGrade,
          safety: finalSafetyGrade,
          hospitality: finalHospitalityGrade
        });
      } catch (error) {
        console.error('Error loading approved reports:', error);
      }
    };

    loadApprovedReports();
  }, [venueId]);

  useEffect(() => {
    if (!venueId) {
      setApprovedVenueInterestDocs([]);
      return undefined;
    }

    const db = getFirestore();
    return subscribeApprovedVenueInterestTagDocs(db, venueId, setApprovedVenueInterestDocs);
  }, [venueId]);

  useEffect(() => {
    if (!venueId) {
      setLiveSpecsReports([]);
      return undefined;
    }
    const db = getFirestore();
    const liveQuery = query(
      collection(db, 'venue_reports'),
      where('venueId', '==', venueId),
      where('status', '==', LIVE_COMMUNITY_STATUS),
      where('isTechSpecsOnly', '==', true)
    );
    const unsub = onSnapshot(
      liveQuery,
      (snapshot) => {
        const reports = sortLiveReportsNewestFirst(
          snapshot.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter(isPublicLiveCommunityReport)
        );
        setLiveSpecsReports(reports);
      },
      (error) => {
        console.error('Error loading LIVE community tech specs:', error);
        setLiveSpecsReports([]);
      }
    );
    return () => unsub();
  }, [venueId]);

  // Load manuals when selector opens
  useEffect(() => {
    if (showManualSelector && availableManuals.length === 0) {
      loadAvailableManuals();
    }
  }, [showManualSelector]);

  useEffect(() => {
    const fetchVotes = async () => {
      if (!venueId) return;

      try {
        const db = getFirestore();
        const votesRef = doc(db, 'venues', venueId);
        const votesDoc = await getDoc(votesRef);

        if (votesDoc.exists()) {
          const data = votesDoc.data();
          setVotes({
            up: data.upVotes || 0,
            down: data.downVotes || 0
          });
          setTitleVotes({
            up: data.titleUpVotes || 0,
            down: data.titleDownVotes || 0
          });
          setSpecsVotes({
            up: data.specsUpVotes || 0,
            down: data.specsDownVotes || 0
          });
          setLinksVotes({
            up: data.linksUpVotes || 0,
            down: data.linksDownVotes || 0
          });
        }

        // Get all user's votes in parallel if they're logged in
        //   if (auth.currentUser) {
        //     const voteTypes = [
        //       { collection: 'votes', setter: setUserVote },
        //       { collection: 'titleVotes', setter: setUserTitleVote },
        //       { collection: 'specsVotes', setter: setUserSpecsVote },
        //       { collection: 'linksVotes', setter: setUserLinksVote }
        //     ];

        //     await Promise.all(
        //       voteTypes.map(async ({ collection, setter }) => {
        //         const voteRef = doc(db, 'venues', venueId, collection, auth.currentUser.uid);
        //         const voteDoc = await getDoc(voteRef);
        //         if (voteDoc.exists()) {
        //           setter(voteDoc.data().vote);
        //         }
        //       })
        //     );
        // }
        // Load user's votes from local storage
        const venueVote = localStorage.getItem(`venue_${venueId}_vote`);
        const titleVote = localStorage.getItem(`venue_${venueId}_title_vote`);
        const specsVote = localStorage.getItem(`venue_${venueId}_specs_vote`);
        const linksVote = localStorage.getItem(`venue_${venueId}_links_vote`);

        setUserVote(venueVote);
        setUserTitleVote(titleVote);
        setUserSpecsVote(specsVote);
        setUserLinksVote(linksVote);
      } catch (error) {
        console.error('Error fetching votes:', error);
      }
    };

    fetchVotes();
  }, [venueId]);

  useEffect(() => {
    if (currentView === 'media' && venueId) {
      loadDiscussionMessages();
    }
  }, [currentView, venueId]);

  useEffect(() => {
    if (activeDiscussionTab === 'uploads' && venueId && activeSection === 'discussionsAndUploads') {
      loadDiscussionMessages();
    }
  }, [activeDiscussionTab]);

  const loadPublicRoomEqs = async () => {
    if (!venueId) return;
    try {
      const db = getFirestore();
      const q = query(
        collection(db, `venues/${venueId}/roomEq`),
        where('isPublic', '==', true),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      const list = [];
      snapshot.forEach((d) => list.push({ id: d.id, ...d.data() }));
      setPublicRoomEqs(list);
    } catch (err) {
      console.error('Load public room EQs:', err);
      setPublicRoomEqs([]);
    }
  };

  const handleDeleteRoomEqSubmission = useCallback(async (submission) => {
    if (!venueId || !submission?.id) return;
    const db = getFirestore();
    try {
      await deleteDoc(doc(db, `venues/${venueId}/roomEq`, submission.id));
      setPublicRoomEqs((prev) => prev.filter((e) => e.id !== submission.id));
      setRoomEqViewerSubmissions((prev) => {
        const next = prev.filter((s) => s.id !== submission.id);
        if (next.length === 0) setRoomEqViewerVisible(false);
        return next;
      });
    } catch (err) {
      console.error('Delete Room EQ submission:', err);
      Alert.alert('Error', err.message || 'Failed to delete.');
    }
  }, [venueId]);

  const handleEditRoomEqSubmission = useCallback((submission) => {
    if (!submission) return;
    setEditSubmissionId(submission.id);
    setRoomEqIsPublic(submission.isPublic !== false);
    if (submission.eq) {
      roomEqFormRef.current?.setFormData?.({
        photos: submission.eq.photos || [],
        setlist: submission.eq.setlist || [],
        notes: submission.eq.notes || [],
      });
    }
    if (submission.rf) {
      const ti = submission.rf.textItems || [];
      setRfTextItems(ti.length ? ti : ['', '', '', '']);
      const ph = (submission.rf.photos || []).map((p) => ({ file: null, preview: p.url, description: p.description || '' }));
      setRfPhotos(ph.length ? ph : [{ file: null, preview: null, description: '' }]);
    }
    if (submission.addTab) {
      setAddTabTitle(submission.addTab.title || '');
      setAddTabType(submission.addTab.formType || null);
      setAddTabConfirmed(true);
      if (submission.addTab.formType === 'EQ' && submission.addTab.data) {
        addTabEqFormRef.current?.setFormData?.(submission.addTab.data);
      } else if (submission.addTab.formType === 'RF' && submission.addTab.data) {
        const ti = submission.addTab.data.textItems || [];
        setAddTabRfTextItems(ti.length ? ti : ['', '']);
        const ph = (submission.addTab.data.photos || []).map((p) => ({ file: null, preview: p.url, description: p.description || '' }));
        setAddTabRfPhotos(ph.length ? ph : [{ file: null, preview: null, description: '' }]);
      }
    }
    const firstTab = submission.eq ? 'EQ' : submission.rf ? 'RF' : 'ADD';
    setShareEqActiveTab(firstTab);
    setRoomEqViewerVisible(false);
    setShowRoomEqModal(true);
    setTimeout(() => {
      if (submission.eq) roomEqFormRef.current?.setFormData?.({ photos: submission.eq.photos || [], setlist: submission.eq.setlist || [], notes: submission.eq.notes || [] });
      if (submission.addTab?.formType === 'EQ' && submission.addTab.data) addTabEqFormRef.current?.setFormData?.(submission.addTab.data);
    }, 100);
  }, []);

  const saveRfEntry = useCallback(async (payload) => {
    const { textItems, photos: photoSlots, title: rfTitle } = payload;
    const withFiles = (photoSlots || []).filter((p) => p.file);
    if (withFiles.length === 0) {
      Alert.alert('Add at least one photo');
      return false;
    }
    const auth = getAuth();
    if (!auth.currentUser) await signInAnonymously(auth);
    const uid = auth.currentUser?.uid;
    if (!uid) return false;
    const db = getFirestore();
    const storage = getStorage();
    const timestamp = Date.now();
    const uploadedPhotos = [];
    for (let i = 0; i < (photoSlots || []).length; i++) {
      const slot = photoSlots[i];
      if (!slot.file) continue;
      const file = slot.file;
      const blob = file?.uri ? await (await fetch(file.uri)).blob() : (file instanceof Blob ? file : await (await fetch(file)).blob());
      const ext = (file?.name || 'image').split('.').pop()?.toLowerCase() || 'jpg';
      const path = `venues/${venueId}/roomEq/${uid}_rf_${timestamp}_${i}.${ext}`;
      const ref = storageRef(storage, path);
      await uploadBytes(ref, blob);
      const url = await getDownloadURL(ref);
      uploadedPhotos.push({ url, description: (slot.description || '').trim() });
    }
    const docPayload = {
      type: 'rf',
      userId: uid,
      uploaderDisplayName: userData?.displayName || auth.currentUser?.displayName || 'Anonymous',
      textItems: (textItems || []).map((t) => String(t || '').trim()).filter(Boolean),
      photos: uploadedPhotos,
      isPublic: true,
      createdAt: serverTimestamp(),
    };
    if (rfTitle && String(rfTitle).trim()) docPayload.title = String(rfTitle).trim();
    await addDoc(collection(db, `venues/${venueId}/roomEq`), docPayload);
    await updateDoc(doc(db, 'users', uid), { [`venueIdsWithRoomEq.${venueId}`]: true });
    return true;
  }, [venueId, userData?.displayName]);

  const uploadRfPhotoSlots = useCallback(async (photoSlots, suffix) => {
    const auth = getAuth();
    if (!auth.currentUser) await signInAnonymously(auth);
    const uid = auth.currentUser?.uid;
    const storage = getStorage();
    const timestamp = Date.now();
    const out = [];
    for (let i = 0; i < (photoSlots || []).length; i++) {
      const slot = photoSlots[i];
      if (!slot?.file) continue;
      const file = slot.file;
      const blob = file?.uri ? await (await fetch(file.uri)).blob() : (file instanceof Blob ? file : await (await fetch(file)).blob());
      const ext = (file?.name || 'image').split('.').pop()?.toLowerCase() || 'jpg';
      const path = `venues/${venueId}/roomEq/${uid}_${suffix}_${timestamp}_${i}.${ext}`;
      const ref = storageRef(storage, path);
      await uploadBytes(ref, blob);
      const url = await getDownloadURL(ref);
      out.push({ url, description: (slot.description || '').trim() });
    }
    return out;
  }, [venueId]);

  const saveAllRoomEq = useCallback(async () => {
    if (!venueId) return;
    setSaveAllLoading(true);
    try {
      const auth = getAuth();
      if (!auth.currentUser) await signInAnonymously(auth);
      const uid = auth.currentUser?.uid;
      const db = getFirestore();
      const displayName = userData?.displayName || auth.currentUser?.displayName || 'Anonymous';

      let eqPayload = null;
      if (roomEqFormRef.current?.getPayload) {
        eqPayload = await roomEqFormRef.current.getPayload();
      }

      let rfPayload = null;
      const rfTextItemsClean = (rfTextItems || []).map((t) => String(t || '').trim()).filter(Boolean);
      const rfWithFiles = (rfPhotos || []).filter((p) => p?.file);
      if (rfWithFiles.length > 0 || rfTextItemsClean.length > 0) {
        let uploaded = [];
        if (rfWithFiles.length > 0) {
          uploaded = await uploadRfPhotoSlots(rfPhotos, 'rf');
        }
        rfPayload = {
          textItems: rfTextItemsClean,
          photos: uploaded,
        };
      }

      let addTabPayload = null;
      if (addTabConfirmed && addTabTitle?.trim()) {
        if (addTabType === 'EQ' && addTabEqFormRef.current?.getPayload) {
          const data = await addTabEqFormRef.current.getPayload();
          const hasEqContent =
            !!data &&
            (
              (data.photos && data.photos.length) ||
              (data.setlist && data.setlist.length) ||
              (data.notes && data.notes.length)
            );
          if (hasEqContent) {
            addTabPayload = {
              title: addTabTitle.trim(),
              formType: 'EQ',
              data: {
                photos: data.photos || [],
                setlist: data.setlist || [],
                notes: data.notes || [],
              },
            };
          }
        } else if (addTabType === 'RF') {
          const addTabRfTextClean = (addTabRfTextItems || []).map((t) => String(t || '').trim()).filter(Boolean);
          const addWithFiles = (addTabRfPhotos || []).filter((p) => p?.file);
          if (addWithFiles.length > 0 || addTabRfTextClean.length > 0) {
            let uploaded = [];
            if (addWithFiles.length > 0) {
              uploaded = await uploadRfPhotoSlots(addTabRfPhotos, 'addTabRf');
            }
            addTabPayload = {
              title: addTabTitle.trim(),
              formType: 'RF',
              data: {
                textItems: addTabRfTextClean,
                photos: uploaded,
              },
            };
          }
        }
      }

      if (!eqPayload && !rfPayload && !addTabPayload) {
        Alert.alert('Add content', 'Add some text or a photo in the EQ tab, RF tab, or your custom tab to save.');
        return;
      }

      const docPayload = {
        type: 'roomEqSubmission',
        userId: uid,
        uploaderDisplayName: displayName,
        isPublic: roomEqIsPublic,
        createdAt: serverTimestamp(),
        eq: eqPayload
          ? {
              photos: eqPayload.photos || [],
              setlist: eqPayload.setlist || [],
              notes: eqPayload.notes || [],
            }
          : null,
        rf: rfPayload,
        addTab: addTabPayload,
      };
      if (editSubmissionId) {
        await updateDoc(doc(db, `venues/${venueId}/roomEq`, editSubmissionId), {
          eq: docPayload.eq,
          rf: docPayload.rf,
          addTab: docPayload.addTab,
          isPublic: docPayload.isPublic,
        });
        setEditSubmissionId(null); setRoomEqIsPublic(false);
      } else {
        const submissionRef = await addDoc(collection(db, `venues/${venueId}/roomEq`), docPayload);
        const newSubmissionId = submissionRef.id;
        await updateDoc(doc(db, 'users', uid), { [`venueIdsWithRoomEq.${venueId}`]: true });
        const previewFromPayload = (() => {
          const p = docPayload;
          if (!p) return null;
          if (p.eq?.photos?.length && p.eq.photos[0]?.url) return p.eq.photos[0].url;
          if (p.rf?.photos?.length && p.rf.photos[0]?.url) return p.rf.photos[0].url;
          if (p.addTab?.formType === 'EQ' && p.addTab.data?.photos?.length && p.addTab.data.photos[0]?.url) {
            return p.addTab.data.photos[0].url;
          }
          if (p.addTab?.formType === 'RF' && p.addTab.data?.photos?.length && p.addTab.data.photos[0]?.url) {
            return p.addTab.data.photos[0].url;
          }
          return null;
        })();
        await addRoomEqSubmissionLibraryFile(uid, {
          venueId,
          submissionId: newSubmissionId,
          venueName: venueData?.name || venueData?.venueName || '',
          previewImageUrl: previewFromPayload,
        });
        if (venueData) {
          const savedVenueImage = userData?.savedVenueImages?.[venueId] || null;
          await ensureVenueInRoomNotesFolder(uid, { id: venueId, ...venueData }, savedVenueImage);
        }
      }
      loadPublicRoomEqs();
      setShowRoomEqModal(false);
      setRoomEqIsPublic(false);
      setShareEqActiveTab('EQ');
    } catch (err) {
      console.error('Save all Room EQ:', err);
      Alert.alert('Error', err.message || 'Failed to save.');
    } finally {
      setSaveAllLoading(false);
    }
  }, [venueId, userData?.displayName, rfPhotos, rfTextItems, addTabConfirmed, addTabTitle, addTabType, addTabRfPhotos, addTabRfTextItems, uploadRfPhotoSlots, editSubmissionId, venueData, userData?.savedVenueImages]);

  useEffect(() => {
    loadPublicRoomEqs();
  }, [venueId]);

  // Open Share EQ modal in edit mode when navigated from MediaManager with edit params
  useEffect(() => {
    const editId = route.params?.editRoomEqSubmissionId;
    const editSub = route.params?.editRoomEqSubmission;
    if (!editId || !editSub || !venueId) return;
    handleEditRoomEqSubmission(editSub);
    navigation.setParams({ editRoomEqSubmissionId: undefined, editRoomEqSubmission: undefined });
  }, [route.params?.editRoomEqSubmissionId, route.params?.editRoomEqSubmission, venueId, handleEditRoomEqSubmission, navigation]);

  useEffect(() => {
    const checkScreenSize = () => {
      if (typeof window !== 'undefined') {
        const width = window.innerWidth;
        setIsMobile(width <= BREAKPOINTS.mobile);
        setIsTablet(width > BREAKPOINTS.mobile && width <= BREAKPOINTS.tablet);
        if (width <= BREAKPOINTS.mobile) {
          setActiveSection(null);
        }
      }
    };

    checkScreenSize();

    // if (typeof window !== 'undefined') {
    //   window.addEventListener('resize', checkScreenSize);
    //   return () => window.removeEventListener('resize', checkScreenSize);
    // }
  }, []);

  // Handle scrollToDiscussion parameter - open Community section with discussions tab
  useEffect(() => {
    if (scrollToDiscussion && !loading) {
      // Set the active section to discussions and uploads (Community section)
      setActiveSection('discussionsAndUploads');
      // Set the discussion tab to 'discussions' (DiscussionBoard)
      setActiveDiscussionTab('discussions');

      // Scroll to the Community section if possible
      setTimeout(() => {
        // Try to find the element by a ref or id
        const communitySection = document.getElementById('venue-community-section');
        if (communitySection) {
          communitySection.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });
        } else {
          // Fallback: try to scroll to the mobile display buttons area
          const mobileButtons = document.querySelector('[data-testid="mobile-display-buttons"]');
          if (mobileButtons) {
            mobileButtons.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }, 200); // Increased timeout to ensure DOM is ready


      // Clear the parameter to prevent re-triggering
      navigation.setParams({ ...route.params, scrollToDiscussion: false });
    }
  }, [scrollToDiscussion, loading, navigation, route.params]);

  const loadDiscussionMessages = async () => {
    try {
      const db = getFirestore();
      const q = query(
        collection(db, `venues/${venueId}/discussions`),
        orderBy('timestamp', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const messageData = [];

      querySnapshot.forEach((doc) => {
        messageData.push({ id: doc.id, ...doc.data() });
      });

      setMessages(messageData);
    } catch (error) {
      console.error('Error loading discussion messages:', error);
    }
  };

  const gradeData = [
    // { title: "Average\nEmployee Grade", grade: getLetterGrade(venueData?.approved?.employeeGrade) },
    // { title: "Average\nArtist Grade", grade: getLetterGrade(venueData?.approved?.artistGrade) },
    // { title: "Average\nTouring Crew Grade", grade: getLetterGrade(venueData?.approved?.crewGrade) }
  ];

  const categoryRows = [
    ["Shift Length", "Backstage", "Venue Management"],
    ["Break Time", "Hospitality", "Audio Staff"],
    ["Overtime", "Backline/Equipment", "Lighting Staff"],
    ["Scheduling", "Stage Safety", "Cleanliness and Space"],
    ["House Equipment", "Audio Staff", "Safety"],
    ["Cleanliness", "Lighting Staff", "Communication"]
  ];

  const handleSearch = (text) => {
    setSearchQuery(text);
  };

  /**
   * Open a new venue search. Pushes rather than navigate(): navigating back to an earlier VenueList in
   * the stack makes web linking call history.go(-n), whose popstate restores that entry's old params
   * (e.g. the previous search term) over the new ones.
   */
  const pushVenueSearch = (params) => navigation.push('VenueList', params);

  const handleSearchSubmit = () => {
    if (searchQuery.trim()) {
      // Create a structured search object with advanced search parameters
      const searchParams = {
        searchQuery,
        timestamp: Date.now(),
        latitude: userLocation?.latitude,
        longitude: userLocation?.longitude,
        nearMe: userLocation ? true : false,
        sourceVenue: venueId,
        searchContext: {
          affiliate: venueData?.affiliate,
          capacity: venueData?.capacity,
          fromVenue: venueData?.name
        }
      };

      pushVenueSearch(searchParams);
    }
  };

  const affiliateTokens = useMemo(
    () =>
      String(venueData?.affiliate || '')
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean),
    [venueData?.affiliate]
  );

  const handleAffiliatePress = (affiliateValue) => {
    if (affiliateValue) {
      const searchParams = {
        affiliate: affiliateValue,
        timestamp: Date.now(),
      };
      pushVenueSearch(searchParams);
    }
  };

  const venueFeatureBadges = useMemo(
    () => FEATURE_BADGES.filter((badge) => hasFeature(venueData, badge.key)),
    [venueData]
  );

  /** Venue search for every venue with this feature, ranked by how closely each relates to this venue. */
  const handleFeaturePress = (badge) => {
    pushVenueSearch({
      features: [badge.filterValue],
      sourceVenue: venueId,
      searchContext: { fromVenue: venueData?.name },
      timestamp: Date.now(),
    });
  };

  const renderFeatureBadges = () =>
    venueFeatureBadges.length > 0 && (
      <View style={venueStyles.featureBadgesBox}>
        <View style={venueStyles.featureBadgesRow}>
          {venueFeatureBadges.map((badge) => (
            <TouchableOpacity
              key={badge.key}
              style={venueStyles.featureBadgeItem}
              onPress={() => handleFeaturePress(badge)}
              activeOpacity={0.7}
              accessibilityRole="link"
              accessibilityLabel={`Find venues with ${badge.label}`}
            >
              <Text style={venueStyles.featureBadgeItemText}>{badge.label}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>
    );

  const handleAddTechInfo = () => {
    setShowReportForm(true);
    navigation.setParams({ ...route.params, isTechSpecsOnly: true });
  };

  // Manual editing functions for admin
  const handleStartEditingManuals = () => {
    setEditableManuals([...(venueData?.manualsLinks || [])]);
    setIsEditingManuals(true);
  };

  const handleCancelEditingManuals = () => {
    setIsEditingManuals(false);
    setEditableManuals([]);
  };

  const handleAddManual = () => {
    setEditableManuals([...editableManuals, { name: '', url: '' }]);
  };

  const handleDeleteManual = (index) => {
    const newManuals = editableManuals.filter((_, i) => i !== index);
    setEditableManuals(newManuals);
  };

  const handleMoveManual = (index, direction) => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === editableManuals.length - 1)
    ) {
      return;
    }

    const newManuals = [...editableManuals];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [newManuals[index], newManuals[newIndex]] = [newManuals[newIndex], newManuals[index]];
    setEditableManuals(newManuals);
  };

  const handleUpdateManual = (index, field, value) => {
    const newManuals = [...editableManuals];
    newManuals[index] = { ...newManuals[index], [field]: value };
    setEditableManuals(newManuals);
  };

  const handleSaveManuals = async () => {
    if (!auth.currentUser || !isAdmin) {
      alert('Admin access required');
      return;
    }

    setSavingManuals(true);

    // Optimistic update - update UI immediately
    const previousVenueData = venueData;
    const previousEditingState = isEditingManuals;
    setVenueData({ ...venueData, manualsLinks: editableManuals });
    setIsEditingManuals(false);

    try {
      const db = getFirestore();
      const venueRef = doc(db, 'venues', venueId);

      await updateDoc(venueRef, {
        manualsLinks: editableManuals,
        manualsUpdatedAt: serverTimestamp(),
        manualsUpdatedBy: {
          uid: auth.currentUser.uid,
          email: auth.currentUser.email
        }
      });
    } catch (error) {
      console.error('Error updating manuals:', error);
      // Revert optimistic update on error
      setVenueData(previousVenueData);
      setIsEditingManuals(previousEditingState);
      alert('Error updating manuals: ' + error.message);
    } finally {
      setSavingManuals(false);
    }
  };

  // Load available manuals from Firebase
  const loadAvailableManuals = async () => {
    try {
      setLoadingManuals(true);
      const db = getFirestore();
      const manualsSnapshot = await getDocs(collection(db, 'equipment_manuals'));

      const manuals = [];
      manualsSnapshot.forEach((doc) => {
        manuals.push({
          keyword: doc.id,
          ...doc.data()
        });
      });

      setAvailableManuals(manuals);
    } catch (error) {
      console.error('Error loading available manuals:', error);
      setAvailableManuals([]);
    } finally {
      setLoadingManuals(false);
    }
  };

  const handleSelectExistingManual = (manual) => {
    const manualEntry = {
      name: manual.name,
      url: manual.url,
      ...(manual.software?.name && manual.software?.url ? {
        software: {
          name: manual.software.name,
          url: manual.software.url
        }
      } : {}),
      ...(manual.category ? { category: manual.category } : {}),
      ...(manual.manufacturer ? { manufacturer: manual.manufacturer } : {}),
      ...(manual.model ? { model: manual.model } : {}),
      ...(manual.type ? { type: manual.type } : {}),
      keyword: manual.keyword
    };

    setEditableManuals([...editableManuals, manualEntry]);
    setShowManualSelector(false);
    setManualSearchQuery('');
  };

  // Spec Sheets editing functions for admin
  const handleStartEditingSpecSheets = () => {
    setEditableSpecSheets(initialEditableSpecSheets(venueData));
    setIsEditingSpecSheets(true);
  };

  const handleCancelEditingSpecSheets = () => {
    setIsEditingSpecSheets(false);
    setEditableSpecSheets([]);
  };

  const handleAddSpecSheet = () => {
    setEditableSpecSheets([...editableSpecSheets, { name: '', url: '', specType: 'url' }]);
  };

  const handleDeleteSpecSheet = async (index) => {
    const specToDelete = editableSpecSheets[index];

    // If it's a file type, delete it from storage
    if (specToDelete?.specType === 'file' && specToDelete?.url) {
      try {
        // Extract the file path from the download URL
        // Firebase Storage URLs format: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{encodedPath}?alt=media&token={token}
        const url = new URL(specToDelete.url);
        const pathMatch = url.pathname.match(/\/o\/(.+)/);

        if (pathMatch) {
          const encodedPath = pathMatch[1];
          const filePath = decodeURIComponent(encodedPath);

          const storage = getStorage();
          const fileRef = storageRef(storage, filePath);
          await deleteObject(fileRef);
          console.log('Deleted file from storage:', filePath);
        }
      } catch (error) {
        console.error('Error deleting file from storage:', error);
        // Continue with deletion even if storage deletion fails
      }
    }

    const newSpecSheets = editableSpecSheets.filter((_, i) => i !== index);
    setEditableSpecSheets(newSpecSheets);
  };

  const handleMoveSpecSheet = (index, direction) => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === editableSpecSheets.length - 1)
    ) {
      return;
    }

    const newSpecSheets = [...editableSpecSheets];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [newSpecSheets[index], newSpecSheets[newIndex]] = [newSpecSheets[newIndex], newSpecSheets[index]];
    setEditableSpecSheets(newSpecSheets);
  };

  const handleUpdateSpecSheet = (index, field, value) => {
    const newSpecSheets = [...editableSpecSheets];
    newSpecSheets[index] = { ...newSpecSheets[index], [field]: value };
    setEditableSpecSheets(newSpecSheets);
  };

  const handleUploadSpecFile = async (index) => {
    try {
      setUploadingSpecIndex(index);
      const result = await DocumentPicker.getDocumentAsync({
        type: '*/*' // Accept any file type
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        const file = result.assets[0];
        const fileUri = file.uri;

        // Delete old file if it exists and is a file type
        const oldSpec = editableSpecSheets[index];
        if (oldSpec?.specType === 'file' && oldSpec?.url) {
          try {
            const oldUrl = new URL(oldSpec.url);
            const pathMatch = oldUrl.pathname.match(/\/o\/(.+)/);
            if (pathMatch) {
              const encodedPath = pathMatch[1];
              const oldFilePath = decodeURIComponent(encodedPath);
              const storage = getStorage();
              const oldFileRef = storageRef(storage, oldFilePath);
              await deleteObject(oldFileRef);
              console.log('Deleted old file from storage:', oldFilePath);
            }
          } catch (deleteError) {
            console.error('Error deleting old file from storage:', deleteError);
            // Continue with upload even if old file deletion fails
          }
        }

        const response = await fetch(fileUri);
        const fileBlob = await response.blob();

        const storage = getStorage();
        const fileName = `specSheetMedia/${venueId}/${Date.now()}_${file.name}`;
        const storageRefPath = storageRef(storage, fileName);

        await uploadBytes(storageRefPath, fileBlob);
        const downloadURL = await getDownloadURL(storageRefPath);

        const newSpecSheets = [...editableSpecSheets];
        newSpecSheets[index] = {
          ...newSpecSheets[index],
          url: downloadURL,
          specType: 'file',
          fileName: file.name,
          fileSize: file.size
        };
        setEditableSpecSheets(newSpecSheets);

        alert('File uploaded successfully!');
      }
    } catch (error) {
      console.error('Error uploading spec sheet file:', error);
      alert('Error uploading file: ' + error.message);
    } finally {
      setUploadingSpecIndex(null);
    }
  };

  const handleSaveSpecSheets = async () => {
    if (!auth.currentUser || !isAdmin) {
      alert('Admin access required');
      return;
    }

    setSavingSpecSheets(true);

    // Optimistic update - update UI immediately
    const previousVenueData = venueData;
    const previousEditingState = isEditingSpecSheets;
    setVenueData({ ...venueData, specSheets: editableSpecSheets });
    setIsEditingSpecSheets(false);

    try {
      const db = getFirestore();
      const venueRef = doc(db, 'venues', venueId);

      await updateDoc(venueRef, {
        specSheets: editableSpecSheets,
        specSheetsUpdatedAt: serverTimestamp(),
        specSheetsUpdatedBy: {
          uid: auth.currentUser.uid,
          email: auth.currentUser.email
        }
      });
    } catch (error) {
      console.error('Error updating spec sheets:', error);
      // Revert optimistic update on error
      setVenueData(previousVenueData);
      setIsEditingSpecSheets(previousEditingState);
      alert('Error updating spec sheets: ' + error.message);
    } finally {
      setSavingSpecSheets(false);
    }
  };


  const handleAddToFolder = () => {
    if (!isRealUser(auth.currentUser)) {
      navigation.navigate('Login');
      return;
    }
    setFolderDropdownOpen(false);
    setFolderSelectModalLibraryTab('tours');
    setFolderModalOpenWithCreate(false);
    setShowFolderSelectModal(true);
  };

  const handleAddToNewTour = () => {
    if (!isRealUser(auth.currentUser)) {
      navigation.navigate('Login');
      return;
    }
    setFolderDropdownOpen(false);
    setFolderSelectModalLibraryTab('tours');
    setFolderModalOpenWithCreate(true);
    setShowFolderSelectModal(true);
  };


  const handleFolderIconPress = () => {
    if (!isRealUser(auth.currentUser)) {
      navigation.navigate('Login');
      return;
    }
    setFolderDropdownOpen((prev) => !prev);
  };

  /** Toggles the venue in the user's single Saved Venues folder (shown on Profile → Saved Venues). */
  const handleSaveVenue = async () => {
    if (!isRealUser(auth.currentUser)) {
      navigation.navigate('Login');
      return;
    }
    if (!venueData) return;
    setFolderDropdownOpen(false);

    try {
      // Push the written library into the user context so Profile → Saved Venues updates without a reload.
      if (isSaved) {
        const library = await removeVenueFromSavedVenues(auth.currentUser.uid, venueId);
        setUser((prev) => (prev ? { ...prev, ...library } : prev));
        setIsSaved(false);
        setToast({ message: 'Venue removed from Saved Venues', type: 'success' });
      } else {
        const library = await saveVenueToSavedVenues(auth.currentUser.uid, { ...venueData, id: venueId });
        setUser((prev) => (prev ? { ...prev, ...library } : prev));
        setIsSaved(true);
        setToast({ message: 'Venue saved', type: 'success' });
      }
    } catch (error) {
      console.error('Error updating venue save status:', error);
      setToast({ message: 'Failed to update saved venues. Please try again.', type: 'error' });
    }
  };

  const handleSaveVenueToFolder = async (selectedFolderId) => {
    if (!venueData) return;

    try {
      // Prepare venue data for MediaManager
      const venueDataForSave = {
        id: venueData.id,
        name: venueData.name,
        city: venueData.city,
        state: venueData.state,
        capacity: venueData.capacity,
        ...venueData
      };

      // Use MediaManager's saveVenueToFolder function if available
      if (window.mediaManagerSaveVenueToFolder) {
        await window.mediaManagerSaveVenueToFolder(selectedFolderId, venueDataForSave);
      } else {
        // Fallback: use drop handler
        if (window.mediaManagerDropHandler) {
          await window.mediaManagerDropHandler({
            dragType: 'venue',
            ...venueDataForSave
          });
        }
      }

      // setShowFolderSelectModal(false);
      // alert('Venue saved to folder successfully!');
    } catch (error) {
      console.error('Error saving venue to folder:', error);
      alert('Failed to save venue to folder. Please try again.');
    }
  };

  const handleFormSubmit = () => {
    setShowReportForm(false);
    navigation.setParams({
      ...route.params,
      isTechSpecsOnly: undefined
    });
    navigation.navigate('MyReports');
  };

  const handleFormCancel = () => {
    setShowReportForm(false);
    // Also clear the isTechSpecsOnly parameter from route params
    navigation.setParams({
      ...route.params,
      isTechSpecsOnly: undefined // or delete this parameter
    });
  };

  const handleSubmitReport = () => {
    if (!isRealUser(auth.currentUser)) {
      navigation.navigate('Login');
      return;
    }

    setShowReportForm(true);
  };

  const handleToggleNotes = () => {
    if (!isRealUser(auth.currentUser)) {
      navigation.navigate('Login');
      return;
    }
    toggleMobileDisplay('discussionsAndUploads')
  }

  const handleAddToTour = () => {
    if (canAddToTour()) {
      // Navigate to tour planner with the current venue
      navigation.navigate('TourPlanner', {
        addVenue: {
          id: venueId,
          name: venueData?.name,
          city: venueData?.city,
          state: venueData?.state,
          address: venueData?.fullAddress,
          latitude: venueData?.latitude,
          longitude: venueData?.longitude,
          backgroundImage: venueData?.backgroundImage
        }
      });
    } else {
      // Show coming soon message for non-admin users
      Alert.alert(
        "Tour Planner Coming Soon!",
        "We're working on making the Tour Planner available to all users. Stay tuned for updates!",
        [{ text: "OK" }]
      );
    }
  };

  const canAddToTour = () => {
    return userType === 'admin';
  };

  const getSubmitButtonStyle = () => {
    return [
      venueStyles.actionButton,
      { backgroundColor: '#666769' }
    ];
  };

  const getAddToTourButtonStyle = () => {
    return [
      venueStyles.actionButton,
      { backgroundColor: '#666769' }
    ];
  };

  const getSaveButtonStyle = () => {
    return {
      ...venueStyles.saveButton,
      transform: isSaveHovered ? 'rotate(10deg) scale(1.2)' : 'rotate(10deg)',
      filter: isSaveHovered ? 'brightness(1.2)' : 'brightness(1)',
      transition: 'transform 0.3s ease-in-out, filter 0.3s ease-in-out'
    };
  };

  const handleVote = async (voteTarget, voteType) => {
    // if (!auth.currentUser) {
    //   navigation.navigate('Login');
    //   return;
    // }

    // Define vote configurations based on target
    const voteConfig = {
      'venue': {
        stateGetter: votes,
        stateSetter: setVotes,
        userVoteGetter: userVote,
        userVoteSetter: setUserVote,
        votePrefix: '',
        up: 'upVotes',
        down: 'downVotes',
        storageKey: `venue_${venueId}_vote`
      },
      'title': {
        stateGetter: titleVotes,
        stateSetter: setTitleVotes,
        userVoteGetter: userTitleVote,
        userVoteSetter: setUserTitleVote,
        votePrefix: 'title',
        up: 'titleUpVotes',
        down: 'titleDownVotes',
        storageKey: `venue_${venueId}_title_vote`
      },
      'specs': {
        stateGetter: specsVotes,
        stateSetter: setSpecsVotes,
        userVoteGetter: userSpecsVote,
        userVoteSetter: setUserSpecsVote,
        votePrefix: 'specs',
        up: 'specsUpVotes',
        down: 'specsDownVotes',
        storageKey: `venue_${venueId}_specs_vote`
      },
      'links': {
        stateGetter: linksVotes,
        stateSetter: setLinksVotes,
        userVoteGetter: userLinksVote,
        userVoteSetter: setUserLinksVote,
        votePrefix: 'links',
        up: 'linksUpVotes',
        down: 'linksDownVotes',
        storageKey: `venue_${venueId}_links_vote`
      }
    };

    const config = voteConfig[voteTarget];
    if (!config) {
      console.error('Invalid vote target:', voteTarget);
      return;
    }

    try {
      const db = getFirestore();
      const venueRef = doc(db, 'venues', venueId);
      // const userVoteRef = doc(
      //   db, 
      //   'venues', 
      //   venueId, 
      //   voteTarget == 'venue' ? 'votes' : `${voteTarget}Votes`, 
      //   auth.currentUser.uid
      // );


      // Get existing vote from local storage
      const existingVote = localStorage.getItem(config.storageKey);

      // If user already voted the same way, remove their vote
      if (existingVote === voteType) {
        await updateDoc(venueRef, {
          [config[voteType]]: increment(-1)
        });
        // await deleteDoc(userVoteRef);
        localStorage.removeItem(config.storageKey);
        config.userVoteSetter(null);
        config.stateSetter(prev => ({
          ...prev,
          [voteType]: prev[voteType] - 1
        }));
        return;
      }

      // If user is changing their vote
      if (existingVote) {
        await updateDoc(venueRef, {
          // [config[config.userVoteGetter]]: increment(-1),
          [config[existingVote]]: increment(-1),
          [config[voteType]]: increment(1)
        });
      } else {
        // First time voting
        await updateDoc(venueRef, {
          [config[voteType]]: increment(1)
        });
      }

      // Save user's vote
      // await setDoc(userVoteRef, {
      //   vote: voteType,
      //   timestamp: serverTimestamp()
      // });
      // Save vote to local storage
      localStorage.setItem(config.storageKey, voteType);

      config.userVoteSetter(voteType);
      config.stateSetter(prev => ({
        ...prev,
        [voteType]: prev[voteType] + 1,
        // [config.userVoteGetter]: config.userVoteGetter ? prev[config.userVoteGetter] - 1 : prev[config.userVoteGetter]
        [existingVote]: existingVote ? prev[existingVote] - 1 : prev[existingVote]
      }));

    } catch (error) {
      console.error('Error updating vote:', error);
      alert('Failed to update vote. Please try again.');
    }
  };

  const handlePhotoClick = (photo, index) => {
    setSelectedPhoto(photo);
    setSelectedPhotoIndex(index);
  };

  const handleCloseModal = () => {
    setSelectedPhoto(null);
  };

  const handlePrevPhoto = () => {
    const discussionPhotos = messages.filter(msg => msg.photoURL);
    const newIndex = selectedPhotoIndex > 0 ? selectedPhotoIndex - 1 : discussionPhotos.length - 1;
    setSelectedPhotoIndex(newIndex);
    setSelectedPhoto(discussionPhotos[newIndex]);
  };

  const handleNextPhoto = () => {
    const discussionPhotos = messages.filter(msg => msg.photoURL);
    const newIndex = selectedPhotoIndex < discussionPhotos.length - 1 ? selectedPhotoIndex + 1 : 0;
    setSelectedPhotoIndex(newIndex);
    setSelectedPhoto(discussionPhotos[newIndex]);
  };

  const renderPhotoModal = () => {
    if (!selectedPhoto) return null;

    return (
      <View style={venueStyles.photoModal}>
        <View style={venueStyles.modalContent}>
          <TouchableOpacity style={venueStyles.modalClose} onPress={handleCloseModal}>
            <Text style={{ color: 'white', fontSize: 24 }}>×</Text>
          </TouchableOpacity>

          <TouchableOpacity style={[venueStyles.modalNav, venueStyles.modalNavPrev]} onPress={handlePrevPhoto}>
            <Text style={{ color: 'white', fontSize: 48 }}>‹</Text>
          </TouchableOpacity>

          <TouchableOpacity style={[venueStyles.modalNav, venueStyles.modalNavNext]} onPress={handleNextPhoto}>
            <Text style={{ color: 'white', fontSize: 48 }}>›</Text>
          </TouchableOpacity>

          <View style={{ width: '100%', height: '100%', minHeight: 320, justifyContent: 'center', alignItems: 'center' }}>
            <Image
              source={{ uri: selectedPhoto.photoURL }}
              style={{ width: '90%', height: '90%', objectFit: 'contain' }}
              resizeMode="contain"
            />
          </View>

          <View style={venueStyles.modalInfo}>
            <Text style={venueStyles.modalUsername}>{selectedPhoto.username}</Text>
            {selectedPhoto.timestamp && (
              <Text style={venueStyles.modalTimestamp}>
                {new Date(selectedPhoto.timestamp.seconds * 1000).toLocaleDateString()}
              </Text>
            )}
          </View>
        </View>
      </View>
    );
  };

  const renderReports = () => {
    if (!discussionData?.reports || discussionData.reports.length === 0) {
      return (
        <View style={venueStyles.emptyStateContainer}>
          <Text style={venueStyles.emptyStateText}>No reports available for this venue</Text>
        </View>
      );
    }

    return (
      <ScrollView style={venueStyles.reportsContainer}>
        {discussionData.reports.map((report, index) => (
          <View key={index} style={venueStyles.reportCard}>
            <View style={venueStyles.reportHeader}>
              <View style={venueStyles.reportUserInfo}>
                <Text style={venueStyles.reportUserName}>{report.userName || 'Anonymous'}</Text>
                <Text style={venueStyles.reportDate}>
                  {report.createdAt ? new Date(report.createdAt.seconds * 1000).toLocaleDateString() : 'Date not available'}
                </Text>
              </View>
              <View style={venueStyles.reportGrade}>
                <Text style={venueStyles.gradeLetter}>{getLetterGrade(report.averageGrade)}</Text>
              </View>
            </View>

            <View style={venueStyles.reportContent}>
              <Text style={venueStyles.reportExperience}>{report.overallExperience}</Text>

              <View style={venueStyles.ratingsGrid}>
                {report.safetyRating && (
                  <View style={venueStyles.ratingItem}>
                    <Text style={venueStyles.ratingLabel}>Safety</Text>
                    <Text style={venueStyles.ratingValue}>{getLetterGrade(report.safetyRating)}</Text>
                  </View>
                )}
                {report.equipmentRating && (
                  <View style={venueStyles.ratingItem}>
                    <Text style={venueStyles.ratingLabel}>Equipment</Text>
                    <Text style={venueStyles.ratingValue}>{getLetterGrade(report.equipmentRating)}</Text>
                  </View>
                )}
                {report.staffRating && (
                  <View style={venueStyles.ratingItem}>
                    <Text style={venueStyles.ratingLabel}>Staff</Text>
                    <Text style={venueStyles.ratingValue}>{getLetterGrade(report.staffRating)}</Text>
                  </View>
                )}
                {report.workingConditionsRating && (
                  <View style={venueStyles.ratingItem}>
                    <Text style={venueStyles.ratingLabel}>Working Conditions</Text>
                    <Text style={venueStyles.ratingValue}>{getLetterGrade(report.workingConditionsRating)}</Text>
                  </View>
                )}
                {report.breakTimeRating && (
                  <View style={venueStyles.ratingItem}>
                    <Text style={venueStyles.ratingLabel}>Break Time</Text>
                    <Text style={venueStyles.ratingValue}>{getLetterGrade(report.breakTimeRating)}</Text>
                  </View>
                )}
                {report.overtimeRating && (
                  <View style={venueStyles.ratingItem}>
                    <Text style={venueStyles.ratingLabel}>Overtime</Text>
                    <Text style={venueStyles.ratingValue}>{getLetterGrade(report.overtimeRating)}</Text>
                  </View>
                )}
                {report.schedulingRating && (
                  <View style={venueStyles.ratingItem}>
                    <Text style={venueStyles.ratingLabel}>Scheduling</Text>
                    <Text style={venueStyles.ratingValue}>{getLetterGrade(report.schedulingRating)}</Text>
                  </View>
                )}
              </View>

              {report.photos && report.photos.length > 0 && (
                <View style={venueStyles.mediaSection}>
                  <Text style={venueStyles.mediaLabel}>Photos:</Text>
                  <ScrollView horizontal style={venueStyles.mediaScroll}>
                    {report.photos.map((photo, photoIndex) => (
                      <Image
                        key={photoIndex}
                        source={{ uri: photo }}
                        style={venueStyles.mediaPreview}
                      />
                    ))}
                  </ScrollView>
                </View>
              )}

              {report.notes && (
                <View style={venueStyles.notesSection}>
                  <Text style={venueStyles.notesLabel}>Additional Notes:</Text>
                  <Text style={venueStyles.notesText}>{report.notes}</Text>
                </View>
              )}
            </View>
          </View>
        ))}
      </ScrollView>
    );
  };

  const toggleMobileDisplay = (section) => {
    if (activeSection === section) {
      setActiveSection(null);
    } else {
      setActiveSection(section);
    }
  }

  const MobileButton = ({ label, onPress, isActive = false, size = isMobile ? '0.9rem' : isTablet ? '0.7rem' : '1.1rem', textStyle = {}, buttonStyle }) => {
    const flatTextStyle = Array.isArray(textStyle) ? StyleSheet.flatten(textStyle) : textStyle;
    return (
    <TouchableOpacity style={[venueStyles.mobileButton, buttonStyle]} onPress={onPress}>
      <Text
        style={[
          venueStyles.mobileButtonText,
          isActive ? venueStyles.activeMobileButtonText : venueStyles.inactiveMobileButtonText,
          { fontSize: size, ...flatTextStyle }
        ]}
      >
        {label.split('\n').map((line, index) => (
          <Text key={index}>
            {line}
            {index < label.split('\n').length - 1 && '\n'}
          </Text>
        ))}
      </Text>
    </TouchableOpacity>
  );
  };

  const renderMobileDisplayButtons = () => {
    const toggleSize = getResponsiveValue({ mobile: '1.2rem', tablet: '1.5rem', medium: '1.8rem', laptop: '1.4rem', desktop: '1.4rem' });
    const optionSize = getResponsiveValue({ mobile: '0.7rem', tablet: '0.9rem', medium: '1.2rem', laptop: '0.9rem', desktop: '0.9rem' });

    const toggleShare = () => setMobileOptionsToggle(mobileOptionsToggle === 'share' ? null : 'share');
    const toggleView = () => setMobileOptionsToggle(mobileOptionsToggle === 'view' ? null : 'view');

    const rowHeight = getResponsiveValue({ mobile: 130, tablet: 150, medium: 160, laptop: 100, desktop: 100 });
    const slotStyle = { flex: 1, minHeight: rowHeight, justifyContent: 'center', minWidth: 0, position: 'relative' };

    const shareButtonOpacity = shareExpandedAnim.interpolate({ inputRange: [0, 1], outputRange: [1, 0] });
    const shareOptionsOpacity = shareExpandedAnim;
    const viewButtonOpacity = viewExpandedAnim.interpolate({ inputRange: [0, 1], outputRange: [1, 0] });
    const viewOptionsOpacity = viewExpandedAnim;

    return (
      <View data-testid="mobile-display-buttons" style={[venueStyles.mobileDisplayButtons, { minHeight: rowHeight }]}>
        <View style={[venueStyles.mobileShareViewRow, { minHeight: rowHeight }]}>
          {/* Left slot: Share button + options overlay (smooth expand/collapse) */}
          <View style={slotStyle}>
            <Animated.View style={[StyleSheet.absoluteFill, { justifyContent: 'center', opacity: shareButtonOpacity }]} pointerEvents={mobileOptionsToggle === 'share' ? 'none' : 'auto'}>
              <MobileButton buttonStyle={{ aspectRatio: 9 / 6, width: '100%', flex: 1 }} label="SHARE" onPress={toggleShare} isActive={mobileOptionsToggle === 'share'} size={toggleSize} />
            </Animated.View>
            <Animated.View
              style={[StyleSheet.absoluteFill, venueStyles.mobileSlotExpanded, { justifyContent: 'flex-start', opacity: shareOptionsOpacity }]}
              pointerEvents={mobileOptionsToggle === 'share' ? 'auto' : 'none'}
            >
              <View style={venueStyles.mobileSlotOptionsRow}>
                <MobileButton buttonStyle={venueStyles.mobileOptionButtonSmall} textStyle={venueStyles.mobileOptionButtonSmallText} label={"VENUE\nSPECS"} onPress={() => { handleAddTechInfo(); setMobileOptionsToggle(null); }} size={optionSize} />
                <MobileButton buttonStyle={venueStyles.mobileOptionButtonSmall} textStyle={venueStyles.mobileOptionButtonSmallText} label={"REPORT\nCARD"} onPress={() => { handleSubmitReport(); setMobileOptionsToggle(null); }} size={optionSize} />
                <MobileButton buttonStyle={venueStyles.mobileOptionButtonSmall} textStyle={venueStyles.mobileOptionButtonSmallText} label={"ROOM"} onPress={() => { setShowRoomEqModal(true); setMobileOptionsToggle(null); }} size={optionSize} />
              </View>
            </Animated.View>
          </View>

          {/* Right slot: View button + options overlay (smooth expand/collapse) */}
          <View style={slotStyle}>
            <Animated.View style={[StyleSheet.absoluteFill, { justifyContent: 'center', opacity: viewButtonOpacity }]} pointerEvents={mobileOptionsToggle === 'view' ? 'none' : 'auto'}>
              <MobileButton buttonStyle={{ aspectRatio: 9 / 6, width: '100%', flex: 1 }} label="VIEW" onPress={toggleView} isActive={mobileOptionsToggle === 'view'} size={toggleSize} />
            </Animated.View>
            <Animated.View
              style={[StyleSheet.absoluteFill, venueStyles.mobileSlotExpanded, { justifyContent: 'flex-start', opacity: viewOptionsOpacity }]}
              pointerEvents={mobileOptionsToggle === 'view' ? 'auto' : 'none'}
            >
              <View style={venueStyles.mobileSlotOptionsRow}>
                <MobileButton buttonStyle={venueStyles.mobileOptionButtonSmall} textStyle={venueStyles.mobileOptionButtonSmallText} isActive={activeSection === 'report'} label={"VENUE\nSPECS"} onPress={() => { toggleMobileDisplay('report'); setMobileOptionsToggle(null); }} size={optionSize} />
                <MobileButton buttonStyle={venueStyles.mobileOptionButtonSmall} textStyle={[venueStyles.mobileOptionButtonSmallText, { fontSize: isMobile ? '0.65rem' : '0.6rem' }]} isActive={activeSection === 'resources'} label={"RESOURCES"} onPress={() => { toggleMobileDisplay('resources'); setMobileOptionsToggle(null); }} size={optionSize} />
                <MobileButton buttonStyle={venueStyles.mobileOptionButtonSmall} textStyle={[venueStyles.mobileOptionButtonSmallText, { fontSize: isMobile ? '0.9rem' : '0.7rem' }]} isActive={activeSection === 'links'} label={"LINKS &\nMANUALS"} onPress={() => { toggleMobileDisplay('links'); setMobileOptionsToggle(null); }} size={optionSize} />
                <MobileButton
                  buttonStyle={venueStyles.mobileOptionButtonSmall}
                  textStyle={venueStyles.mobileOptionButtonSmallText}
                  label={"ROOM"}
                  onPress={() => {
                    setMobileOptionsToggle(null);
                    const submissions = publicRoomEqs.filter((e) => e.type === 'roomEqSubmission');
                    if (submissions.length > 0) {
                      setRoomEqViewerSubmissions(submissions);
                      setRoomEqViewerSubmissionIndex(0);
                      const first = submissions[0];
                      setRoomEqViewerActiveTab(first.eq ? 'EQ' : first.rf ? 'RF' : 'ADD');
                      setRoomEqViewerMode('submission');
                      setRoomEqViewerVisible(true);
                      return;
                    }
                    const slides = [];
                    publicRoomEqs.forEach((entry) => {
                      if (entry.type === 'rf') {
                        slides.push({ type: 'rf', entry });
                      } else if (entry.type !== 'roomEqSubmission') {
                        (entry.photos || []).forEach((p, i) => slides.push({ type: 'eq', entry, photo: p, photoIndex: i }));
                      }
                    });
                    if (slides.length === 0) {
                      setShowNoRoomEqPopup(true);
                      return;
                    }
                    setRoomEqViewerSlides(slides);
                    setRoomEqViewerIndex(0);
                    setRoomEqViewerMode('slides');
                    setRoomEqViewerVisible(true);
                  }}
                  size={optionSize}
                />
              </View>
            </Animated.View>
          </View>
        </View>
      </View>
    );
  }

  const specsFields = useMemo(
    () => [...VENUE_REPORT_TECH_SPEC_FIELDS, { label: 'Notes', field: 'notes' }],
    []
  );
  const activeLiveSpecsReport = liveSpecsReports[liveSpecsIndex] || null;
  const activeSpecCardInterestItems = useMemo(() => {
    let labels = [];
    if (specsTab === 'live' && activeLiveSpecsReport?.id) {
      labels = mergeApprovedVenueInterestLabels(approvedVenueInterestDocs, {
        liveReportId: activeLiveSpecsReport.id,
      });
    } else if (specsTab === 'vrc') {
      labels = mergeApprovedVenueInterestLabels(approvedVenueInterestDocs, { liveReportId: null });
    }
    return resolveInterestDisplayItems(labels, interestCatalog);
  }, [approvedVenueInterestDocs, specsTab, activeLiveSpecsReport?.id, interestCatalog]);
  const hasLiveSpecsReports = liveSpecsReports.length > 0;
  const canNavigateLiveReportPrev = liveSpecsReports.length > 1 && liveSpecsIndex > 0;
  const canNavigateLiveReportNext =
    liveSpecsReports.length > 1 && liveSpecsIndex < liveSpecsReports.length - 1;
  const canEditLiveSpecs = isSignedInAccount(auth.currentUser);
  const isLiveFormActive = liveSpecsCreateMode || liveSpecsEditingExisting;
  const isLiveEditable = canEditLiveSpecs && isLiveFormActive;
  const isOwnActiveLiveReport = useMemo(() => {
    if (!activeLiveSpecsReport?.id || !auth.currentUser?.uid) return false;
    return activeLiveSpecsReport.userId === auth.currentUser.uid;
  }, [activeLiveSpecsReport, auth.currentUser?.uid]);

  const liveSpecBaselineValue = useCallback(
    (field) => {
      if (liveSpecsCreateMode) {
        const vrc = venueData?.[field];
        return vrc == null ? '' : String(vrc);
      }
      const raw = activeLiveSpecsReport?.[field];
      return raw == null ? '' : String(raw);
    },
    [liveSpecsCreateMode, venueData, activeLiveSpecsReport]
  );

  const hasLiveSpecsChanges = useMemo(() => {
    return specsFields.some(({ field }) => {
      const current = liveSpecBaselineValue(field);
      const draft = liveSpecsDraft[field] == null ? '' : String(liveSpecsDraft[field]);
      return draft !== current;
    });
  }, [specsFields, liveSpecsDraft, liveSpecBaselineValue]);

  const changedLiveSpecFields = useMemo(() => {
    const changed = new Set();
    specsFields.forEach(({ field }) => {
      const current = liveSpecBaselineValue(field);
      const draft = liveSpecsDraft[field] == null ? '' : String(liveSpecsDraft[field]);
      if (draft !== current) changed.add(field);
    });
    return changed;
  }, [specsFields, liveSpecsDraft, liveSpecBaselineValue]);
  const canDeleteActiveLiveReport = useMemo(() => {
    if (isLiveFormActive || !isOwnActiveLiveReport) return false;
    return true;
  }, [isLiveFormActive, isOwnActiveLiveReport]);

  useEffect(() => {
    setLiveSpecsIndex((prev) => Math.max(0, Math.min(prev, Math.max(0, liveSpecsReports.length - 1))));
  }, [liveSpecsReports.length]);

  // Leaving the active report (paginate / delete) should exit edit-existing mode.
  useEffect(() => {
    if (!liveSpecsEditingExisting) return;
    if (!isOwnActiveLiveReport) {
      setLiveSpecsEditingExisting(false);
    }
  }, [liveSpecsEditingExisting, isOwnActiveLiveReport, activeLiveSpecsReport?.id]);

  useEffect(() => {
    if (liveSpecsCreateMode) return;
    if (liveSpecsEditingExisting) return; // keep in-progress edits while toggling fields
    if (!activeLiveSpecsReport) {
      setLiveSpecsDraft({});
      return;
    }
    const nextDraft = {};
    specsFields.forEach(({ field }) => {
      const raw = activeLiveSpecsReport?.[field];
      nextDraft[field] = raw == null ? '' : String(raw);
    });
    setLiveSpecsDraft(nextDraft);
  }, [activeLiveSpecsReport, specsFields, liveSpecsCreateMode, liveSpecsEditingExisting]);

  const handleLiveSpecChange = useCallback((field, value) => {
    setLiveSpecsDraft((prev) => ({ ...prev, [field]: value }));
  }, []);

  const resetLiveSpecsDraft = useCallback(() => {
    if (liveSpecsCreateMode) {
      const baseline = {};
      specsFields.forEach(({ field }) => {
        baseline[field] = liveSpecBaselineValue(field);
      });
      setLiveSpecsDraft(baseline);
      return;
    }
    if (!activeLiveSpecsReport) {
      setLiveSpecsDraft({});
      return;
    }
    const nextDraft = {};
    specsFields.forEach(({ field }) => {
      const raw = activeLiveSpecsReport?.[field];
      nextDraft[field] = raw == null ? '' : String(raw);
    });
    setLiveSpecsDraft(nextDraft);
  }, [activeLiveSpecsReport, specsFields, liveSpecsCreateMode, liveSpecBaselineValue]);

  const exitLiveSpecsForm = useCallback(() => {
    setLiveSpecsCreateMode(false);
    setLiveSpecsEditingExisting(false);
    setLiveSpecsDraft({});
  }, []);

  const formatLiveReportDate = useCallback((value) => {
    if (!value) return null;
    try {
      const date = value?.toDate
        ? value.toDate()
        : value?.seconds
          ? new Date(value.seconds * 1000)
          : value instanceof Date
            ? value
            : new Date(value);
      if (Number.isNaN(date.getTime())) return null;
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (e) {
      return null;
    }
  }, []);

  const activeLiveReportAttribution = useMemo(() => {
    if (!activeLiveSpecsReport) return null;
    const name = String(
      activeLiveSpecsReport.userName
      || activeLiveSpecsReport.lastEditedByName
      || 'Member'
    ).trim() || 'Member';
    const dateLabel = formatLiveReportDate(
      activeLiveSpecsReport.updatedAt || activeLiveSpecsReport.createdAt
    );
    return { name, dateLabel, wasEdited: Boolean(activeLiveSpecsReport.updatedAt) };
  }, [activeLiveSpecsReport, formatLiveReportDate]);

  const openLiveSpecsEditExisting = useCallback(() => {
    if (!isSignedInAccount(auth.currentUser)) {
      setToast({ message: 'Sign in to edit LIVE tech specs.', type: 'warning' });
      navigation.navigate('Login');
      return;
    }
    if (!isOwnActiveLiveReport || !activeLiveSpecsReport) {
      setToast({ message: 'You can only edit LIVE specs you submitted.', type: 'warning' });
      return;
    }
    if (liveSpecsEditingExisting) {
      if (hasLiveSpecsChanges) {
        Alert.alert(
          'Discard LIVE edits?',
          'Stop editing and discard your unsaved changes?',
          [
            { text: 'Keep editing', style: 'cancel' },
            { text: 'Discard', style: 'destructive', onPress: () => exitLiveSpecsForm() },
          ]
        );
        return;
      }
      exitLiveSpecsForm();
      return;
    }
    const nextDraft = {};
    specsFields.forEach(({ field }) => {
      const raw = activeLiveSpecsReport?.[field];
      nextDraft[field] = raw == null ? '' : String(raw);
    });
    setLiveSpecsDraft(nextDraft);
    setLiveSpecsEditingExisting(true);
    setLiveSpecsCreateMode(false);
    setSpecsTab('live');
  }, [
    auth,
    navigation,
    isOwnActiveLiveReport,
    activeLiveSpecsReport,
    liveSpecsEditingExisting,
    hasLiveSpecsChanges,
    exitLiveSpecsForm,
    specsFields,
  ]);

  const openLiveSpecsCreateForm = useCallback(() => {
    if (!isSignedInAccount(auth.currentUser)) {
      setToast({ message: 'Sign in to publish LIVE tech specs.', type: 'warning' });
      navigation.navigate('Login');
      return;
    }
    const startCreate = () => {
      const baseline = {};
      specsFields.forEach(({ field }) => {
        const vrc = venueData?.[field];
        baseline[field] = vrc == null ? '' : String(vrc);
      });
      setLiveSpecsDraft(baseline);
      setLiveSpecsEditingExisting(false);
      setLiveSpecsCreateMode(true);
      setSpecsTab('live');
    };
    if (isLiveFormActive && hasLiveSpecsChanges) {
      Alert.alert(
        'Discard LIVE edits?',
        'Start a new LIVE submission and discard your unsaved changes?',
        [
          { text: 'Keep editing', style: 'cancel' },
          { text: 'Discard', style: 'destructive', onPress: startCreate },
        ]
      );
      return;
    }
    startCreate();
  }, [
    auth,
    navigation,
    specsFields,
    venueData,
    isLiveFormActive,
    hasLiveSpecsChanges,
  ]);

  const openLiveSpecsEditForm = useCallback(() => {
    if (!isSignedInAccount(auth.currentUser)) {
      setToast({ message: 'Sign in to edit LIVE tech specs.', type: 'warning' });
      navigation.navigate('Login');
      return;
    }

    // Toggle off whichever form mode is active.
    if (isLiveFormActive) {
      if (hasLiveSpecsChanges) {
        Alert.alert(
          'Discard LIVE edits?',
          'Turn off the pencil and discard your unsaved changes?',
          [
            { text: 'Keep editing', style: 'cancel' },
            {
              text: 'Discard',
              style: 'destructive',
              onPress: () => exitLiveSpecsForm(),
            },
          ]
        );
        return;
      }
      exitLiveSpecsForm();
      return;
    }

    // Header pencil always starts a *new* LIVE submission (edit-own uses the
    // separate Edit control next to the attribution line).
    openLiveSpecsCreateForm();
  }, [
    auth,
    navigation,
    isLiveFormActive,
    hasLiveSpecsChanges,
    exitLiveSpecsForm,
    openLiveSpecsCreateForm,
  ]);

  const navigateLiveReport = useCallback((direction) => {
    if (!liveSpecsReports.length || isLiveFormActive) return;
    if (direction === 'prev' && !canNavigateLiveReportPrev) return;
    if (direction === 'next' && !canNavigateLiveReportNext) return;
    const go = () => {
      setLiveSpecsIndex((p) => {
        if (direction === 'prev') return Math.max(0, p - 1);
        return Math.min(liveSpecsReports.length - 1, p + 1);
      });
    };
    if (hasLiveSpecsChanges) {
      Alert.alert(
        'Unsaved LIVE changes',
        'Discard your unsaved changes and switch reports?',
        [
          { text: 'Keep editing', style: 'cancel' },
          { text: 'Discard', style: 'destructive', onPress: () => go() },
        ]
      );
      return;
    }
    go();
  }, [
    liveSpecsReports.length,
    hasLiveSpecsChanges,
    isLiveFormActive,
    canNavigateLiveReportPrev,
    canNavigateLiveReportNext,
  ]);

  const handleLiveSpecsSubmit = useCallback(async () => {
    if (liveSpecsSubmitting) return;
    if (!hasLiveSpecsChanges) return;
    if (!isSignedInAccount(auth.currentUser)) {
      setToast({ message: 'Sign in to publish LIVE tech specs.', type: 'warning' });
      navigation.navigate('Login');
      return;
    }
    const payloadSpecs = {};
    specsFields.forEach(({ field }) => {
      const raw = liveSpecsDraft[field];
      payloadSpecs[field] = raw == null ? '' : String(raw);
    });
    const hasAnyInput = Object.values(payloadSpecs).some((v) => String(v).trim() !== '');
    if (!hasAnyInput) return;
    try {
      setLiveSpecsSubmitting(true);
      const db = getFirestore();

      // Update an existing submission the current user owns.
      if (liveSpecsEditingExisting && activeLiveSpecsReport?.id) {
        if (activeLiveSpecsReport.userId !== auth.currentUser.uid) {
          Alert.alert('Error', 'You can only edit LIVE specs you submitted.');
          return;
        }
        const updatePayload = {
          ...payloadSpecs,
          updatedAt: new Date(),
          lastEditedBy: auth.currentUser.uid,
          lastEditedByName: userData?.displayName || userData?.username || 'Member',
        };
        await updateDoc(doc(db, 'venue_reports', activeLiveSpecsReport.id), updatePayload);
        setLiveSpecsReports((prev) =>
          sortLiveReportsNewestFirst(
            prev.map((r) => (r.id === activeLiveSpecsReport.id ? { ...r, ...updatePayload } : r))
          )
        );
        setLiveSpecsEditingExisting(false);
        setSpecsTab('live');
        setToast({ message: 'Your LIVE specs were updated.', type: 'success' });
        return;
      }

      const reportData = {
        ...payloadSpecs,
        venueId,
        venueName: venueData?.name || '',
        userId: auth.currentUser.uid,
        userEmail: auth.currentUser.email || '',
        userName: userData?.displayName || userData?.username || 'Member',
        createdAt: new Date(),
        status: LIVE_COMMUNITY_STATUS,
        submissionSource: LIVE_COMMUNITY_SOURCE,
        isTechSpecsOnly: true,
        userType: 'technical',
        promotedToVrc: false,
      };
      const reportRef = await addDoc(collection(db, 'venue_reports'), reportData);
      const nextReport = { id: reportRef.id, ...reportData };
      setLiveSpecsReports((prev) => sortLiveReportsNewestFirst([nextReport, ...prev.filter((r) => r.id !== reportRef.id)]));
      setLiveSpecsCreateMode(false);
      setLiveSpecsEditingExisting(false);
      setSpecsTab('live');
      setLiveSpecsIndex(0);
      setToast({ message: 'LIVE specs published for this venue.', type: 'success' });
    } catch (error) {
      console.error('Error submitting LIVE tech specs report:', error);
      Alert.alert(
        'Error',
        liveSpecsEditingExisting
          ? 'Failed to update your LIVE specs. Please try again.'
          : 'Failed to publish LIVE specs. Please try again.'
      );
    } finally {
      setLiveSpecsSubmitting(false);
    }
  }, [
    auth,
    liveSpecsSubmitting,
    hasLiveSpecsChanges,
    specsFields,
    liveSpecsDraft,
    venueId,
    venueData?.name,
    userData?.displayName,
    userData?.username,
    navigation,
    liveSpecsEditingExisting,
    activeLiveSpecsReport,
  ]);

  const performLiveSpecsDelete = useCallback(async (reportId) => {
    if (!reportId || liveSpecsDeleting) return;
    try {
      setLiveSpecsDeleting(true);
      const db = getFirestore();
      await deleteDoc(doc(db, 'venue_reports', reportId));
      setLiveSpecsReports((prev) => prev.filter((report) => report.id !== reportId));
      setLiveSpecsCreateMode(false);
      setLiveSpecsEditingExisting(false);
      setLiveSpecsDraft({});
      setToast({ message: 'Your LIVE report was deleted.', type: 'success' });
    } catch (error) {
      console.error('Error deleting LIVE tech specs report:', error);
      Alert.alert('Error', 'Failed to delete LIVE report. Please try again.');
    } finally {
      setLiveSpecsDeleting(false);
    }
  }, [liveSpecsDeleting]);

  const handleLiveSpecsDelete = useCallback(() => {
    if (!canDeleteActiveLiveReport || !activeLiveSpecsReport?.id || liveSpecsDeleting || liveSpecsSubmitting) return;

    const reportId = activeLiveSpecsReport.id;
    const message = hasLiveSpecsChanges
      ? 'You have unsaved edits on this report. Delete your submitted LIVE tech specs anyway? This cannot be undone.'
      : 'Remove your LIVE tech specs submission? This cannot be undone.';

    showConfirmation(
      'Delete LIVE report',
      message,
      () => performLiveSpecsDelete(reportId),
      null,
      { confirmLabel: 'Delete', cancelLabel: 'Cancel', confirmVariant: 'danger' }
    );
  }, [
    canDeleteActiveLiveReport,
    activeLiveSpecsReport?.id,
    liveSpecsDeleting,
    liveSpecsSubmitting,
    hasLiveSpecsChanges,
    showConfirmation,
    performLiveSpecsDelete,
  ]);

  const navPillStyle = (active, variant = 'default') => {
    const base = {
      paddingHorizontal: 10,
      paddingVertical: 5,
      borderRadius: 12,
      minHeight: SPEC_ICON_BTN_SIZE,
      justifyContent: 'center',
      alignItems: 'center',
    };
    if (variant === 'vrc') {
      return {
        ...base,
        backgroundColor: active ? SPEC_NAV_BLUE : SPEC_NAV_ICON_GRAY,
      };
    }
    if (variant === 'live') {
      return {
        ...base,
        backgroundColor: active ? SPEC_NAV_BLUE : SPEC_NAV_ICON_GRAY,
      };
    }
    if (variant === 'links') {
      return {
        ...base,
        width: '100%',
        backgroundColor: SPEC_NAV_LINKS_BG,
        borderWidth: active ? 2 : 1,
        borderColor: active ? '#111111' : 'rgba(0,0,0,0.12)',
      };
    }
    if (variant === 'resources') {
      return {
        ...base,
        width: '100%',
        backgroundColor: SPEC_NAV_RESOURCES_BG,
        opacity: active ? 1 : 0.9,
      };
    }
    return {
      ...base,
      backgroundColor: active ? 'rgba(0,0,0,0.12)' : SPEC_NAV_BLUE,
    };
  };

  const specIconBtnStyle = (active = false) => ({
    width: SPEC_ICON_BTN_SIZE,
    height: SPEC_ICON_BTN_SIZE,
    borderRadius: SPEC_ICON_BTN_SIZE / 2,
    backgroundColor: active ? SPEC_NAV_BLUE : SPEC_NAV_ICON_GRAY,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  });

  const goToTechSpecs = useCallback((tab = specsTab) => {
    setActiveSection(null);
    setSpecsTab(tab);
    if (tab === 'vrc') {
      setLiveSpecsCreateMode(false);
      setLiveSpecsEditingExisting(false);
    }
  }, [specsTab]);

  const handleLiveTabPress = useCallback(() => {
    if (hasLiveSpecsReports) {
      setLiveSpecsCreateMode(false);
      setLiveSpecsEditingExisting(false);
    }
    goToTechSpecs('live');
  }, [hasLiveSpecsReports, goToTechSpecs]);

  const handleSpecFlagSubmit = useCallback(async () => {
    const notes = specFlagNotes.trim();
    if (!notes) {
      setToast({ message: 'Please add a note for this venue.', type: 'warning' });
      return;
    }
    const isLoggedIn = Boolean(auth.currentUser);
    const isInstant = isInstantSpecFlagCategory(specFlagCategory);
    if (!isLoggedIn && !isInstant) {
      setToast({ message: 'Sign in to flag Accessibility or Safety notes.', type: 'warning' });
      return;
    }
    if (!isInstant && !isLoggedIn) {
      return;
    }
    const contributor = auth.currentUser;
    const status = isInstant ? 'approved' : 'pending';
    try {
      setSpecFlagSubmitting(true);
      const db = getFirestore();
      await addDoc(collection(db, 'venue_spec_flags'), {
        venueId,
        venueName: venueData?.name || '',
        category: specFlagCategory,
        categoryLabel: getResourceCategoryLabel(specFlagCategory),
        notes,
        userId: contributor?.uid || null,
        userEmail: contributor?.email || '',
        userName: userData?.displayName || userData?.username || (contributor ? 'Anonymous' : 'Guest'),
        status,
        createdAt: new Date(),
        ...(isInstant ? { reviewedAt: new Date() } : {}),
      });
      setShowSpecFlagModal(false);
      setSpecFlagNotes('');
      setSpecFlagCategory('education');
      setToast({
        message: isInstant ? 'Note added to this venue.' : 'Thanks — your note was submitted for review.',
        type: 'success',
        id: Date.now(),
      });
    } catch (error) {
      console.error('Error submitting spec flag:', error);
      setToast({ message: 'Failed to add note. Please try again.', type: 'error' });
    } finally {
      setSpecFlagSubmitting(false);
    }
  }, [auth, specFlagNotes, specFlagCategory, venueId, venueData?.name, userData?.displayName, userData?.username]);

  const handleVenueInterestsSubmit = useCallback(async () => {
    if (venueInterestSelection.length === 0) {
      setToast({ message: 'Select at least one interest to tag this venue.', type: 'warning' });
      return;
    }
    const contributor = auth.currentUser;
    try {
      setVenueInterestsSubmitting(true);
      const db = getFirestore();
      const liveReportId =
        specsTab === 'live' && !isLiveFormActive && activeLiveSpecsReport?.id
          ? activeLiveSpecsReport.id
          : null;
      await addDoc(collection(db, 'venue_interest_tags'), {
        venueId,
        venueName: venueData?.name || '',
        interests: venueInterestSelection,
        userId: contributor?.uid || null,
        userEmail: contributor?.email || '',
        userName: userData?.displayName || userData?.username || (contributor ? 'Anonymous' : 'Guest'),
        status: 'approved',
        createdAt: new Date(),
        reviewedAt: new Date(),
        ...(liveReportId ? { liveReportId } : {}),
      });
      setShowVenueInterestsModal(false);
      setVenueInterestSelection([]);
      setToast({ message: 'Tags added to this venue.', type: 'success' });
    } catch (error) {
      console.error('Error submitting venue interests:', error);
      setToast({ message: 'Failed to add tags. Please try again.', type: 'error' });
    } finally {
      setVenueInterestsSubmitting(false);
    }
  }, [
    auth,
    venueInterestSelection,
    venueId,
    venueData?.name,
    userData?.displayName,
    userData?.username,
    specsTab,
    isLiveFormActive,
    activeLiveSpecsReport?.id,
  ]);

  const openVenueInterestsModal = useCallback(() => {
    setShowVenueInterestsModal(true);
  }, []);

  const openSpecFlagModal = useCallback(() => {
    if (!auth.currentUser && !isInstantSpecFlagCategory(specFlagCategory)) {
      setSpecFlagCategory('education');
    }
    setShowSpecFlagModal(true);
  }, [auth, specFlagCategory]);

  const toggleVenueInterest = useCallback((label) => {
    setVenueInterestSelection((prev) =>
      prev.includes(label) ? prev.filter((item) => item !== label) : [...prev, label]
    );
  }, []);

  const resetResourceSubmissionForm = useCallback(() => {
    setResourceForm({ name: '', url: '', description: '', scope: 'venue', category: '', mapEmbedLink: '' });
    setResourceCustomCategory('');
    setResourcePhoto({ file: null, preview: null, width: null, height: null });
    setResourceEmbedStatus({ state: 'idle', text: '' });
    setResourceModalStep(null);
  }, []);

  const closeResourceModal = useCallback(() => {
    resetResourceSubmissionForm();
  }, [resetResourceSubmissionForm]);

  const openResourceModal = useCallback(() => {
    setResourceForm({ name: '', url: '', description: '', scope: 'venue', category: '', mapEmbedLink: '' });
    setResourceCustomCategory('');
    setResourcePhoto({ file: null, preview: null, width: null, height: null });
    setResourceEmbedStatus({ state: 'idle', text: '' });
    setResourceModalStep('category');
  }, []);

  const openCustomResourceCategoryModal = useCallback(() => {
    setResourceForm({ name: '', url: '', description: '', scope: 'venue', category: '', mapEmbedLink: '' });
    setResourceCustomCategory('');
    setResourcePhoto({ file: null, preview: null, width: null, height: null });
    setResourceEmbedStatus({ state: 'idle', text: '' });
    setResourceModalStep('custom-category');
  }, []);

  /** Open the share-resource form already scoped to a category (skips category picker). */
  const openResourceModalForCategory = useCallback((categoryId) => {
    if (!categoryId) return;
    setResourceForm({ name: '', url: '', description: '', scope: 'venue', category: categoryId, mapEmbedLink: '' });
    setResourceCustomCategory('');
    setResourcePhoto({ file: null, preview: null, width: null, height: null });
    setResourceEmbedStatus({ state: 'idle', text: '' });
    setResourceModalStep('form');
  }, []);

  useEffect(() => {
    const embed = String(resourceForm.mapEmbedLink || '').trim();
    if (resourceEmbedResolveTimer.current) clearTimeout(resourceEmbedResolveTimer.current);
    if (!embed || resourceModalStep !== 'form') {
      setResourceEmbedStatus({ state: 'idle', text: '' });
      return undefined;
    }
    const placeRef = extractGooglePlaceRefFromMapEmbed(embed);
    setResourceEmbedStatus({
      state: 'loading',
      text: placeRef.featureId || placeRef.placeId
        ? 'Looking up Google place coordinates…'
        : 'Resolving coordinates from this embed…',
    });
    resourceEmbedResolveTimer.current = setTimeout(async () => {
      const resolved = await resolveEmbedCoordinatesForSave(embed, {
        city: (venueData?.city || '').trim(),
        state: (venueData?.state || '').trim(),
        name: resourceForm.name,
      });
      setResourceEmbedStatus(formatEmbedResolveStatus(resolved));
    }, 700);
    return () => {
      if (resourceEmbedResolveTimer.current) clearTimeout(resourceEmbedResolveTimer.current);
    };
  }, [resourceForm.mapEmbedLink, resourceForm.name, resourceModalStep, venueData?.city, venueData?.state]);

  const handleResourcePhotoSelect = useCallback(async (file) => {
    if (!file) return;
    let preview = null;
    if (file.uri) preview = file.uri;
    else if (typeof URL !== 'undefined' && URL.createObjectURL) preview = URL.createObjectURL(file);
    const { width, height } = await readImageDimensions(file);
    setResourcePhoto({ file, preview, width, height });
  }, []);

  const handleResourcePhotoPick = useCallback(async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({ type: 'image/*', copyToCacheDirectory: true });
      if (!result.canceled && result.assets?.length) {
        const asset = result.assets[0];
        await handleResourcePhotoSelect({ ...asset, uri: asset.uri, name: asset.name });
      }
    } catch (error) {
      console.error('Error picking resource photo:', error);
      setToast({ message: 'Could not pick image. Please try again.', type: 'error' });
    }
  }, [handleResourcePhotoSelect]);

  const handleResourceSubmit = useCallback(async () => {
    if (!isRealUser(auth.currentUser)) {
      setToast({ message: 'Please sign in to share a resource.', type: 'warning' });
      return;
    }
    const name = resourceForm.name.trim();
    if (!resourceForm.category) {
      setToast({ message: 'Please choose a resource category.', type: 'warning' });
      return;
    }
    if (!name) {
      setToast({ message: 'Please enter a resource name.', type: 'warning' });
      return;
    }
    try {
      setResourceSubmitting(true);
      const db = getFirestore();
      const storage = getStorage();
      const uid = auth.currentUser.uid;
      let imageUrl = null;
      let imageWidth = null;
      let imageHeight = null;

      if (resourcePhoto.file) {
        const file = resourcePhoto.file;
        let blob;
        if (file.uri) {
          blob = await (await fetch(file.uri)).blob();
        } else if (file instanceof Blob) {
          blob = file;
        } else {
          blob = await (await fetch(file)).blob();
        }
        const ext = (file.name || 'image').split('.').pop()?.toLowerCase() || 'jpg';
        const path = `resource-submissions/${venueId}/${uid}_${Date.now()}.${ext}`;
        const refPath = storageRef(storage, path);
        await uploadBytes(refPath, blob);
        imageUrl = await getDownloadURL(refPath);
        imageWidth = resourcePhoto.width || null;
        imageHeight = resourcePhoto.height || null;
      }

      const mapEmbedLink = resourceForm.mapEmbedLink.trim() || null;
      const embedCoords = mapEmbedLink
        ? await resolveEmbedCoordinatesForSave(mapEmbedLink, {
            city: (venueData?.city || '').trim(),
            state: (venueData?.state || '').trim(),
            name,
          })
        : {};

      await addDoc(collection(db, 'resource_submissions'), {
        resourceName: name,
        url: formatExternalUrl(resourceForm.url.trim()) || null,
        description: resourceForm.description.trim() || null,
        scope: resourceForm.scope,
        category: resourceForm.category,
        categoryLabel: resourceCustomCategory.trim()
          || resourceCategoryCatalog.find((category) => category.id === resourceForm.category)?.label
          || getResourceCategoryLabel(resourceForm.category),
        mapEmbedLink,
        imageUrl,
        imageWidth,
        imageHeight,
        venueId,
        venueName: venueData?.name || '',
        venueCity: (venueData?.city || '').trim(),
        venueState: (venueData?.state || '').trim(),
        latitude: embedCoords.latitude ?? null,
        longitude: embedCoords.longitude ?? null,
        googlePlaceId: embedCoords.googlePlaceId || null,
        googleCid: embedCoords.googleCid || null,
        embedResolvedBy: embedCoords.embedResolvedBy || null,
        embedResolvedMethod: embedCoords.embedResolvedMethod || null,
        embedResolvedAt: embedCoords.latitude != null ? serverTimestamp() : null,
        userId: uid,
        userEmail: auth.currentUser.email || '',
        userName: userData?.displayName || userData?.username || userData?.name || 'Anonymous',
        status: 'pending',
        createdAt: serverTimestamp(),
        submittedFrom: 'venue_screen',
      });
      resetResourceSubmissionForm();
      setToast({
        message: 'Resource submitted! It will appear here once an admin approves it.',
        type: 'success',
      });
    } catch (error) {
      console.error('Error submitting resource:', error);
      setToast({ message: 'Failed to submit resource. Please try again.', type: 'error' });
    } finally {
      setResourceSubmitting(false);
    }
  }, [auth.currentUser, resourceForm, resourcePhoto, resourceCustomCategory, resourceCategoryCatalog, venueId, venueData?.name, venueData?.city, venueData?.state, userData?.displayName, userData?.username, userData?.name, resetResourceSubmissionForm]);

  const addVenueDocumentFiles = useCallback((files) => {
    const selectedFiles = Array.from(files || []).filter(Boolean);
    if (!selectedFiles.length) return;
    setVenueDocumentFiles((previous) => {
      const additions = selectedFiles.map((file) => {
        const fileName = file.name || 'Document';
        const extensionIndex = fileName.lastIndexOf('.');
        return {
          file,
          fileName,
          name: extensionIndex > 0 ? fileName.slice(0, extensionIndex) : fileName,
        };
      });
      return [...previous, ...additions];
    });
  }, []);

  const handleVenueDocumentPick = useCallback(async () => {
    if (Platform.OS === 'web') {
      venueDocumentInputRef.current?.click?.();
      return;
    }
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: '*/*',
        multiple: true,
        copyToCacheDirectory: true,
      });
      if (!result.canceled) addVenueDocumentFiles(result.assets || []);
    } catch (error) {
      console.error('Error selecting venue documents:', error);
      setToast({ message: 'Could not select documents. Please try again.', type: 'error' });
    }
  }, [addVenueDocumentFiles]);

  const handleVenueDocumentSubmit = useCallback(async () => {
    const contributor = auth.currentUser;
    if (!isSignedInAccount(contributor)) {
      setToast({ message: 'Sign in to upload venue documents.', type: 'warning' });
      navigation.navigate('Login');
      return;
    }
    if (!venueDocumentFiles.length) {
      setToast({ message: 'Choose at least one document to upload.', type: 'warning' });
      return;
    }
    if (venueDocumentFiles.some((item) => !item.name.trim())) {
      setToast({ message: 'Enter a name for each document.', type: 'warning' });
      return;
    }

    setVenueDocumentSubmitting(true);
    try {
      const db = getFirestore();
      const storage = getStorage();
      const timestamp = Date.now();
      const documents = [];
      for (let index = 0; index < venueDocumentFiles.length; index += 1) {
        const item = venueDocumentFiles[index];
        const fileName = item.fileName || item.file?.name || `document-${index + 1}`;
        const safeFileName = fileName.replace(/[^\w.-]+/g, '_');
        const storagePath = `venue-document-submissions/${venueId}/${contributor.uid}_${timestamp}_${index}_${safeFileName}`;
        const fileBlob = item.file?.uri
          ? await (await fetch(item.file.uri)).blob()
          : typeof Blob !== 'undefined' && item.file instanceof Blob
            ? item.file
            : await (await fetch(item.file)).blob();
        const fileRef = storageRef(storage, storagePath);
        await uploadBytes(fileRef, fileBlob);
        const url = await getDownloadURL(fileRef);
        documents.push({
          name: item.name.trim(),
          fileName,
          url,
          storagePath,
          contentType: fileBlob.type || item.file?.mimeType || item.file?.type || '',
          size: item.file?.size || fileBlob.size || 0,
        });
      }

      await addDoc(collection(db, 'venue_document_submissions'), {
        venueId,
        venueName: venueData?.name || '',
        documents,
        userId: contributor.uid,
        userEmail: contributor.email || '',
        userName: userData?.displayName || userData?.username || contributor.displayName || 'Anonymous',
        status: 'pending',
        createdAt: serverTimestamp(),
      });
      setShowVenueDocumentUploadModal(false);
      setVenueDocumentFiles([]);
      setToast({
        message: 'Documents submitted. They will appear under Links & Manuals after admin approval.',
        type: 'success',
      });
    } catch (error) {
      console.error('Error submitting venue documents:', error);
      setToast({ message: 'Failed to submit documents. Please try again.', type: 'error' });
    } finally {
      setVenueDocumentSubmitting(false);
    }
  }, [auth, navigation, venueDocumentFiles, venueId, venueData?.name, userData?.displayName, userData?.username]);

  /** Opens the venue document upload (admin-reviewed). Guests go to sign in first so they don't lose picked files. */
  const openVenueDocumentUpload = useCallback(() => {
    if (!isSignedInAccount(auth.currentUser)) {
      setToast({ message: 'Sign in to upload venue documents.', type: 'warning' });
      navigation.navigate('Login');
      return;
    }
    setVenueDocumentFiles([]);
    setShowVenueDocumentUploadModal(true);
  }, [auth, navigation]);

  const specNavHeight = isLiveFormActive ? SPEC_SHEET_NAV_OFFSET_EDIT : SPEC_SHEET_NAV_OFFSET;

  const specSheetHeaderBarStyle = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 4,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingHorizontal: SPEC_SHEET_PANEL_PADDING,
    paddingTop: 8,
    height: specNavHeight,
    backgroundColor: isDarkMode ? SPEC_SHEET_PANEL_DARK_BG : '#E9E9E9',
    ...(updatedColorScheme ? {
      borderTopLeftRadius: 8,
      borderTopRightRadius: 8,
    } : {}),
  };

  const specSheetHeaderLeftColStyle = {
    width: SPEC_SHEET_HEADER_LEFT_WIDTH,
    flexShrink: 0,
    flexDirection: 'column',
    gap: 6,
    alignItems: 'stretch',
    justifyContent: 'flex-start',
  };

  const specSheetHeaderRightColStyle = {
    flex: 1,
    alignItems: 'flex-end',
    justifyContent: 'flex-start',
    marginLeft: 8,
  };

  const specSheetBodyStyle = {
    paddingTop: specNavHeight,
    paddingHorizontal: SPEC_SHEET_PANEL_PADDING,
    width: '100%',
  };

  const renderGradeCards = () => (
    <View style={[venueStyles.gradeCardGroup, { width: '100%' }]}>
      {gradeData.map((data, index) => (
        <TouchableOpacity
          key={index}
          style={{
            ...venueStyles.gradeCard,
            width: '100%',
            maxWidth: '100%',
            alignSelf: 'stretch',
            flex: 1,
            ...(isDarkMode ? { backgroundColor: '#1e3050' } : {}),
          }}
          onPress={() => setShowCategories(data.title)}
        >
          <Text style={venueStyles.gradeCardTitle}>{data.title}</Text>
          <Text style={venueStyles.gradeCardGrade}>{data.grade}</Text>
        </TouchableOpacity>
      ))}
    </View>
  );

  const renderSpecSheetPanelView = (bodyContent, { showLiveChevrons = false } = {}) => (
    <View style={[...specSheetPanelStyle]} data-column="left">
      {renderGradeCards()}
      <View style={{ width: '100%', position: 'relative' }}>
        {renderSpecSheetNavHeader()}

        {showLiveChevrons && specsTab === 'live' && !isLiveFormActive && (
          <>
            <TouchableOpacity
              onPress={() => navigateLiveReport('prev')}
              disabled={!canNavigateLiveReportPrev}
              accessibilityState={{ disabled: !canNavigateLiveReportPrev }}
              style={{
                position: 'absolute',
                left: SPEC_SHEET_PANEL_PADDING,
                top: specNavHeight + 24,
                zIndex: 4,
                width: 32,
                height: 32,
                borderRadius: 16,
                backgroundColor: canNavigateLiveReportPrev ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.08)',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: canNavigateLiveReportPrev ? 1 : 0.4,
                ...(Platform.OS === 'web'
                  ? { cursor: canNavigateLiveReportPrev ? 'pointer' : 'default' }
                  : {}),
              }}
            >
              <Ionicons
                name="chevron-back"
                size={18}
                color={canNavigateLiveReportPrev ? '#fff' : 'rgba(255,255,255,0.45)'}
              />
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => navigateLiveReport('next')}
              disabled={!canNavigateLiveReportNext}
              accessibilityState={{ disabled: !canNavigateLiveReportNext }}
              style={{
                position: 'absolute',
                right: SPEC_SHEET_PANEL_PADDING,
                top: specNavHeight + 24,
                zIndex: 4,
                width: 32,
                height: 32,
                borderRadius: 16,
                backgroundColor: canNavigateLiveReportNext ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.08)',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: canNavigateLiveReportNext ? 1 : 0.4,
                ...(Platform.OS === 'web'
                  ? { cursor: canNavigateLiveReportNext ? 'pointer' : 'default' }
                  : {}),
              }}
            >
              <Ionicons
                name="chevron-forward"
                size={18}
                color={canNavigateLiveReportNext ? '#fff' : 'rgba(255,255,255,0.45)'}
              />
            </TouchableOpacity>
          </>
        )}

        <View style={specSheetBodyStyle}>
          {bodyContent}
        </View>
      </View>
    </View>
  );

  const renderSpecSheetNavHeader = () => {
    const onTechSpecsView = activeSection !== 'links' && activeSection !== 'resources';

    const renderLiveEditTools = () => (
      isLiveFormActive ? (
        <View style={{ flexDirection: 'column', gap: 4, alignItems: 'center' }}>
          <TouchableOpacity
            onPress={openVenueInterestsModal}
            style={specIconBtnStyle(showVenueInterestsModal)}
            accessibilityLabel="Tag venue interests"
          >
            <HexagonIcon
              size={17}
              color={showVenueInterestsModal ? '#ffffff' : '#e5e7eb'}
            />
          </TouchableOpacity>
          <TouchableOpacity
            onPress={openSpecFlagModal}
            style={specIconBtnStyle(showSpecFlagModal)}
            accessibilityLabel="Flag a category note"
          >
            {EDUCATION_TOOLBOX_CATEGORY ? (
              <Image
                source={EDUCATION_TOOLBOX_CATEGORY.source}
                style={{
                  width: getResourceCategoryIconSize(EDUCATION_TOOLBOX_CATEGORY, 18),
                  height: getResourceCategoryIconSize(EDUCATION_TOOLBOX_CATEGORY, 18),
                  ...(EDUCATION_TOOLBOX_CATEGORY.invertInLightMode && !isDarkMode && Platform.OS === 'web'
                    ? { filter: 'invert(1)' }
                    : null),
                }}
                resizeMode="contain"
              />
            ) : null}
          </TouchableOpacity>
          <TouchableOpacity
            onPress={openVenueDocumentUpload}
            style={specIconBtnStyle(showVenueDocumentUploadModal)}
            accessibilityLabel="Upload venue documents for admin review"
          >
            <View style={{ width: 20, height: 20, alignItems: 'center', justifyContent: 'center' }}>
              <Ionicons name="document-outline" size={19} color="#e5e7eb" />
              <Ionicons name="arrow-up" size={10} color="#e5e7eb" style={{ position: 'absolute', top: 5 }} />
            </View>
          </TouchableOpacity>
        </View>
      ) : null
    );

    const renderResourcesNavPill = () => (
      <TouchableOpacity
        onPress={() => toggleMobileDisplay('resources')}
        style={navPillStyle(activeSection === 'resources', 'resources')}
      >
        <Text style={{ color: '#F8F8FA', fontSize: 11, fontFamily: 'VastagoGrotesk', fontWeight: '700' }}>
          Resources
        </Text>
      </TouchableOpacity>
    );

    return (
      <View style={specSheetHeaderBarStyle}>
        <View style={specSheetHeaderLeftColStyle}>
          <TouchableOpacity
            onPress={() => toggleMobileDisplay('links')}
            style={navPillStyle(activeSection === 'links', 'links')}
          >
            <Text style={{ color: '#111111', fontSize: 11, fontFamily: 'VastagoGrotesk', fontWeight: '700' }}>
              Links & Manuals
            </Text>
          </TouchableOpacity>
          {renderResourcesNavPill()}
        </View>

        <View style={specSheetHeaderRightColStyle}>
          {onTechSpecsView ? (
            <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 6 }}>
              <TouchableOpacity onPress={() => goToTechSpecs('vrc')} style={navPillStyle(specsTab === 'vrc', 'vrc')}>
                <Text style={{ color: '#fff', fontSize: 11, fontFamily: 'VastagoGrotesk', fontWeight: '700' }}>VRC</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={handleLiveTabPress}
                style={navPillStyle(specsTab === 'live', 'live')}
              >
                <Text style={{ color: '#fff', fontSize: 11, fontFamily: 'VastagoGrotesk', fontWeight: '700' }}>LIVE</Text>
              </TouchableOpacity>
              <View style={{ gap: 4, alignItems: 'center' }}>
                <TouchableOpacity
                  onPress={openLiveSpecsEditForm}
                  style={specIconBtnStyle(isLiveFormActive)}
                  accessibilityLabel={
                    isLiveFormActive ? 'Cancel LIVE edits' : 'Submit new LIVE specs'
                  }
                >
                  <Ionicons name="pencil" size={18} color={venueAccentColor} />
                </TouchableOpacity>
                {renderLiveEditTools()}
              </View>
            </View>
          ) : (
            <TouchableOpacity
              onPress={() => setActiveSection(null)}
              style={{
                paddingHorizontal: 10,
                paddingVertical: 5,
                borderRadius: 12,
                minHeight: SPEC_ICON_BTN_SIZE,
                justifyContent: 'center',
                alignItems: 'center',
                alignSelf: 'flex-end',
                backgroundColor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
                borderWidth: 1,
                borderColor: isDarkMode ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.12)',
              }}
            >
              <Text style={{ color: specSheetInnerTextColor, fontFamily: 'VastagoGrotesk', fontSize: 11, fontWeight: '700' }}>
                ← Back to Specs
              </Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  };

  const renderResourcesContent = () => {
    const venueState = (venueData?.state || '').trim();
    const specSheetInnerStyle = {
      flex: 1,
      paddingHorizontal: 0,
      paddingBottom: isMobile ? 0 : 20,
      borderRadius: 10,
      alignItems: 'center',
      justifyContent: 'flex-start',
      width: '100%',
    };

    const renderResourceCard = (resource) => {
      const resourceUrl = formatExternalUrl(resource.url);
      const hasImage = Boolean(resource.imageUrl);
      return (
        <TouchableOpacity
          key={resource.id}
          style={[
            venueStyles.manualButton,
            {
              backgroundColor: '#00A575',
              marginBottom: 12,
              width: '100%',
              ...(hasImage ? { padding: 0, overflow: 'hidden' } : {}),
            },
          ]}
          onPress={() => {
            if (resourceUrl) window.open(resourceUrl, '_blank', 'noopener,noreferrer');
          }}
          disabled={!resourceUrl}
        >
          {hasImage ? (
            <ResourceCoverImage
              url={resource.imageUrl}
              width={resource.imageWidth}
              height={resource.imageHeight}
              backgroundColor={isDarkMode ? '#0f1a2e' : '#f5f5f0'}
            />
          ) : null}
          <View style={{ ...(hasImage ? { padding: 14, alignItems: 'center', width: '100%' } : {}) }}>
            <Text style={[venueStyles.manualButtonText, { color: '#ffffff' }]}>{resource.resourceName}</Text>
            {resource.description ? (
              <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 12, marginTop: 6, textAlign: 'center', fontFamily: 'VastagoGrotesk' }}>
                {resource.description}
              </Text>
            ) : null}
            {resourceUrl ? (
              <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, marginTop: 4, textAlign: 'center', fontFamily: 'VastagoGrotesk' }}>
                {resourceUrl.replace(/^https?:\/\//, '')}
              </Text>
            ) : null}
            {resource.scope === 'state' && venueState ? (
              <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, marginTop: 4, textAlign: 'center', fontFamily: 'VastagoGrotesk' }}>
                Shared for all {venueState} venues
              </Text>
            ) : null}
            {resource.scope === 'city' && venueData?.city ? (
              <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, marginTop: 4, textAlign: 'center', fontFamily: 'VastagoGrotesk' }}>
                Shared for all {venueData.city} venues
              </Text>
            ) : null}
          </View>
        </TouchableOpacity>
      );
    };

    const renderCategoryIconRow = () => (
      <View
        style={{
          flexDirection: 'row',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          width: '100%',
          marginBottom: 20,
          gap: 4,
        }}
      >
        {venueResourceCategories.map((category) => {
          const iconSize = getResourceCategoryIconSize(category, 40);
          const categoryCount = approvedResources.filter((item) => item.category === category.id).length;
          return (
            <View
              key={category.id}
              style={{
                width: '18%',
                minWidth: 58,
                alignItems: 'center',
                gap: 6,
              }}
            >
              <View
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: 10,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                }}
              >
                {category.source ? (
                  <Image
                    source={category.source}
                    style={{
                      width: iconSize,
                      height: iconSize,
                      ...(category.invertInLightMode && !isDarkMode && Platform.OS === 'web'
                        ? { filter: 'invert(1)' }
                        : null),
                    }}
                    resizeMode="contain"
                  />
                ) : (
                  <Ionicons name={category.iconName || 'construct-outline'} size={iconSize * 0.7} color={specSheetInnerTextColor} />
                )}
              </View>
              <Text
                style={{
                  fontFamily: 'Playpen Sans',
                  fontSize: 10,
                  fontWeight: '500',
                  color: specSheetInnerTextColor,
                  textAlign: 'center',
                }}
                numberOfLines={1}
              >
                {category.label}
              </Text>
              {categoryCount > 0 ? (
                <Text style={{ fontFamily: 'VastagoGrotesk', fontSize: 10, color: specSheetInnerMutedColor }}>
                  {categoryCount} listing{categoryCount === 1 ? '' : 's'}
                </Text>
              ) : null}
              <TouchableOpacity
                onPress={() => openResourceModalForCategory(category.id)}
                accessibilityLabel={`Add ${category.label} resource`}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                  ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
                }}
              >
                <Ionicons
                  name="add"
                  size={18}
                  color={isDarkMode ? 'rgba(255,255,255,0.75)' : 'rgba(0,0,0,0.55)'}
                />
              </TouchableOpacity>
            </View>
          );
        })}
        <TouchableOpacity
          onPress={openCustomResourceCategoryModal}
          accessibilityRole="button"
          accessibilityLabel="Add a different resource category"
          style={{
            width: '18%',
            minWidth: 58,
            alignItems: 'center',
            gap: 6,
            ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
          }}
        >
          <View
            style={{
              width: 52,
              height: 52,
              borderRadius: 10,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
              borderWidth: 1,
              borderColor: isDarkMode ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.14)',
            }}
          >
            <Ionicons name="add-circle-outline" size={30} color={specSheetInnerTextColor} />
          </View>
          <Text
            style={{
              fontFamily: 'Playpen Sans',
              fontSize: 10,
              fontWeight: '500',
              color: specSheetInnerTextColor,
              textAlign: 'center',
            }}
          >
            Add a different category
          </Text>
        </TouchableOpacity>
      </View>
    );

    const renderGroupedResources = () => (
      <>
        {venueResourceCategories.map((category) => {
          const categoryResources = approvedResources.filter((item) => item.category === category.id);
          if (categoryResources.length === 0) return null;
          return (
            <View key={category.id} style={{ width: '100%', marginBottom: 20 }}>
              {categoryResources.map((resource) => renderResourceCard(resource))}
            </View>
          );
        })}
        {approvedResources.filter((item) => !resourceCategoryCatalog.some((category) => category.id === item.category)).map((resource) => renderResourceCard(resource))}
      </>
    );

    return (
      <View style={specSheetInnerStyle}>
        <View style={{ width: '100%' }}>
          <Text style={venueStyles.manualHeaderText}>Community Resources</Text>
        </View>

        {renderCategoryIconRow()}

        {approvedResources.length > 0 ? (
          <View style={{ width: '100%', marginBottom: 16 }}>
            {renderGroupedResources()}
          </View>
        ) : (
          <View style={{ width: '100%', marginBottom: 20 }}>
            <Text style={[venueStyles.noContentText, { color: specSheetInnerMutedColor, textAlign: 'center', marginBottom: 12 }]}>
              {venueState
                ? `No resource listings for ${venueState} yet. Groups will appear here as they are added.`
                : 'Resource listings will appear here as they are added.'}
            </Text>
          </View>
        )}

        {/* Share-resource CTA replaced by per-category + buttons in renderCategoryIconRow */}
        {/* <View style={{ width: '100%', marginTop: 16 }}>
          <TouchableOpacity
            style={[venueStyles.manualButton, { backgroundColor: '#4e7de0', width: '100%' }]}
            onPress={openResourceModal}
          >
            <Text style={[venueStyles.manualButtonText, { color: '#ffffff', textAlign: 'center', lineHeight: 20 }]}>
              Share resource within region
            </Text>
          </TouchableOpacity>
        </View> */}
      </View>
    );
  };

  const renderLiveSubmissionAttribution = (placement = 'bottom') => {
    if (specsTab !== 'live' || liveSpecsCreateMode || !activeLiveReportAttribution) return null;

    return (
      <View
        style={{
          marginTop: placement === 'top' ? 0 : 8,
          marginBottom: placement === 'top' ? 10 : 0,
          flexDirection: 'row',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          maxWidth: '92%',
          alignSelf: 'center',
        }}
      >
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: 6,
            backgroundColor: isOwnActiveLiveReport
              ? (isDarkMode ? 'rgba(22, 163, 74, 0.22)' : 'rgba(22, 163, 74, 0.15)')
              : (isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'),
            borderColor: isOwnActiveLiveReport
              ? (isDarkMode ? 'rgba(74, 222, 128, 0.55)' : 'rgba(22, 163, 74, 0.45)')
              : (isDarkMode ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.12)'),
            borderWidth: 1,
            borderRadius: 12,
            paddingHorizontal: 10,
            paddingVertical: 5,
          }}
        >
          <Ionicons
            name={isOwnActiveLiveReport ? 'checkmark-circle' : 'person-outline'}
            size={14}
            color={
              isOwnActiveLiveReport
                ? (isDarkMode ? '#86efac' : '#15803d')
                : specSheetInnerMutedColor
            }
          />
          <Text
            style={{
              color: isOwnActiveLiveReport
                ? (isDarkMode ? '#86efac' : '#15803d')
                : specSheetInnerMutedColor,
              fontFamily: 'VastagoGrotesk',
              fontSize: 11,
              fontWeight: '700',
            }}
          >
            {`by ${activeLiveReportAttribution.name}`}
            {activeLiveReportAttribution.dateLabel
              ? ` · ${activeLiveReportAttribution.wasEdited ? 'updated ' : ''}${activeLiveReportAttribution.dateLabel}`
              : ''}
            {isOwnActiveLiveReport ? ' · you' : ''}
          </Text>
        </View>
        {isOwnActiveLiveReport && !isLiveFormActive ? (
          <TouchableOpacity
            onPress={openLiveSpecsEditExisting}
            disabled={liveSpecsSubmitting || liveSpecsDeleting}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 4,
              backgroundColor: SPEC_NAV_BLUE,
              opacity: liveSpecsSubmitting || liveSpecsDeleting ? 0.6 : 1,
              paddingHorizontal: 10,
              paddingVertical: 6,
              borderRadius: 10,
              ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
            }}
            accessibilityLabel="Edit your LIVE submission"
          >
            <Ionicons name="pencil" size={13} color="#fff" />
            <Text style={{ color: '#fff', fontFamily: 'VastagoGrotesk', fontSize: 11, fontWeight: '700' }}>
              Edit
            </Text>
          </TouchableOpacity>
        ) : null}
      </View>
    );
  };

  const renderSpecsContent = () => (
    <>
      <View style={[
        venueStyles.techSpecsContainer,
        getResponsiveValue({
          mobile: venueStyles.techSpecMobileView,
          tablet: {},
          laptop: {},
          desktop: {}
        }),
        {
          marginTop: 0,
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          minHeight: 'auto',
          height: 'auto',
          overflow: 'visible',
          flexGrow: 1,
          flexShrink: 0,
          padding: 0,
        }
      ]}>
        {specsTab === 'live' ? renderLiveSubmissionAttribution('top') : null}
        {specsFields.map((spec, index, array) => {
          const sourceValue =
            specsTab === 'vrc'
              ? venueData?.[spec.field]
              : (isLiveFormActive ? '' : (activeLiveSpecsReport?.[spec.field] ?? ''));
          const liveValue = liveSpecsDraft[spec.field] ?? '';
          return (
            <View key={spec.label} style={{ width: '100%', alignItems: 'center', flexShrink: 0 }}>
              <View style={{ width: '80%', paddingVertical: 15, alignItems: 'flex-start', flexShrink: 0 }}>
                <Text style={{
                  fontSize: getResponsiveValue({ mobile: 18, tablet: 20, laptop: 20, desktop: 20 }),
                  fontWeight: 'bold',
                  color: isLiveFormActive && changedLiveSpecFields.has(spec.field)
                    ? (isDarkMode ? '#86efac' : '#15803d')
                    : specSheetInnerTextColor,
                  marginBottom: 8,
                  fontFamily: 'VastagoGrotesk',
                  width: '100%',
                  textAlign: 'center',
                  flexWrap: 'wrap',
                }}>
                  {isLiveFormActive && changedLiveSpecFields.has(spec.field) ? '*' : ''}{spec.label}:
                </Text>

                {isLiveFormActive ? (
                  // REQ-101: grows with its text, so long LIVE fields never get their own scrollbar.
                  <AutoGrowTextInput
                    value={liveValue}
                    onChangeText={(t) => handleLiveSpecChange(spec.field, t)}
                    placeholder="Unknown"
                    placeholderTextColor={specSheetEmptyValueColor}
                    minHeight={24}
                    style={{
                      fontSize: getResponsiveValue({ mobile: 16, tablet: 18, laptop: 18, desktop: 18 }),
                      color: specSheetInnerTextColor,
                      opacity: 1,
                      fontFamily: 'VastagoGrotesk',
                      width: '100%',
                      textAlign: 'left',
                      borderBottomWidth: StyleSheet.hairlineWidth,
                      borderBottomColor: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)',
                      paddingVertical: 4,
                      ...(isDarkMode ? { backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: 4, paddingHorizontal: 6 } : {}),
                    }}
                  />
                ) : (sourceValue ? (
                  String(sourceValue).split(/\r?\n/).map((line, lineIndex) => {
                    const urlRegex = /(https?:\/\/[^\s]+)/g;
                    const parts = line.split(urlRegex);
                    return (
                      <Text
                        key={lineIndex}
                        style={{
                          fontSize: getResponsiveValue({ mobile: 16, tablet: 18, laptop: 18, desktop: 18 }),
                          color: specSheetValueColor,
                          opacity: 1,
                          fontFamily: 'VastagoGrotesk',
                          flexWrap: 'wrap',
                          width: '100%',
                          textAlign: line.includes('•') || line.includes('▪') ? 'left' : (lineIndex === 0 ? 'center' : 'left'),
                          minHeight: '1em',
                        }}
                      >
                        {parts.map((part, partIndex) => {
                          if (part.match(urlRegex)) {
                            return (
                              <Text
                                key={partIndex}
                                style={{
                                  color: theme.text.accent,
                                  textDecorationLine: 'underline',
                                  ...(Platform.OS === 'web' ? {
                                    cursor: 'pointer',
                                    WebkitUserSelect: 'text',
                                    MozUserSelect: 'text',
                                    msUserSelect: 'text',
                                    userSelect: 'text'
                                  } : {})
                                }}
                                onPress={() => {
                                  if (Platform.OS === 'web') window.open(part, '_blank');
                                  else Linking.openURL(part);
                                }}
                              >
                                {part}
                              </Text>
                            );
                          }
                          return part || ' ';
                        })}
                      </Text>
                    );
                  })
                ) : (
                  <Text style={{
                    fontSize: getResponsiveValue({ mobile: 16, tablet: 18, laptop: 18, desktop: 18 }),
                    color: specSheetEmptyValueColor,
                    fontFamily: 'VastagoGrotesk',
                    flexWrap: 'wrap',
                    width: '100%',
                    textAlign: 'center',
                  }}>
                    {'Unknown'}
                  </Text>
                ))}
              </View>
              {index < array.length - 1 && (
                <View style={{
                  width: '60%',
                  height: 1,
                  backgroundColor: specSheetDividerColor,
                  marginVertical: 5
                }} />
              )}
            </View>
          );
        })}
      </View>

      {specsTab === 'live' && (
        <View style={{ alignItems: 'center', marginTop: 8 }}>
          <Text style={{ color: specSheetInnerMutedColor, fontFamily: 'VastagoGrotesk', fontSize: 12 }}>
            {liveSpecsCreateMode
              ? 'New LIVE submission (based on VRC specs)'
              : liveSpecsEditingExisting
              ? 'Editing your LIVE submission'
              : liveSpecsReports.length > 0
              ? `LIVE ${liveSpecsIndex + 1} / ${liveSpecsReports.length} (community submissions)`
              : 'LIVE 0 / 0 (no community submissions yet)'}
          </Text>
          {renderLiveSubmissionAttribution('bottom')}
          {!canEditLiveSpecs && specsTab === 'live' ? (
            <Text style={{ color: specSheetInnerMutedColor, fontFamily: 'VastagoGrotesk', fontSize: 11, marginTop: 4 }}>
              Sign in to publish community LIVE specs. VRC tab shows admin-vetted specs.
            </Text>
          ) : null}
          {isLiveEditable && (
            <>
              {hasLiveSpecsChanges && (
                <View
                  style={{
                    marginTop: 6,
                    backgroundColor: isDarkMode ? 'rgba(22, 163, 74, 0.22)' : 'rgba(22, 163, 74, 0.15)',
                    borderColor: isDarkMode ? 'rgba(74, 222, 128, 0.55)' : 'rgba(22, 163, 74, 0.45)',
                    borderWidth: 1,
                    borderRadius: 12,
                    paddingHorizontal: 10,
                    paddingVertical: 4,
                  }}
                >
                  <Text style={{ color: isDarkMode ? '#86efac' : '#15803d', fontFamily: 'VastagoGrotesk', fontSize: 11, fontWeight: '700' }}>
                    ● Unsaved LIVE edits
                  </Text>
                </View>
              )}
              <Text style={{ color: specSheetInnerMutedColor, fontFamily: 'VastagoGrotesk', fontSize: 11, marginTop: 2 }}>
                {liveSpecsEditingExisting
                  ? 'Update your published LIVE specs'
                  : 'Starting from VRC specs — publish updates to LIVE immediately'}
              </Text>
              {hasLiveSpecsChanges && (
                <View style={{ flexDirection: 'row', gap: 8, marginTop: 8 }}>
                  <TouchableOpacity
                    onPress={resetLiveSpecsDraft}
                    disabled={liveSpecsSubmitting || liveSpecsDeleting}
                    style={{
                      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
                      opacity: liveSpecsSubmitting || liveSpecsDeleting ? 0.6 : 1,
                      paddingHorizontal: 12,
                      paddingVertical: 8,
                      borderRadius: 8,
                      alignItems: 'center',
                    }}
                  >
                    <Text style={{ color: specSheetInnerTextColor, fontFamily: 'VastagoGrotesk', fontSize: 12, fontWeight: '700' }}>
                      Discard
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={handleLiveSpecsSubmit}
                    disabled={liveSpecsSubmitting || liveSpecsDeleting}
                    style={{
                      backgroundColor: '#16a34a',
                      opacity: liveSpecsSubmitting || liveSpecsDeleting ? 0.6 : 1,
                      paddingHorizontal: 14,
                      paddingVertical: 8,
                      borderRadius: 8,
                      minWidth: 170,
                      alignItems: 'center',
                    }}
                  >
                    {liveSpecsSubmitting ? (
                      <ActivityIndicator size="small" color="#fff" />
                    ) : (
                      <Text style={{ color: '#fff', fontFamily: 'VastagoGrotesk', fontSize: 12, fontWeight: '700' }}>
                        {liveSpecsEditingExisting ? 'Save LIVE changes' : 'Publish LIVE specs'}
                      </Text>
                    )}
                  </TouchableOpacity>
                </View>
              )}
            </>
          )}
          {canEditLiveSpecs && !isLiveFormActive ? (
            <TouchableOpacity
              onPress={openLiveSpecsCreateForm}
              style={{ marginTop: 8, paddingHorizontal: 10, paddingVertical: 6 }}
            >
              <Text style={{ color: venueAccentColor, fontFamily: 'VastagoGrotesk', fontSize: 11, fontWeight: '700' }}>
                + New LIVE submission
              </Text>
            </TouchableOpacity>
          ) : null}
          {canDeleteActiveLiveReport && !isLiveFormActive && (
            <TouchableOpacity
              onPress={handleLiveSpecsDelete}
              disabled={liveSpecsSubmitting || liveSpecsDeleting}
              style={{
                marginTop: 10,
                backgroundColor: isDarkMode ? 'rgba(220, 38, 38, 0.22)' : 'rgba(220, 38, 38, 0.12)',
                borderColor: isDarkMode ? 'rgba(248, 113, 113, 0.55)' : 'rgba(220, 38, 38, 0.45)',
                borderWidth: 1,
                opacity: liveSpecsSubmitting || liveSpecsDeleting ? 0.6 : 1,
                paddingHorizontal: 14,
                paddingVertical: 8,
                borderRadius: 8,
                minWidth: 170,
                alignItems: 'center',
              }}
            >
              {liveSpecsDeleting ? (
                <ActivityIndicator size="small" color="#dc2626" />
              ) : (
                <Text style={{ color: '#dc2626', fontFamily: 'VastagoGrotesk', fontSize: 12, fontWeight: '700' }}>
                  Delete My LIVE Report
                </Text>
              )}
            </TouchableOpacity>
          )}
        </View>
      )}

      <View style={[venueStyles.smallerVotingContainer, {
        marginTop: 20,
        marginBottom: 40,
      }]}>
        <Text style={[venueStyles.smallerVotingText, { color: '#3d80e6' }]}>Is this correct?</Text>

        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <TouchableOpacity
            onPress={() => handleVote('specs', 'up')}
            style={[venueStyles.voteButton, {
              marginRight: 10,
              opacity: userSpecsVote === 'up' ? 1 : 0.7,
              flexDirection: 'row',
              alignItems: 'center'
            }]}
          >
            <Ionicons
              name={userSpecsVote === 'up' ? 'thumbs-up' : 'thumbs-up-outline'}
              size={16}
              color={venueAccentColor}
            />
            <Text style={[venueStyles.voteCount, { fontSize: 10, color: venueAccentColor }]}>{specsVotes.up}</Text>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => handleVote('specs', 'down')}
            style={[venueStyles.voteButton, {
              opacity: userSpecsVote === 'down' ? 1 : 0.7,
              flexDirection: 'row',
              alignItems: 'center'
            }]}
          >
            <Ionicons
              name={userSpecsVote === 'down' ? 'thumbs-down' : 'thumbs-down-outline'}
              size={16}
              color={venueAccentColor}
            />
            <Text style={[venueStyles.voteCount, { fontSize: 10, color: venueAccentColor }]}>{specsVotes.down}</Text>
          </TouchableOpacity>
        </View>
      </View>
    </>
  );

  const renderSpecs = () => {
    if (showCategories) {
      return (
        <View style={[...specSheetPanelStyle]} data-column="left">
          {renderGradeCards()}
          <View>
            <TouchableOpacity
              style={venueStyles.backButton}
              onPress={() => setShowCategories(false)}
            >
              <Text style={venueStyles.backButtonText}>← Back to Specs</Text>
            </TouchableOpacity>
            <View style={venueStyles.categoryContainer}>
              {categoryRows.map((row, rowIndex) => (
                <View key={rowIndex} style={venueStyles.categoryRow}>
                  {row.map((category, index) => (
                    <CategoryButton
                      key={index}
                      text={category}
                      onPress={() => {
                        alert(`${category} reports coming soon...`);
                      }}
                    />
                  ))}
                </View>
              ))}
            </View>
          </View>
        </View>
      );
    }

    return renderSpecSheetPanelView(renderSpecsContent(), { showLiveChevrons: true });
  };

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'report':
        return renderSpecs();

      case 'discussionsAndUploads':
        return (
          <View id="venue-community-section" style={venueStyles.discussionsAndUploadsContainer}>
            <View style={[venueStyles.discussionAndUploadsBox, isDarkMode && { backgroundColor: theme.background.tertiary, borderColor: theme.ui.borderStrong }]}>
              {activeDiscussionTab === 'discussions' ? (
                <DiscussionBoard
                  venueId={venueId}
                  discussions={discussionData?.discussions || []}
                  reports={discussionData?.reports || []}
                  textStyle={[venueStyles.discussionText, isDarkMode && { color: theme.text.primary }]}
                  inputStyle={[venueStyles.discussionInput, isDarkMode && { color: theme.input.text, backgroundColor: theme.input.background, borderColor: theme.input.border }]}
                  placeholderStyle={[venueStyles.discussionPlaceholder, isDarkMode && { color: theme.input.placeholder }]}
                  containerStyle={venueStyles.discussionSection}
                />
              ) : activeDiscussionTab === 'uploads' ? renderMedia() : renderRoomEqTab()}
            </View>
            <View style={venueStyles.discussionsAndUploadsTabs}>
              <TouchableOpacity
                style={[venueStyles.discussionsAndUploadsTab, isDarkMode && { backgroundColor: theme.background.tertiary }]}
                onPress={() => setActiveDiscussionTab('discussions')}
              >
                <Text style={[venueStyles.discussionsAndUploadsTabText, { fontSize: activeDiscussionTab === 'discussions' ? 20 : 14 }, isDarkMode && { color: theme.text.primary }]}>NOTES</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[venueStyles.discussionsAndUploadsTab, isDarkMode && { backgroundColor: theme.background.tertiary }]}
                onPress={() => setActiveDiscussionTab('uploads')}
              >
                <Text style={[venueStyles.discussionsAndUploadsTabText, { fontSize: activeDiscussionTab === 'uploads' ? 20 : 14 }, isDarkMode && { color: theme.text.primary }]}>UPLOADS</Text>
              </TouchableOpacity>
              {/* <TouchableOpacity
                style={[venueStyles.discussionsAndUploadsTab, isDarkMode && { backgroundColor: theme.background.tertiary }]}
                onPress={() => setActiveDiscussionTab('eq')}
              >
                <Text style={[venueStyles.discussionsAndUploadsTabText, { fontSize: activeDiscussionTab === 'eq' ? 20 : 14 }, isDarkMode && { color: theme.text.primary }]}>EQ</Text>
              </TouchableOpacity> */}
            </View>
          </View>
        );

      case 'tour':
        return (
          <View style={venueStyles.tourSection}>
            <Text style={[venueStyles.comingSoonText, isDarkMode && { color: theme.text.primary }]}>Tour planning coming soon...</Text>
          </View>
        );

      case 'links':
        return renderSpecSheetPanelView(renderManualsButtons());

      case 'resources':
        return renderSpecSheetPanelView(renderResourcesContent());


      default:
        return renderSpecs();

    }
  };

  const renderSpecSheets = () => {

    // If admin is editing spec sheets, show the editor (checked first so a venue that
    // only has the legacy specSheetLink can still be edited; REQ-101).
    if (isAdmin && isEditingSpecSheets) {
      return renderSpecSheetsEditor();
    }

    if (!venueData?.specSheets && venueData?.specSheetLink) {
      return (
        <View style={{ width: '100%' }}>
          {isAdmin && (
            <View style={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
              <TouchableOpacity
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  padding: 8,
                  borderRadius: 5,
                  alignItems: 'center',
                }}
                onPress={handleStartEditingSpecSheets}
              >
                <Text style={{ color: '#3D7FE6', fontSize: 12, fontWeight: 'bold' }}>EDIT SPEC SHEETS</Text>
              </TouchableOpacity>
            </View>
          )}
        <TouchableOpacity
          style={[venueStyles.manualButton, { marginBottom: isMobile ? 40 : 20, ...outlinedLink(SPEC_LINK_BUTTON_BG) }]}
          onPress={() => window.open(venueData.specSheetLink, '_blank')}
        >
          <Text style={[venueStyles.manualButtonText, { color: LINK_BUTTON_TEXT }]}>{'Venue\'s Spec Link 🔗'}</Text>
        </TouchableOpacity>
        </View>
      )
    }

    if (venueData?.specSheets && venueData?.specSheets.length > 0) {
      return (
        <View style={{ width: '100%' }}>
          {isAdmin && (
            <View style={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
              <TouchableOpacity
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  padding: 8,
                  borderRadius: 5,
                  alignItems: 'center',
                }}
                onPress={handleStartEditingSpecSheets}
              >
                <Text style={{ color: '#3D7FE6', fontSize: 12, fontWeight: 'bold' }}>EDIT SPEC SHEETS</Text>
              </TouchableOpacity>
            </View>
          )}
          <View style={venueStyles.specSheetsContainer}>
            {venueData?.specSheets.map((spec, index) => (
              <TouchableOpacity
                key={index}
                style={[venueStyles.horizontalManualButton, { ...outlinedLink(SPEC_LINK_BUTTON_BG), width: 'auto' }]}
                onPress={() => {
                  if (spec.url) {
                    window.open(spec.url, '_blank');
                  }
                }}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Text style={[venueStyles.manualButtonText, { color: LINK_BUTTON_TEXT }]}>{spec.name || 'Spec Sheet'}</Text>
                  {spec.specType === 'file' && (
                    <Text style={{ color: LINK_BUTTON_TEXT, fontSize: 12, marginLeft: 10 }}>
                      📄
                    </Text>
                  )}
                  {spec.specType === 'url' && (
                    <Text style={{ color: LINK_BUTTON_TEXT, fontSize: 12, marginLeft: 10 }}>🔗</Text>
                  )}
                </View>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      )
    }
    else {
      return (
        <View style={{ width: '100%', marginBottom: 40 }}>
          <Text style={[venueStyles.noContentText, { color: specSheetInnerMutedColor }]}>No spec sheets available</Text>
          {isAdmin && (
            <TouchableOpacity
              style={[venueStyles.manualButton, { backgroundColor: '#2980b9', marginTop: 10 }]}
              onPress={handleStartEditingSpecSheets}
            >
              <Text style={[venueStyles.manualButtonText, { color: '#ffffff' }]}>Add Spec Sheets (Admin)</Text>
            </TouchableOpacity>
          )}
        </View>
      )
    }
  }

  const renderManualsButtons = () => {
    return (
      <View style={{
        flex: 1,
        paddingHorizontal: 0,
        paddingBottom: isMobile ? 0 : 20,
        borderRadius: 10,
        alignItems: 'center',
        justifyContent: 'flex-start',
        width: '100%',
      }}>
        {/* Show venue's spec sheet if available */}

        <View style={{ width: '100%' }}>
          <Text style={venueStyles.manualHeaderText}>Spec Sheets</Text>
        </View>
        {renderSpecSheets()}
        {/* {venueData?.specSheetLink && (
          <TouchableOpacity
            style={[venueStyles.manualButton, { marginBottom: isMobile ? 40 : 20, backgroundColor: '#00A575' }]}
            onPress={() => window.open(venueData.specSheetLink, '_blank')}
          >
            <Text style={[venueStyles.manualButtonText, { color: '#ffffff' }]}>{'Venue\'s Spec Link'}</Text>
          </TouchableOpacity>
        )} */}

        {/* Show any tech specs documents */}
        {venueData?.techSpecs?.map((spec, index) => (
          <TouchableOpacity
            key={index}
            style={[venueStyles.manualButton, { marginBottom: 20, ...outlinedLink(SPEC_LINK_BUTTON_BG) }]}
            onPress={() => window.open(spec.url, '_blank')}
          >
            <Text style={[venueStyles.manualButtonText, { color: LINK_BUTTON_TEXT }]}>{spec.name || 'Tech Specs'}</Text>
          </TouchableOpacity>
        ))}

        {/* Show relevant equipment manuals */}
        <View style={{ width: '100%' }}>
          <View style={{ width: '100%' }}>
            <Text style={venueStyles.manualHeaderText}>Manual Links</Text>
          </View>
          {renderManualSection(venueData?.manualsLinks || findRelevantManuals(venueData))}

          <View style={{ width: '100%', marginTop: 16 }}>
            <Text style={venueStyles.manualHeaderText}>Uploads</Text>
          </View>
          {/* REQ-101: the document upload was only reachable from the LIVE pencil's edit
              tools, so it looked gone. It now also sits here, next to the approved uploads. */}
          <TouchableOpacity
            onPress={openVenueDocumentUpload}
            style={[venueStyles.manualButton, { marginBottom: 10, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, ...outlinedLink(SPEC_LINK_BUTTON_BG) }]}
            accessibilityRole="button"
            accessibilityLabel="Upload venue documents for admin review"
          >
            <Ionicons name="cloud-upload-outline" size={18} color={LINK_BUTTON_TEXT} />
            <Text style={[venueStyles.manualButtonText, { color: LINK_BUTTON_TEXT }]}>Upload spec sheets & documents</Text>
          </TouchableOpacity>
          {(venueData?.uploads || []).length ? (
            venueData.uploads.map((upload, index) => (
              <TouchableOpacity
                key={upload.submissionId ? `${upload.submissionId}-${index}` : `${upload.url}-${index}`}
                style={[venueStyles.manualButton, { marginBottom: 10, flexDirection: 'row', alignItems: 'center', gap: 8, ...outlinedLink(MANUAL_LINK_BUTTON_BG) }]}
                onPress={() => {
                  if (upload.url && typeof window !== 'undefined') window.open(upload.url, '_blank');
                }}
              >
                <Ionicons name="document-text-outline" size={18} color={LINK_BUTTON_TEXT} />
                <Text style={[venueStyles.manualButtonText, { color: LINK_BUTTON_TEXT }]}>{upload.name || upload.fileName || 'Venue document'}</Text>
              </TouchableOpacity>
            ))
          ) : (
            <Text style={[venueStyles.noContentText, { color: specSheetInnerMutedColor }]}>No approved uploads yet</Text>
          )}

          {/* Links voting section */}
          <View style={[venueStyles.smallerVotingContainer, {
            marginTop: 20,
            marginBottom: 10,
          }]}>
            <Text style={[venueStyles.smallerVotingText, { color: '#3d80e6' }]}>Is this correct?</Text>

            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <TouchableOpacity
                onPress={() => handleVote('links', 'up')}
                style={[venueStyles.voteButton, {
                  marginRight: 10,
                  opacity: userLinksVote === 'up' ? 1 : 0.7,
                  flexDirection: 'row',
                  alignItems: 'center'
                }]}
              >
                <Ionicons
                  name={userLinksVote === 'up' ? 'thumbs-up' : 'thumbs-up-outline'}
                  size={16}
                  color={venueAccentColor}
                />
                <Text style={[venueStyles.voteCount, { fontSize: 10, color: venueAccentColor }]}>{linksVotes.up}</Text>
              </TouchableOpacity>

              <TouchableOpacity
                onPress={() => handleVote('links', 'down')}
                style={[venueStyles.voteButton, {
                  opacity: userLinksVote === 'down' ? 1 : 0.7,
                  flexDirection: 'row',
                  alignItems: 'center'
                }]}
              >
                <Ionicons
                  name={userLinksVote === 'down' ? 'thumbs-down' : 'thumbs-down-outline'}
                  size={16}
                  color={venueAccentColor}
                />
                <Text style={[venueStyles.voteCount, { fontSize: 10, color: venueAccentColor }]}>{linksVotes.down}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </View>
    )
  }

  const renderMedia = () => {
    const discussionPhotos = messages.filter(msg => msg.photoURL);

    if (discussionPhotos.length === 0) {
      return (
        <View style={venueStyles.emptyStateContainer}>
          <Text style={venueStyles.emptyStateText}>No user photos available</Text>
        </View>
      );
    }

    return (
      <>
        <ScrollView style={venueStyles.photoCollageContainer}>
          <View style={venueStyles.photoCollageGrid}>
            {discussionPhotos.map((msg, index) => (
              <TouchableOpacity
                key={msg.id}
                style={venueStyles.mobilePhotoCollageItem}
                onPress={() => handlePhotoClick(msg, index)}
              >
                <Image
                  source={{ uri: msg.photoURL }}
                  style={venueStyles.mobilePhotoCollageImage}
                />
                <Text style={[venueStyles.mobilePhotoCollageTimestamp, { width: '100%', textAlign: 'left' }]}>{new Date(msg.timestamp.toDate()).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })} </Text>
                <View style={venueStyles.mobilePhotoCollageUsernameContainer}>
                  <Text style={venueStyles.mobilePhotoCollageTimestamp}>{new Date(msg.timestamp.toDate()).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} </Text>
                  <Text style={venueStyles.mobilePhotoCollageUsername}>{msg.username} </Text>
                </View>
              </TouchableOpacity>
              //   )
              // : (
              //   <TouchableOpacity
              //     key={msg.id}
              //     style={venueStyles.photoCollageItem}
              //     onPress={() => handlePhotoClick(msg, index)}
              //   >
              //     <Image
              //       source={{ uri: msg.photoURL }}
              //       style={venueStyles.photoCollageImage}
              //     />
              //     <Text style={venueStyles.photoCollageUsername}>{msg.username}</Text>
              //   </TouchableOpacity>
            ))}
          </View>
        </ScrollView>
        {renderPhotoModal()}
      </>
    );
  };

  const renderRoomEqTab = () => {
    const submissions = publicRoomEqs.filter((e) => e.type === 'roomEqSubmission');
    const slides = [];
    publicRoomEqs.forEach((entry) => {
      if (entry.type === 'roomEqSubmission') return;
      if (entry.type === 'rf') {
        slides.push({ type: 'rf', entry });
      } else {
        (entry.photos || []).forEach((p, i) => slides.push({ type: 'eq', entry, photo: p, photoIndex: i }));
      }
    });
    const hasSubmission = submissions.length > 0;
    const hasSlides = slides.length > 0;
    if (!hasSubmission && !hasSlides) {
      return (
        <View style={venueStyles.emptyStateContainer}>
          <Text style={venueStyles.emptyStateText}>No public Room EQ setups yet</Text>
        </View>
      );
    }
    return (
      <>
        <ScrollView style={venueStyles.photoCollageContainer}>
          <View style={venueStyles.photoCollageGrid}>
            {hasSubmission ? (
              <TouchableOpacity
                key="submission"
                style={venueStyles.mobilePhotoCollageItem}
                onPress={() => {
                  const subs = publicRoomEqs.filter((e) => e.type === 'roomEqSubmission');
                  setRoomEqViewerSubmissions(subs);
                  setRoomEqViewerSubmissionIndex(0);
                  const first = subs[0];
                  setRoomEqViewerActiveTab(first.eq ? 'EQ' : first.rf ? 'RF' : 'ADD');
                  setRoomEqViewerMode('submission');
                  setRoomEqViewerVisible(true);
                }}
              >
                <View style={[venueStyles.mobilePhotoCollageImage, { backgroundColor: '#e8f4f0', justifyContent: 'center', alignItems: 'center' }]}>
                  <Ionicons name="layers" size={40} color="#208c6a" />
                </View>
                <Text style={[venueStyles.mobilePhotoCollageTimestamp, { width: '100%', textAlign: 'left' }]} numberOfLines={2}>Room EQ (EQ + RF + tabs)</Text>
              </TouchableOpacity>
                ) : null}
            {slides.map((slide, index) => (
              <TouchableOpacity
                key={`slide-${index}`}
                style={venueStyles.mobilePhotoCollageItem}
                onPress={() => {
                  setRoomEqViewerSlides(slides);
                  setRoomEqViewerIndex(index);
                  setRoomEqViewerMode('slides');
                  setRoomEqViewerVisible(true);
                }}
              >
                {slide.type === 'rf' ? (
                  <>
                    <View style={[venueStyles.mobilePhotoCollageImage, { backgroundColor: '#f0f0f0', justifyContent: 'center', alignItems: 'center' }]}>
                      <Ionicons name="radio" size={40} color="#666" />
                    </View>
                    <Text style={[venueStyles.mobilePhotoCollageTimestamp, { width: '100%', textAlign: 'left' }]} numberOfLines={2}>{slide.entry.title || 'RF'}</Text>
                  </>
                ) : (
                  <>
                    <Image source={{ uri: slide.photo?.url }} style={venueStyles.mobilePhotoCollageImage} resizeMode="cover" />
                    {(slide.photo?.description || slide.entry?.title) ? (
                      <Text style={[venueStyles.mobilePhotoCollageTimestamp, { width: '100%', textAlign: 'left' }]} numberOfLines={2}>{slide.photo?.description || slide.entry?.title}</Text>
                    ) : null}
                  </>
                )}
              </TouchableOpacity>
            ))}
          </View>
        </ScrollView>
      </>
    );
  }

  const renderCurrentView = () => {
    switch (currentView) {
      case 'map':
        return (
          <View style={venueStyles.mediaContainer}>
            {venueData?.mapEmbedLink ? (
              <div
                style={venueStyles.mapContainer}
                dangerouslySetInnerHTML={{
                  __html: venueData.mapEmbedLink
                    .replace(/&lt;iframe/g, '<iframe')
                    .replace(/&lt;\/iframe&gt;/g, '</iframe>')
                    .replace(/&amp;/g, '&')
                    .replace(/<iframe(.*?)>/, '<iframe$1 style="width:100% !important; height:100% !important; border:0 !important; position:absolute !important; top:0 !important; left:0 !important; right:0 !important; bottom:0 !important;">')
                }}
              />
            ) : (
              <Text style={venueStyles.noContentText}>No map available</Text>
            )}
          </View>
        );

      case 'media':
        return renderMedia();

      case 'manuals':
        return (
          <View style={venueStyles.mediaContainer}>
            {renderManualsButtons()}
          </View>
        );

      default:
        return null;
    }
  };

  /** Approved grade reports only — tech-specs-only submissions are excluded from public counts. */
  const publicVenueReports = useMemo(
    () => approvedReports.filter((r) => {
      if (r?.isTechSpecsOnly === true) return false;
      const rawLower = String(r?.userType || '').toLowerCase();
      return rawLower !== 'technical';
    }),
    [approvedReports]
  );

  const renderOverallGrade = () => {
    const approved = venueData?.approved || {};

    return (
      <View style={[venueStyles.overallGradeCard, { flex: 1, width: '100%', padding: 0 }]}>
        <TouchableOpacity
          style={getSaveButtonStyle()}
          onPress={handleSaveVenue}
          onMouseEnter={() => setIsSaveHovered(true)}
          onMouseLeave={() => setIsSaveHovered(false)}
        >
          <Image
            source={isSaved ? require('../../assets/imgs/saved_alt.png') : require('../../assets/imgs/save_alt.png')}
            style={venueStyles.saveButtonIcon}
          />
        </TouchableOpacity>
        <ReportCardChalkboard
          grade={getLetterGrade(approved.overallGrade)}
          reportCount={publicVenueReports.length}
          onOpenReports={() => setShowReportsModal(true)}
          onSubmitReport={handleSubmitReport}
          style={{ flex: 1, height: '100%', minHeight: 320, aspectRatio: undefined }}
        />
      </View>
    );
  };

  const renderMapCard = () => {
    return (
      <View style={venueStyles.mapCard}>
        {Platform.OS === 'web' && MAPBOX_ACCESS_TOKEN && venueMapCenter ? (
          <View style={venueStyles.mapWrapper}>
            <div
              ref={(el) => {
                venueMapContainerRef.current = el;
                setVenueMapContainerReady(!!el);
              }}
              style={{
                width: '100%',
                height: '100%',
                minHeight: 320,
                borderRadius: 10,
                position: 'relative',
              }}
              className="venue-mapbox-container"
            />
            <TouchableOpacity
              style={venueStyles.mapFullscreenButton}
              onPress={() => setMapFullscreen(true)}
              activeOpacity={0.8}
            >
              <Ionicons name="expand" size={24} color="#1d1d1d" />
            </TouchableOpacity>
            <TouchableOpacity
              style={venueStyles.mapAddLocationButton}
              onPress={openAddLocationPanel}
              activeOpacity={0.8}
            >
              <Text style={venueStyles.mapAddLocationButtonText}>Add a Location</Text>
            </TouchableOpacity>
          </View>
        ) : venueData?.mapEmbedLink ? (
          <div
            style={{
              width: '100%',
              height: '100%',
              minHeight: 320,
              borderRadius: 10,
              overflow: 'hidden',
              position: 'relative',
            }}
            dangerouslySetInnerHTML={{
              __html: venueData.mapEmbedLink
                .replace('zoom=14', 'zoom=12')
                .replace(/&lt;iframe/g, '<iframe')
                .replace(/&lt;\/iframe&gt;/g, '</iframe>')
                .replace(/&amp;/g, '&')
                .replace(/width="\d+"/, 'width="100%"')
                .replace(/height="\d+"/, 'height="100%"')
                .replace(/style="[^"]*"/, 'style="width:100% !important;height:100% !important;min-height:320px !important;border:0 !important;position:absolute !important;top:0 !important;left:0 !important;border-radius:10px !important;"'),
            }}
          />
        ) : (
          <Text style={venueStyles.noContentText}>No map available</Text>
        )}
      </View>
    );
  };

  const getVenueTextStyle = (label) => {
    return getStyle('venueScreenStyles', label, isMobile ? 'mobile' : 'web');
  };

  // Add this useEffect to handle height synchronization
  useEffect(() => {
    if (Platform.OS === 'web') {
      // Create a ResizeObserver to watch the left column height
      const observer = new ResizeObserver(entries => {
        for (let entry of entries) {
          // Set the height as a CSS variable
          document.documentElement.style.setProperty(
            '--left-column-height',
            `${entry.contentRect.height}px`
          );
        }
      });

      // Get the left column element
      const leftColumn = document.querySelector('[data-column="left"]');
      if (leftColumn) {
        observer.observe(leftColumn);
      }

      return () => observer.disconnect();
    }
  }, []);

  if (loading) {
    return (
      <View style={[globalStyles.container, { backgroundColor: venuePalette?.pageBackground ?? '#3D7FE6' }]}>
        <Text style={globalStyles.loadingText}>Loading...</Text>
      </View>
    );
  }

  // If showing report form, render the form
  // if (showReportForm && venueData) {
  //   return (
  //     <View style={globalStyles.container}>
  //       <BackgroundVideo />
  //       <View style={globalStyles.overlay}>
  //         <VenueReportForm
  //           venue={{
  //             ...venueData,
  //             id: venueId // Explicitly include the ID from route params
  //           }}
  //           onSubmit={handleFormSubmit}
  //           onCancel={handleFormCancel}
  //           isTechSpecsOnly={route.params?.isTechSpecsOnly}
  //         />
  //       </View>
  //     </View>
  //   );
  // }

  const renderReportFormModal = () => {
    return (
      <CustomModal onClose={handleFormCancel}>
        <VenueReportForm
          venue={{
            ...venueData,
            id: venueId // Explicitly include the ID from route params
          }}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          isTechSpecsOnly={route.params?.isTechSpecsOnly}
          initialUserType={route.params?.reportRole || ''}
        />
      </CustomModal>
    );
  };

  const renderResourceSubmissionModal = () => {
    const venueState = (venueData?.state || '').trim();
    const venueCity = (venueData?.city || '').trim();
    const customCategoryId = resourceCategoryIdFromLabel(resourceCustomCategory);
    const selectedCategory = resourceCategoryCatalog.find((category) => category.id === resourceForm.category)
      || (resourceCustomCategory
        ? { id: resourceForm.category, label: resourceCustomCategory, iconName: 'construct-outline' }
        : getResourceCategoryById(resourceForm.category));
    const modalColors = isDarkMode
      ? {
          bg: SPEC_SHEET_PANEL_DARK_BG,
          text: '#f0f4ff',
          muted: '#a8b8d8',
          inputBg: '#1e3050',
          inputText: '#f0f4ff',
          placeholder: '#8fa3c4',
          inactiveBtn: '#243a5c',
          inactiveBtnText: '#d8e2f8',
        }
      : {
          bg: '#E9E9E9',
          text: '#1a2332',
          muted: '#4a5568',
          inputBg: '#ffffff',
          inputText: '#1a2332',
          placeholder: '#5a6478',
          inactiveBtn: '#d8dce3',
          inactiveBtnText: '#1a2332',
        };

    return (
      <Modal
        visible={resourceModalStep !== null}
        transparent
        animationType="fade"
        onRequestClose={closeResourceModal}
      >
        <View style={{
          flex: 1,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 20,
        }}>
          <View style={{
            backgroundColor: modalColors.bg,
            borderRadius: 12,
            width: '100%',
            maxWidth: 520,
            maxHeight: Platform.OS === 'web' ? '90vh' : '92%',
            overflow: 'hidden',
          }}>
            {resourceModalStep === 'category' ? (
              <View style={{ padding: 20 }}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <Text style={{ color: modalColors.text, fontSize: 18, fontWeight: 'bold', fontFamily: 'VastagoGrotesk', flex: 1, paddingRight: 12 }}>
                    Choose a category for this resource
                  </Text>
                  <TouchableOpacity onPress={closeResourceModal}>
                    <Text style={{ color: modalColors.text, fontSize: 18, fontWeight: 'bold' }}>✕</Text>
                  </TouchableOpacity>
                </View>

                <View style={{
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  gap: 8,
                }}>
                  {venueResourceCategories.map((category) => {
                    const iconSize = getResourceCategoryIconSize(category, 56);
                    return (
                    <TouchableOpacity
                      key={category.id}
                      onPress={() => {
                        setResourceForm((prev) => ({ ...prev, category: category.id }));
                        setResourceCustomCategory('');
                        setResourceModalStep('form');
                      }}
                      accessibilityLabel={category.label}
                      style={{
                        width: '18%',
                        minWidth: 64,
                        minHeight: 82,
                        padding: 5,
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 12,
                        backgroundColor: modalColors.inputBg,
                        borderWidth: 1,
                        borderColor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)',
                      }}
                    >
                      {category.source ? (
                        <Image
                          source={category.source}
                          style={{
                            width: iconSize,
                            height: iconSize,
                            ...(category.invertInLightMode && !isDarkMode && Platform.OS === 'web'
                              ? { filter: 'invert(1)' }
                              : null),
                          }}
                          resizeMode="contain"
                        />
                      ) : (
                        <Ionicons name={category.iconName || 'construct-outline'} size={28} color={modalColors.text} />
                      )}
                      <Text style={{ color: modalColors.text, fontFamily: 'VastagoGrotesk', fontSize: 10, textAlign: 'center' }}>
                        {category.label}
                      </Text>
                    </TouchableOpacity>
                  );})}
                  <TouchableOpacity
                    onPress={() => setResourceModalStep('custom-category')}
                    accessibilityRole="button"
                    accessibilityLabel="Or add a different category"
                    style={{
                      width: '18%',
                      minWidth: 64,
                      minHeight: 82,
                      padding: 5,
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 12,
                      backgroundColor: modalColors.inputBg,
                      borderWidth: 1,
                      borderColor: isDarkMode ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.14)',
                    }}
                  >
                    <Ionicons name="add-circle-outline" size={28} color={modalColors.text} />
                    <Text style={{ color: modalColors.text, fontFamily: 'VastagoGrotesk', fontSize: 10, fontWeight: '700', textAlign: 'center' }}>
                      Add a different category
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>
            ) : resourceModalStep === 'custom-category' ? (
              <View style={{ padding: 20 }}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <Text style={{ color: modalColors.text, fontSize: 18, fontWeight: 'bold', fontFamily: 'VastagoGrotesk', flex: 1, paddingRight: 12 }}>
                    Add a different category
                  </Text>
                  <TouchableOpacity onPress={closeResourceModal}>
                    <Text style={{ color: modalColors.text, fontSize: 18, fontWeight: 'bold' }}>✕</Text>
                  </TouchableOpacity>
                </View>
                <Text style={{ color: modalColors.muted, fontSize: 13, marginBottom: 12, fontFamily: 'VastagoGrotesk' }}>
                  Enter a category name. Admins will review it with your resource submission.
                </Text>
                <TextInput
                  value={resourceCustomCategory}
                  onChangeText={setResourceCustomCategory}
                  placeholder="Category name"
                  placeholderTextColor={modalColors.placeholder}
                  maxLength={48}
                  autoFocus
                  style={{
                    backgroundColor: modalColors.inputBg,
                    borderRadius: 8,
                    padding: 12,
                    color: modalColors.inputText,
                    borderWidth: 1,
                    borderColor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)',
                    fontFamily: 'VastagoGrotesk',
                  }}
                />
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 18 }}>
                  <TouchableOpacity
                    onPress={() => setResourceModalStep('category')}
                    style={{ paddingVertical: 10, paddingHorizontal: 14, borderRadius: 8 }}
                  >
                    <Text style={{ color: modalColors.text, fontFamily: 'VastagoGrotesk', fontWeight: '700' }}>Back</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() => {
                      const label = resourceCustomCategory.trim().replace(/\s+/g, ' ');
                      const categoryId = resourceCategoryIdFromLabel(label);
                      if (!categoryId) return;
                      const existing = venueResourceCategories.find(
                        (category) => category.id === categoryId || category.label.toLowerCase() === label.toLowerCase()
                      );
                      setResourceForm((prev) => ({ ...prev, category: existing?.id || categoryId }));
                      setResourceCustomCategory(existing ? '' : label);
                      setResourceModalStep('form');
                    }}
                    disabled={
                      !customCategoryId || customCategoryId === MERCH_RESOURCE_CATEGORY.id
                    }
                    style={{
                      paddingVertical: 10,
                      paddingHorizontal: 14,
                      borderRadius: 8,
                      backgroundColor:
                        customCategoryId && customCategoryId !== MERCH_RESOURCE_CATEGORY.id
                          ? '#4e7de0'
                          : modalColors.inactiveBtn,
                      opacity:
                        customCategoryId && customCategoryId !== MERCH_RESOURCE_CATEGORY.id
                          ? 1
                          : 0.55,
                    }}
                  >
                    <Text style={{
                      color:
                        customCategoryId && customCategoryId !== MERCH_RESOURCE_CATEGORY.id
                          ? '#fff'
                          : modalColors.inactiveBtnText,
                      fontFamily: 'VastagoGrotesk',
                      fontWeight: '700',
                    }}>
                      Continue
                    </Text>
                  </TouchableOpacity>
                </View>
                {customCategoryId === MERCH_RESOURCE_CATEGORY.id ? (
                  <Text style={{ color: modalColors.muted, fontSize: 12, marginTop: 8, textAlign: 'right', fontFamily: 'VastagoGrotesk' }}>
                    Merch is not a venue resource category.
                  </Text>
                ) : null}
              </View>
            ) : (
              <ScrollView
                style={{ flexGrow: 0 }}
                contentContainerStyle={{ padding: 20, paddingBottom: 24 }}
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator
              >
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <Text style={{ color: modalColors.text, fontSize: 18, fontWeight: 'bold', fontFamily: 'VastagoGrotesk' }}>
                Share a Resource
              </Text>
              <TouchableOpacity onPress={closeResourceModal}>
                <Text style={{ color: modalColors.text, fontSize: 18, fontWeight: 'bold' }}>✕</Text>
              </TouchableOpacity>
            </View>

            {selectedCategory ? (
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  marginBottom: 16,
                  paddingVertical: 10,
                  paddingHorizontal: 12,
                  borderRadius: 10,
                  backgroundColor: modalColors.inputBg,
                  borderWidth: 1,
                  borderColor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)',
                }}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                  {selectedCategory.source ? (
                    <Image
                      source={selectedCategory.source}
                      style={{
                        width: 28,
                        height: 28,
                        ...(selectedCategory.invertInLightMode && !isDarkMode && Platform.OS === 'web'
                          ? { filter: 'invert(1)' }
                          : null),
                      }}
                      resizeMode="contain"
                    />
                  ) : (
                    <Ionicons name={selectedCategory.iconName || 'construct-outline'} size={24} color={modalColors.text} />
                  )}
                  <View>
                    <Text style={{ color: modalColors.muted, fontFamily: 'VastagoGrotesk', fontSize: 11, fontWeight: '600' }}>
                      Category
                    </Text>
                    <Text style={{ color: modalColors.text, fontFamily: 'VastagoGrotesk', fontSize: 14, fontWeight: '700' }}>
                      {selectedCategory.label}
                    </Text>
                  </View>
                </View>
              </View>
            ) : null}

            <Text style={{ color: modalColors.muted, fontSize: 13, marginBottom: 16, fontFamily: 'VastagoGrotesk' }}>
              Suggest a resource for {venueData?.name || 'this venue'}
              {venueCity ? `, all venues in ${venueCity}` : ''}
              {venueState ? `${venueCity ? ',' : ''} or all venues in ${venueState}` : ''}. Admins will review before it appears.
            </Text>

            <Text style={{ color: modalColors.text, fontSize: 13, marginBottom: 6, fontFamily: 'VastagoGrotesk', fontWeight: '600' }}>
              Resource name *
            </Text>
            <TextInput
              value={resourceForm.name}
              onChangeText={(text) => setResourceForm((prev) => ({ ...prev, name: text }))}
              placeholder="e.g. Local backline rental"
              placeholderTextColor={modalColors.placeholder}
              style={{
                backgroundColor: modalColors.inputBg,
                borderRadius: 8,
                padding: 12,
                color: modalColors.inputText,
                marginBottom: 14,
                fontFamily: 'VastagoGrotesk',
                borderWidth: 1,
                borderColor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)',
              }}
            />

            <Text style={{ color: modalColors.text, fontSize: 13, marginBottom: 6, fontFamily: 'VastagoGrotesk', fontWeight: '600' }}>
              Google Maps link or embed (optional)
            </Text>
            <TextInput
              value={resourceForm.mapEmbedLink}
              onChangeText={(text) => setResourceForm((prev) => ({ ...prev, mapEmbedLink: text }))}
              placeholder="Paste a Google Maps link or embed"
              placeholderTextColor={modalColors.placeholder}
              autoCapitalize="none"
              style={{
                backgroundColor: modalColors.inputBg,
                borderRadius: 8,
                padding: 12,
                color: modalColors.inputText,
                marginBottom: resourceEmbedStatus.text ? 6 : 14,
                fontFamily: 'VastagoGrotesk',
                borderWidth: 1,
                borderColor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)',
              }}
            />
            {resourceEmbedStatus.text ? (
              <Text style={{
                color: resourceEmbedStatus.state === 'error'
                  ? '#e74c3c'
                  : resourceEmbedStatus.state === 'loading'
                    ? '#f39c12'
                    : resourceEmbedStatus.state === 'ready'
                      ? '#00A575'
                      : modalColors.muted,
                fontSize: 12,
                marginBottom: 14,
                fontFamily: 'VastagoGrotesk',
              }}>
                {resourceEmbedStatus.text}
              </Text>
            ) : null}

            <Text style={{ color: modalColors.text, fontSize: 13, marginBottom: 6, fontFamily: 'VastagoGrotesk', fontWeight: '600' }}>
              URL (optional)
            </Text>
            <TextInput
              value={resourceForm.url}
              onChangeText={(text) => setResourceForm((prev) => ({ ...prev, url: text }))}
              placeholder="https://"
              placeholderTextColor={modalColors.placeholder}
              autoCapitalize="none"
              style={{
                backgroundColor: modalColors.inputBg,
                borderRadius: 8,
                padding: 12,
                color: modalColors.inputText,
                marginBottom: 14,
                fontFamily: 'VastagoGrotesk',
                borderWidth: 1,
                borderColor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)',
              }}
            />

            <Text style={{ color: modalColors.text, fontSize: 13, marginBottom: 6, fontFamily: 'VastagoGrotesk', fontWeight: '600' }}>
              Description (optional)
            </Text>
            <TextInput
              value={resourceForm.description}
              onChangeText={(text) => setResourceForm((prev) => ({ ...prev, description: text }))}
              placeholder="What is this resource and why is it useful?"
              placeholderTextColor={modalColors.placeholder}
              multiline
              style={{
                backgroundColor: modalColors.inputBg,
                borderRadius: 8,
                padding: 12,
                color: modalColors.inputText,
                marginBottom: 14,
                minHeight: 80,
                textAlignVertical: 'top',
                fontFamily: 'VastagoGrotesk',
                borderWidth: 1,
                borderColor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)',
              }}
            />

            <Text style={{ color: modalColors.text, fontSize: 13, marginBottom: 6, fontFamily: 'VastagoGrotesk', fontWeight: '600' }}>
              Cover photo (optional)
            </Text>
            <Text style={{ color: modalColors.muted, fontSize: 12, marginBottom: 10, fontFamily: 'VastagoGrotesk', lineHeight: 18 }}>
              Add a banner or logo to show with this resource. Wide banners and square logos both work — we preserve the original aspect ratio.
            </Text>

            {resourcePhoto.preview ? (
              <View style={{ marginBottom: 14 }}>
                <View style={{
                  width: '100%',
                  aspectRatio: resourcePhoto.width && resourcePhoto.height
                    ? resourcePhoto.width / resourcePhoto.height
                    : 2.5,
                  maxHeight: 220,
                  borderRadius: 8,
                  overflow: 'hidden',
                  backgroundColor: isDarkMode ? '#0f1a2e' : '#f5f5f0',
                  borderWidth: 1,
                  borderColor: isDarkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)',
                }}>
                  <Image
                    source={{ uri: resourcePhoto.preview }}
                    style={{ width: '100%', height: '100%' }}
                    resizeMode="contain"
                  />
                </View>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                  {Platform.OS === 'web' ? (
                    <label style={{ cursor: 'pointer' }}>
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={async (e) => {
                          const file = e?.target?.files?.[0];
                          if (file) await handleResourcePhotoSelect(file);
                          e.target.value = '';
                        }}
                      />
                      <Text style={{ color: '#4e7de0', fontFamily: 'VastagoGrotesk', fontSize: 12, fontWeight: '600' }}>
                        Change photo
                      </Text>
                    </label>
                  ) : (
                    <TouchableOpacity onPress={handleResourcePhotoPick}>
                      <Text style={{ color: '#4e7de0', fontFamily: 'VastagoGrotesk', fontSize: 12, fontWeight: '600' }}>
                        Change photo
                      </Text>
                    </TouchableOpacity>
                  )}
                  <TouchableOpacity onPress={() => setResourcePhoto({ file: null, preview: null, width: null, height: null })}>
                    <Text style={{ color: '#c0392b', fontFamily: 'VastagoGrotesk', fontSize: 12, fontWeight: '600' }}>
                      Remove
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>
            ) : Platform.OS === 'web' ? (
              <label style={{
                display: 'block',
                marginBottom: 14,
                cursor: 'pointer',
              }}>
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={async (e) => {
                    const file = e?.target?.files?.[0];
                    if (file) await handleResourcePhotoSelect(file);
                    e.target.value = '';
                  }}
                />
                <View style={{
                  borderRadius: 8,
                  borderWidth: 2,
                  borderStyle: 'dashed',
                  borderColor: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)',
                  paddingVertical: 28,
                  alignItems: 'center',
                  backgroundColor: modalColors.inputBg,
                }}>
                  <Ionicons name="image-outline" size={32} color={modalColors.muted} />
                  <Text style={{ color: modalColors.text, fontFamily: 'VastagoGrotesk', fontSize: 13, marginTop: 8, fontWeight: '600' }}>
                    Choose a photo
                  </Text>
                  <Text style={{ color: modalColors.muted, fontFamily: 'VastagoGrotesk', fontSize: 11, marginTop: 4 }}>
                    PNG or JPG recommended
                  </Text>
                </View>
              </label>
            ) : (
              <TouchableOpacity
                onPress={handleResourcePhotoPick}
                style={{
                  marginBottom: 14,
                  borderRadius: 8,
                  borderWidth: 2,
                  borderStyle: 'dashed',
                  borderColor: isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)',
                  paddingVertical: 28,
                  alignItems: 'center',
                  backgroundColor: modalColors.inputBg,
                }}
              >
                <Ionicons name="image-outline" size={32} color={modalColors.muted} />
                <Text style={{ color: modalColors.text, fontFamily: 'VastagoGrotesk', fontSize: 13, marginTop: 8, fontWeight: '600' }}>
                  Choose a photo
                </Text>
                <Text style={{ color: modalColors.muted, fontFamily: 'VastagoGrotesk', fontSize: 11, marginTop: 4 }}>
                  PNG or JPG recommended
                </Text>
              </TouchableOpacity>
            )}

            <Text style={{ color: modalColors.text, fontSize: 13, marginBottom: 8, fontFamily: 'VastagoGrotesk', fontWeight: '600' }}>
              Share with
            </Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
              <TouchableOpacity
                onPress={() => setResourceForm((prev) => ({ ...prev, scope: 'venue' }))}
                style={{
                  flexGrow: 1,
                  flexBasis: '30%',
                  minWidth: 120,
                  paddingVertical: 10,
                  borderRadius: 8,
                  backgroundColor: resourceForm.scope === 'venue' ? '#4e7de0' : modalColors.inactiveBtn,
                  alignItems: 'center',
                }}
              >
                <Text style={{ color: resourceForm.scope === 'venue' ? '#fff' : modalColors.inactiveBtnText, fontFamily: 'VastagoGrotesk', fontSize: 12, fontWeight: '600' }}>
                  This venue
                </Text>
              </TouchableOpacity>
              {venueCity ? (
                <TouchableOpacity
                  onPress={() => setResourceForm((prev) => ({ ...prev, scope: 'city' }))}
                  style={{
                    flexGrow: 1,
                    flexBasis: '30%',
                    minWidth: 120,
                    paddingVertical: 10,
                    borderRadius: 8,
                    backgroundColor: resourceForm.scope === 'city' ? '#4e7de0' : modalColors.inactiveBtn,
                    alignItems: 'center',
                  }}
                >
                  <Text style={{ color: resourceForm.scope === 'city' ? '#fff' : modalColors.inactiveBtnText, fontFamily: 'VastagoGrotesk', fontSize: 12, fontWeight: '600', textAlign: 'center' }}>
                    All {venueCity} venues
                  </Text>
                </TouchableOpacity>
              ) : null}
              {venueState ? (
                <TouchableOpacity
                  onPress={() => setResourceForm((prev) => ({ ...prev, scope: 'state' }))}
                  style={{
                    flexGrow: 1,
                    flexBasis: '30%',
                    minWidth: 120,
                    paddingVertical: 10,
                    borderRadius: 8,
                    backgroundColor: resourceForm.scope === 'state' ? '#4e7de0' : modalColors.inactiveBtn,
                    alignItems: 'center',
                  }}
                >
                  <Text style={{ color: resourceForm.scope === 'state' ? '#fff' : modalColors.inactiveBtnText, fontFamily: 'VastagoGrotesk', fontSize: 12, fontWeight: '600', textAlign: 'center' }}>
                    All {venueState} venues
                  </Text>
                </TouchableOpacity>
              ) : null}
            </View>

            <TouchableOpacity
              onPress={handleResourceSubmit}
              disabled={resourceSubmitting}
              style={{
                backgroundColor: '#00A575',
                borderRadius: 8,
                paddingVertical: 14,
                alignItems: 'center',
                opacity: resourceSubmitting ? 0.6 : 1,
              }}
            >
              {resourceSubmitting ? (
                <ActivityIndicator size="small" color="#ffffff" />
              ) : (
                <Text style={{ color: '#ffffff', fontFamily: 'VastagoGrotesk', fontWeight: '700' }}>SUBMIT FOR REVIEW</Text>
              )}
            </TouchableOpacity>
              </ScrollView>
            )}
          </View>
        </View>
      </Modal>
    );
  };

  const VENUE_ARCHIVE_REASON_LABELS = { closed: 'Closed', new_ownership: 'New Ownership' };

  const closeVenueArchiveModal = () => {
    setVenueArchiveReason(null);
    setVenueArchiveDetails('');
  };

  const handleVenueArchiveSubmit = async () => {
    const contributor = auth.currentUser;
    if (!isSignedInAccount(contributor)) {
      setToast({ message: 'Sign in to request a venue archive.', type: 'warning' });
      navigation.navigate('Login');
      return;
    }
    if (!venueArchiveDetails.trim()) {
      setToast({ message: 'Please add some details.', type: 'warning' });
      return;
    }

    setVenueArchiveSubmitting(true);
    try {
      await addDoc(collection(getFirestore(), 'venue_archive_requests'), {
        venueId,
        venueName: venueData?.name || '',
        reason: venueArchiveReason,
        reasonLabel: VENUE_ARCHIVE_REASON_LABELS[venueArchiveReason] || '',
        details: venueArchiveDetails.trim(),
        userId: contributor.uid,
        userEmail: contributor.email || '',
        userName: userData?.displayName || userData?.username || contributor.displayName || 'Anonymous',
        status: 'pending',
        createdAt: serverTimestamp(),
      });
      closeVenueArchiveModal();
      setToast({ message: 'Thanks! Your archive request was sent for admin review.', type: 'success' });
    } catch (error) {
      console.error('Error submitting venue archive request:', error);
      setToast({ message: 'Failed to send request. Please try again.', type: 'error' });
    } finally {
      setVenueArchiveSubmitting(false);
    }
  };

  const renderVenueArchiveModal = () => (
    <Modal
      visible={!!venueArchiveReason}
      transparent
      animationType="fade"
      onRequestClose={() => {
        if (!venueArchiveSubmitting) closeVenueArchiveModal();
      }}
    >
      <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.65)', justifyContent: 'center', alignItems: 'center', padding: 20 }}>
        <View style={{ width: '100%', maxWidth: 480, backgroundColor: '#fff', borderColor: '#000', borderWidth: 2, borderRadius: 16, padding: 20 }}>
          <Text style={{ color: '#000', fontFamily: 'VastagoGrotesk', fontSize: 15, fontWeight: '700', lineHeight: 22 }}>
            Archive Venue: {VENUE_ARCHIVE_REASON_LABELS[venueArchiveReason] || ''}
          </Text>
          <Text style={{ color: '#000', fontFamily: 'VastagoGrotesk', fontSize: 14, lineHeight: 21, marginTop: 12, marginBottom: 14 }}>
            {venueArchiveReason === 'new_ownership'
              ? 'Tell us more about the new ownership (new name, owner, date, source).'
              : 'Tell us more about the closure (when it closed, source, etc.).'}
          </Text>
          <TextInput
            value={venueArchiveDetails}
            onChangeText={setVenueArchiveDetails}
            placeholder="More details..."
            placeholderTextColor="#666"
            multiline
            numberOfLines={5}
            style={{ minHeight: 110, backgroundColor: '#fff', borderWidth: 1, borderColor: '#000', borderRadius: 8, padding: 10, color: '#000', fontFamily: 'VastagoGrotesk', textAlignVertical: 'top' }}
          />
          <View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
            <TouchableOpacity
              onPress={closeVenueArchiveModal}
              disabled={venueArchiveSubmitting}
              style={{ borderWidth: 1, borderColor: '#000', borderRadius: 8, paddingHorizontal: 16, paddingVertical: 11, minWidth: 92, alignItems: 'center' }}
            >
              <Text style={{ color: '#000', fontFamily: 'VastagoGrotesk', fontWeight: '600' }}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              onPress={handleVenueArchiveSubmit}
              disabled={venueArchiveSubmitting}
              style={{ backgroundColor: '#000', borderRadius: 8, paddingHorizontal: 16, paddingVertical: 11, minWidth: 92, alignItems: 'center', opacity: venueArchiveSubmitting ? 0.6 : 1 }}
            >
              {venueArchiveSubmitting ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <Text style={{ color: '#fff', fontFamily: 'VastagoGrotesk', fontWeight: '700' }}>Send</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  const renderVenueDocumentUploadModal = () => (
    <Modal
      visible={showVenueDocumentUploadModal}
      transparent
      animationType="fade"
      onRequestClose={() => {
        if (!venueDocumentSubmitting) setShowVenueDocumentUploadModal(false);
      }}
    >
      <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.65)', justifyContent: 'center', alignItems: 'center', padding: 20 }}>
        <View style={{ width: '100%', maxWidth: 540, maxHeight: '90%', backgroundColor: '#fff', borderColor: '#000', borderWidth: 2, borderRadius: 16, padding: 20 }}>
          <ScrollView keyboardShouldPersistTaps="handled">
            <Text style={{ color: '#000', fontFamily: 'VastagoGrotesk', fontSize: 15, fontWeight: '700', lineHeight: 22 }}>
              UPLOAD tech packs or other venue related documents such as lighting patch sheets.
            </Text>
            <Text style={{ color: '#000', fontFamily: 'VastagoGrotesk', fontSize: 14, lineHeight: 21, marginTop: 14 }}>
              All documents are reviewed before going live.
            </Text>
            <Text style={{ color: '#000', fontFamily: 'VastagoGrotesk', fontSize: 14, lineHeight: 21, marginTop: 12, marginBottom: 20 }}>
              Please make it obvious that the documents correlate with the venue.
            </Text>

            {Platform.OS === 'web' ? (
              <input
                ref={venueDocumentInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={(event) => {
                  addVenueDocumentFiles(event.target.files);
                  event.target.value = '';
                }}
              />
            ) : null}
            <TouchableOpacity
              activeOpacity={0.75}
              onPress={handleVenueDocumentPick}
              onDragEnter={(event) => {
                event.preventDefault?.();
                setVenueDocumentDropActive(true);
              }}
              onDragOver={(event) => event.preventDefault?.()}
              onDragLeave={(event) => {
                event.preventDefault?.();
                setVenueDocumentDropActive(false);
              }}
              onDrop={(event) => {
                event.preventDefault?.();
                setVenueDocumentDropActive(false);
                addVenueDocumentFiles(event.dataTransfer?.files);
              }}
              style={{
                minHeight: 150,
                borderWidth: 2,
                borderStyle: 'dashed',
                borderColor: '#000',
                borderRadius: 12,
                backgroundColor: venueDocumentDropActive ? '#f0f0f0' : '#fff',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 20,
                marginBottom: 16,
                ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
              }}
            >
              <View style={{ width: 76, height: 76, alignItems: 'center', justifyContent: 'center' }}>
                <Ionicons name="document-outline" size={72} color="#000" />
                <Ionicons name="arrow-up" size={34} color="#000" style={{ position: 'absolute', top: 23 }} />
              </View>
              <Text style={{ color: '#000', fontFamily: 'VastagoGrotesk', fontSize: 14, fontWeight: '600', marginTop: 8, textAlign: 'center' }}>
                Drop documents here or click to upload
              </Text>
            </TouchableOpacity>

            {venueDocumentFiles.map((item, index) => (
              <View key={`${item.fileName}-${index}`} style={{ marginBottom: 12 }}>
                <Text style={{ color: '#333', fontFamily: 'VastagoGrotesk', fontSize: 11, marginBottom: 5 }} numberOfLines={1}>
                  {item.fileName}
                </Text>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <TextInput
                    value={item.name}
                    onChangeText={(name) => setVenueDocumentFiles((previous) => previous.map((entry, entryIndex) => entryIndex === index ? { ...entry, name } : entry))}
                    placeholder="Name this document"
                    placeholderTextColor="#666"
                    style={{ flex: 1, backgroundColor: '#fff', borderWidth: 1, borderColor: '#000', borderRadius: 8, paddingHorizontal: 10, paddingVertical: 9, color: '#000', fontFamily: 'VastagoGrotesk' }}
                  />
                  <TouchableOpacity onPress={() => setVenueDocumentFiles((previous) => previous.filter((_, entryIndex) => entryIndex !== index))} accessibilityLabel={`Remove ${item.fileName}`}>
                    <Ionicons name="close-circle-outline" size={24} color="#000" />
                  </TouchableOpacity>
                </View>
              </View>
            ))}

            <View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
              <TouchableOpacity
                onPress={() => {
                  setVenueDocumentFiles([]);
                  setShowVenueDocumentUploadModal(false);
                }}
                disabled={venueDocumentSubmitting}
                style={{ borderWidth: 1, borderColor: '#000', borderRadius: 8, paddingHorizontal: 16, paddingVertical: 11, minWidth: 92, alignItems: 'center' }}
              >
                <Text style={{ color: '#000', fontFamily: 'VastagoGrotesk', fontWeight: '600' }}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={handleVenueDocumentSubmit}
                disabled={venueDocumentSubmitting}
                style={{ backgroundColor: '#000', borderRadius: 8, paddingHorizontal: 16, paddingVertical: 11, minWidth: 112, alignItems: 'center', opacity: venueDocumentSubmitting ? 0.6 : 1 }}
              >
                {venueDocumentSubmitting ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Text style={{ color: '#fff', fontFamily: 'VastagoGrotesk', fontWeight: '700' }}>Submit</Text>
                )}
              </TouchableOpacity>
            </View>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );

  const renderSpecFlagModal = () => {
    const modalColors = isDarkMode
      ? { bg: SPEC_SHEET_PANEL_DARK_BG, text: '#f0f4ff', muted: '#a8b8d8', inputBg: '#1e3050' }
      : { bg: '#E9E9E9', text: '#1a2332', muted: '#4a5568', inputBg: '#ffffff' };
    const isLoggedIn = Boolean(auth.currentUser);
    const specFlagCategories = isLoggedIn ? FOOTER_FLAG_CATEGORIES : VENUE_INSTANT_SPEC_FLAG_CATEGORIES;
    const isInstantSubmit = isInstantSpecFlagCategory(specFlagCategory);

    return (
      <Modal visible={showSpecFlagModal} transparent animationType="fade" onRequestClose={() => setShowSpecFlagModal(false)}>
        <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.75)', justifyContent: 'center', alignItems: 'center', padding: 20 }}>
          <View style={{ backgroundColor: modalColors.bg, borderRadius: 12, padding: 20, width: '100%', maxWidth: 480 }}>
            <Text style={{ color: modalColors.text, fontFamily: 'VastagoGrotesk', fontSize: 18, fontWeight: '700', marginBottom: 16 }}>
              Flag a category note
            </Text>
            <Text style={{ color: modalColors.muted, fontFamily: 'VastagoGrotesk', fontSize: 13, marginBottom: 16 }}>
              {isLoggedIn
                ? 'Unrelated to tech specs — note something about Accessibility, Safety, Environment, or Education for this venue.'
                : 'Unrelated to tech specs — note something about Environment or Education for this venue..'}
            </Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
              {specFlagCategories.map((cat) => (
                <TouchableOpacity
                  key={cat.id}
                  onPress={() => setSpecFlagCategory(cat.id)}
                  style={{
                    paddingHorizontal: 12,
                    paddingVertical: 8,
                    borderRadius: 20,
                    backgroundColor: specFlagCategory === cat.id ? '#ff6600' : (isDarkMode ? '#243a5c' : '#d8dce3'),
                  }}
                >
                  <Text style={{ color: specFlagCategory === cat.id ? '#fff' : modalColors.text, fontFamily: 'VastagoGrotesk', fontSize: 12, fontWeight: '600' }}>
                    {cat.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            <TextInput
              value={specFlagNotes}
              onChangeText={setSpecFlagNotes}
              placeholder="What should we know?"
              placeholderTextColor={modalColors.muted}
              multiline
              numberOfLines={4}
              style={{
                backgroundColor: modalColors.inputBg,
                color: modalColors.text,
                borderRadius: 8,
                padding: 12,
                minHeight: 100,
                fontFamily: 'VastagoGrotesk',
                marginBottom: 16,
                textAlignVertical: 'top',
              }}
            />
            <View style={{ flexDirection: 'row', gap: 10, justifyContent: 'flex-end' }}>
              <TouchableOpacity onPress={() => setShowSpecFlagModal(false)} style={{ paddingHorizontal: 16, paddingVertical: 10 }}>
                <Text style={{ color: modalColors.muted, fontFamily: 'VastagoGrotesk', fontWeight: '600' }}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={handleSpecFlagSubmit}
                disabled={specFlagSubmitting}
                style={{ backgroundColor: '#ff6600', borderRadius: 8, paddingHorizontal: 18, paddingVertical: 10, opacity: specFlagSubmitting ? 0.6 : 1 }}
              >
                {specFlagSubmitting ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Text style={{ color: '#fff', fontFamily: 'VastagoGrotesk', fontWeight: '700' }}>Add note</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    );
  };

  const renderVenueInterestsModal = () => {
    const modalColors = isDarkMode
      ? { bg: SPEC_SHEET_PANEL_DARK_BG, text: '#f0f4ff', muted: '#a8b8d8' }
      : { bg: '#E9E9E9', text: '#1a2332', muted: '#4a5568' };

    return (
      <Modal visible={showVenueInterestsModal} transparent animationType="fade" onRequestClose={() => setShowVenueInterestsModal(false)}>
        <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.75)', justifyContent: 'center', alignItems: 'center', padding: 20 }}>
          <View style={{ backgroundColor: modalColors.bg, borderRadius: 12, padding: 20, width: '100%', maxWidth: 520, maxHeight: '80%' }}>
            <Text style={{ color: modalColors.text, fontFamily: 'VastagoGrotesk', fontSize: 18, fontWeight: '700', marginBottom: 8 }}>
              Tag venue with interests
            </Text>
            <Text style={{ color: modalColors.muted, fontFamily: 'VastagoGrotesk', fontSize: 13, marginBottom: 14 }}>
              Select interests for this venue. No account needed — tags appear publicly right away.
            </Text>
            <ScrollView style={{ maxHeight: 280, marginBottom: 16 }}>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                {interestCatalog.map((item) => {
                  const label = item.label || item.id;
                  const selected = venueInterestSelection.includes(label);
                  return (
                    <TouchableOpacity
                      key={item.id || label}
                      onPress={() => toggleVenueInterest(label)}
                      style={{
                        paddingHorizontal: 12,
                        paddingVertical: 8,
                        borderRadius: 20,
                        backgroundColor: selected ? SPEC_NAV_BLUE : (isDarkMode ? '#243a5c' : '#d8dce3'),
                      }}
                    >
                      <Text style={{ color: selected ? '#fff' : modalColors.text, fontFamily: 'VastagoGrotesk', fontSize: 12 }}>
                        {item.emoji ? `${item.emoji} ` : ''}{label}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </ScrollView>
            <View style={{ flexDirection: 'row', gap: 10, justifyContent: 'flex-end' }}>
              <TouchableOpacity onPress={() => setShowVenueInterestsModal(false)} style={{ paddingHorizontal: 16, paddingVertical: 10 }}>
                <Text style={{ color: modalColors.muted, fontFamily: 'VastagoGrotesk', fontWeight: '600' }}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={handleVenueInterestsSubmit}
                disabled={venueInterestsSubmitting}
                style={{ backgroundColor: SPEC_NAV_BLUE, borderRadius: 8, paddingHorizontal: 18, paddingVertical: 10, opacity: venueInterestsSubmitting ? 0.6 : 1 }}
              >
                {venueInterestsSubmitting ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Text style={{ color: '#fff', fontFamily: 'VastagoGrotesk', fontWeight: '700' }}>Add tags</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    );
  };


  return (
    <>
    <View style={[globalStyles.container, { backgroundColor: venuePalette?.pageBackground ?? '#3D7FE6' }]}>
      <Modal
        visible={showManualSelector}
        transparent={true}
        animationType="fade"
        onRequestClose={() => {
          setShowManualSelector(false);
          setManualSearchQuery('');
        }}
      >
        <View style={{
          flex: 1,
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 20
        }}>
          <View style={{
            backgroundColor: '#2c3e50',
            borderRadius: 10,
            padding: 20,
            width: '100%',
            maxWidth: 700,
            maxHeight: '85%',
            ...(Platform.OS === 'web' && {
              border: '2px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 20px 50px rgba(0, 0, 0, 0.8)'
            })
          }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15 }}>
              <Text style={{ color: '#fff', fontSize: 18, fontWeight: 'bold' }}>
                Select Equipment Manual
              </Text>
              <TouchableOpacity
                style={{
                  padding: 8,
                  borderRadius: 15,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }}
                onPress={() => {
                  setShowManualSelector(false);
                  setManualSearchQuery('');
                }}
              >
                <Text style={{ color: '#fff', fontSize: 16, fontWeight: 'bold' }}>✕</Text>
              </TouchableOpacity>
            </View>

            <TextInput
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: 8,
                padding: 12,
                color: '#fff',
                marginBottom: 15,
                fontSize: 14,
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}
              placeholder="Search manuals by name or keyword..."
              placeholderTextColor="rgba(255, 255, 255, 0.5)"
              value={manualSearchQuery}
              onChangeText={setManualSearchQuery}
            />

            <Text style={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: 12,
              marginBottom: 10
            }}>
              {loadingManuals ? 'Loading...' : `${availableManuals.filter(manual => {
                const nameMatch = manual.name.toLowerCase().includes(manualSearchQuery.toLowerCase());
                const keywordMatch = manual.keyword.toLowerCase().includes(manualSearchQuery.toLowerCase());
                return nameMatch || keywordMatch;
              }).length} of ${availableManuals.length} manuals`}
            </Text>

            <ScrollView style={{ maxHeight: 350, marginBottom: 15 }}>
              {loadingManuals ? (
                <View style={{ textAlign: 'center', padding: 30 }}>
                  <Text style={{ color: '#fff', fontSize: 16 }}>
                    Loading manuals...
                  </Text>
                </View>
              ) : availableManuals.filter(manual => {
                const nameMatch = manual.name.toLowerCase().includes(manualSearchQuery.toLowerCase());
                const keywordMatch = manual.keyword.toLowerCase().includes(manualSearchQuery.toLowerCase());
                return nameMatch || keywordMatch;
              }).length === 0 ? (
                <View style={{ textAlign: 'center', padding: 30 }}>
                  <Text style={{ color: '#fff', fontSize: 16 }}>
                    {manualSearchQuery ? 'No manuals found matching your search.' : 'No manuals available.'}
                  </Text>
                  {manualSearchQuery && (
                    <Text style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 12, marginTop: 5 }}>
                      Try searching for different keywords or equipment names.
                    </Text>
                  )}
                </View>
              ) : (
                availableManuals.filter(manual => {
                  const nameMatch = manual.name.toLowerCase().includes(manualSearchQuery.toLowerCase());
                  const keywordMatch = manual.keyword.toLowerCase().includes(manualSearchQuery.toLowerCase());
                  return nameMatch || keywordMatch;
                }).map((manual) => (
                  <TouchableOpacity
                    key={manual.keyword}
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: 8,
                      padding: 15,
                      marginBottom: 8,
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}
                    onPress={() => handleSelectExistingManual(manual)}
                  >
                    <Text style={{
                      color: '#fff',
                      fontWeight: 'bold',
                      marginBottom: 5,
                      fontSize: 14
                    }}>
                      {manual.name}
                    </Text>
                    <Text style={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      fontSize: 11,
                      marginBottom: 3
                    }}>
                      Keyword: {manual.keyword} • URL: {manual.url.length > 50 ? manual.url.substring(0, 50) + '...' : manual.url}
                    </Text>
                    {manual.software?.name && (
                      <Text style={{
                        color: 'rgba(52, 152, 219, 0.9)',
                        fontSize: 11,
                        marginBottom: 3
                      }}>
                        📦 Software: {manual.software.name}
                      </Text>
                    )}
                    {manual.category && (
                      <Text style={{
                        color: 'rgba(46, 204, 113, 0.9)',
                        fontSize: 10,
                        textTransform: 'uppercase'
                      }}>
                        {manual.category}
                      </Text>
                    )}
                  </TouchableOpacity>
                ))
              )}
            </ScrollView>

            <View style={{ flexDirection: 'row', justifyContent: 'center' }}>
              <TouchableOpacity
                style={{
                  backgroundColor: '#95a5a6',
                  padding: 12,
                  borderRadius: 8,
                  minWidth: 100,
                  alignItems: 'center'
                }}
                onPress={() => {
                  setShowManualSelector(false);
                  setManualSearchQuery('');
                }}
              >
                <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 14 }}>Cancel</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
      {showReportForm && venueData && renderReportFormModal()}
      {renderResourceSubmissionModal()}
      {renderSpecFlagModal()}
      {renderVenueInterestsModal()}
      {renderVenueDocumentUploadModal()}
      {renderVenueArchiveModal()}
      <View style={[globalStyles.overlay, { overflowY: 'hidden', paddingBottom: 300, paddingTop: 0 }]}>
        <View style={{ width: '100%' }}>
          <View style={[venueStyles.venueContainer, { position: 'relative' }]}>
            <TouchableOpacity
              style={[venueStyles.backButton, { backgroundColor: theme.text.primary }]}
              onPress={() => navigation.goBack()}
            >
              <Text style={[venueStyles.backButtonText, { color: theme.text.inverse }]}>← Back</Text>
            </TouchableOpacity>

            <View style={[homeStyles.searchContainer, { marginBottom: 30, marginTop: 12 }]}>
              <SearchBar
                value={searchQuery}
                onChangeText={handleSearch}
                color={theme.text.primary}
                onSubmit={handleSearchSubmit}
                placeholder="Search by venue name, location, or affiliates..."
              />
              <TouchableOpacity
                style={venueStyles.locationIconContainer}
                onPress={handleLocationSearch}
                disabled={isGettingLocation}
              >
                {isGettingLocation ? (
                  <ActivityIndicator size="small" color={theme.text.primary} />
                ) : (
                  <View style={venueStyles.svgIcon}>
                    <View style={[venueStyles.locationDot, { backgroundColor: theme.text.primary }, userLocation && { backgroundColor: '#2ecc71' }]} />
                    <View style={[venueStyles.locationRing, { borderColor: theme.text.primary }, userLocation && { borderColor: '#2ecc71' }]} />
                  </View>
                )}
              </TouchableOpacity>
            </View>

            {!isMobile ? (
              <View style={venueStyles.venueTwoRowGrid}>
                <View style={[venueStyles.venueGridRow, { zIndex: 10 }]}>
                  <View style={venueStyles.venueGridCol1} />
                  <View style={[venueStyles.venueGridCol2, { minHeight: 0 }]}>
                    <View style={[venueStyles.titleCard, { flex: 1, width: '100%' }]}>
                      <View style={venueStyles.titleCardFolderIconWrapper}>
                        <TouchableOpacity
                          onPress={handleFolderIconPress}
                          style={venueStyles.titleCardFolderIcon}
                          activeOpacity={0.8}
                        >
                          <Ionicons name="folder-open" size={getResponsiveValue({ mobile: 28, tablet: 36, laptop: 40, desktop: 42 })} color={'#bca785'} />
                        </TouchableOpacity>
                        {folderDropdownOpen && (
                          <>
                            <TouchableWithoutFeedback onPress={() => setFolderDropdownOpen(false)}>
                              <View style={[StyleSheet.absoluteFill, { top: -3000, left: -3000, width: 6000, height: 6000, zIndex: 15 }]} />
                            </TouchableWithoutFeedback>
                            <View style={venueStyles.folderDropdown}>
                              <TouchableOpacity style={venueStyles.folderDropdownOption} onPress={handleAddToNewTour} activeOpacity={0.7}>
                                <Text style={venueStyles.folderDropdownOptionText}>Add to New Tour</Text>
                              </TouchableOpacity>
                              <TouchableOpacity style={venueStyles.folderDropdownOption} onPress={() => { setFolderDropdownOpen(false); handleAddToFolder(); }} activeOpacity={0.7}>
                                <Text style={venueStyles.folderDropdownOptionText}>Add to Existing Tour</Text>
                              </TouchableOpacity>
                              <TouchableOpacity style={venueStyles.folderDropdownOption} onPress={() => { setFolderDropdownOpen(false); setShowRoomEqModal(true); }} activeOpacity={0.7}>
                                <Text style={venueStyles.folderDropdownOptionText}>Save Room Info</Text>
                              </TouchableOpacity>
                              <TouchableOpacity style={venueStyles.folderDropdownOption} onPress={handleSaveVenue} activeOpacity={0.7}>
                                <Text style={venueStyles.folderDropdownOptionText}>Save Venue</Text>
                              </TouchableOpacity>
                            </View>
                          </>
                        )}
                      </View>
                      <View style={{
                        width: '100%',
                        display: 'flex',
                        marginBottom: 10,
                        flexDirection: 'row',
                        justifyContent: 'flex-start',
                        alignItems: 'flex-start',
                        gap: 20,
                        padding: 0
                      }}>
                        {/* <View style={venueStyles.smallerVotingContainer}>
                     <Text style={[venueStyles.smallerVotingText, { color: '#3d80e6' }]}>Is this correct?</Text>
                    
                    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                   <TouchableOpacity 
                        onPress={() => handleVote('title', 'up')}
                        style={[venueStyles.voteButton, {
                          marginRight: 10,
                          opacity: userTitleVote === 'up' ? 1 : 0.7,
                          flexDirection: 'row',
                          alignItems: 'center'
                        }]}
                      >
                        <Ionicons 
                          name={userTitleVote === 'up' ? 'thumbs-up' : 'thumbs-up-outline'} 
                          size={16} 
                          color="#000000" 
                        />
                        <Text style={[venueStyles.voteCount, { fontSize: 10 }]}>{titleVotes.up}</Text>
                   </TouchableOpacity>
                      
                      <TouchableOpacity
                        onPress={() => handleVote('title', 'down')}
                        style={[venueStyles.voteButton, {
                          opacity: userTitleVote === 'down' ? 1 : 0.7,
                          flexDirection: 'row',
                          alignItems: 'center'
                        }]}
                      >
                        <Ionicons 
                          name={userTitleVote === 'down' ? 'thumbs-down' : 'thumbs-down-outline'} 
                          size={16} 
                          color="#000000" 
                        />
                        <Text style={[venueStyles.voteCount, { fontSize: 10 }]}>{titleVotes.down}</Text>
                      </TouchableOpacity>
                    </View>
                  </View> */}
                      </View>
                      <View style={[{ flex: 1, marginBottom: 'auto' }]}>
                        <View style={venueStyles.venueNameContainer}>
                          <Text style={[venueStyles.venueName, getVenueTextStyle('venueName'), { fontWeight: '500' }]}>{venueData?.name || 'Venue Name'}</Text>
                        </View>

                        <Text style={[venueStyles.venueAddress, getVenueTextStyle('venueAddress')]}>
                          {venueData?.city}, {venueData?.state}
                        </Text>

                        {venueData?.website && (
                          <Text
                            style={[venueStyles.venueWebsite, getVenueTextStyle('website')]}
                            onPress={() => window.open(venueData.website, '_blank')}
                          >
                            {venueData.website}
                          </Text>
                        )}
                        {affiliateTokens.length > 0 && (
                          <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', gap: 6 }}>
                            {affiliateTokens.map((aff, idx) => (
                              <TouchableOpacity key={`${aff}-${idx}`} onPress={() => handleAffiliatePress(aff)}>
                                <Text style={[venueStyles.affiliateInfo, getVenueTextStyle('affiliate'), { textAlign: 'center' }]}>
                                  {aff}
                                  {idx < affiliateTokens.length - 1 ? ',' : ''}
                                </Text>
                              </TouchableOpacity>
                            ))}
                          </View>
                        )}
                        {approvedVenueInterestItems.length > 0 && (
                          <View style={{ alignItems: 'center', marginTop: 14, marginBottom: 2, zIndex: 50 }}>
                            <CardInterestsDisplay
                              items={approvedVenueInterestItems}
                              size="large"
                              popoverPlacement="above"
                              popoverAlign="center"
                            />
                          </View>
                        )}

                        {/* Feature badges - tap to search venues with that feature */}
                        {renderFeatureBadges()}


                      </View>
                      {/* {!isMobile && (
                  <View style={[venueStyles.actionButtonsContainer, { marginTop: 0, marginBottom: 0 }]}>
                   <TouchableOpacity 
                     style={[
                       venueStyles.actionButton,
                        hoveredButton === 'tech' && { backgroundColor: '#434252' }
                     ]}
                      onPress={handleAddTechInfo}
                      onMouseEnter={() => setHoveredButton('tech')}
                      onMouseLeave={() => setHoveredButton(null)}
                   >
                      <Text style={[
                        venueStyles.actionButtonText,
                        hoveredButton === 'tech' && { color: '#DBC0FC' }
                      ]}>SHARE VENUE SPECS</Text>
                   </TouchableOpacity>

                   <TouchableOpacity 
                     style={[
                       venueStyles.actionButton,
                        (isSaved || hoveredButton === 'save') && { backgroundColor: '#434252' }
                     ]}
                     onPress={handleSaveVenue}
                      onMouseEnter={() => setHoveredButton('save')}
                      onMouseLeave={() => setHoveredButton(null)}
                   >
                      <Text style={[
                        venueStyles.actionButtonText,
                        (isSaved || hoveredButton === 'save') && { color: '#DBC0FC' }
                      ]}>
                        {isSaved ? 'VENUE SAVED' : 'SAVE VENUE'}
                      </Text>
                   </TouchableOpacity>

                   <TouchableOpacity 
                     style={[
                       venueStyles.actionButton,
                        hoveredButton === 'report' && { backgroundColor: '#434252' }
                     ]}
                      onPress={handleSubmitReport}
                      onMouseEnter={() => setHoveredButton('report')}
                      onMouseLeave={() => setHoveredButton(null)}
                   >
                      <Text style={[
                        venueStyles.actionButtonText,
                        hoveredButton === 'report' && { color: '#DBC0FC' }
                      ]}>PROVIDE A REPORT</Text>
                   </TouchableOpacity>
                 </View>
                )} */}

                      {isMobile && (
                        <View style={[venueStyles.overallGradeCard, { padding: 0 }]}>
                          <ReportCardChalkboard
                            grade={getLetterGrade(venueData?.approved?.overallGrade)}
                            reportCount={publicVenueReports.length}
                            onOpenReports={() => setShowReportsModal(true)}
                            onSubmitReport={handleSubmitReport}
                          />
                        </View>
                      )}
                      {isMobile && renderMapCard()}
                      {mobileOptionsToggle !== null && (
                        <TouchableWithoutFeedback
                          onPress={() => {
                            LayoutAnimation.configureNext(LayoutAnimation.create(200, LayoutAnimation.Types.easeInEaseOut, LayoutAnimation.Properties.opacity));
                            setMobileOptionsToggle(null);
                          }}
                        >
                          <View style={[StyleSheet.absoluteFill, { zIndex: 50, backgroundColor: 'transparent' }]} />
                        </TouchableWithoutFeedback>
                      )}
                    </View>
                  </View>
                  <View style={venueStyles.venueGridCol3} />
                </View>
                <View style={venueStyles.venueGridRow}>
                  <View style={venueStyles.venueGridCol1}>
                    <View style={{ paddingTop: 20 }}>
                      {renderOverallGrade()}
                  </View>
                  </View>
                  <View style={[venueStyles.venueGridCol2, { minHeight: 0 }]}>
                    <View style={[venueStyles.venueLayout, { flex: 1, width: '100%' }]}>
                      {renderActiveSection()}

                      {/* {!isMobile && (
                  <View style={[venueStyles.rightColumn, (currentView === 'media' || currentView === 'discussion') && { backgroundColor: '#88a0a4' }]}>
                    <View style={{ 
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      minHeight: 'auto',
                      height: 'auto',
                      overflow: 'visible',
                      ...(Platform.OS === 'web' && {
                        minHeight: getResponsiveValue({
                          mobile: 'auto',
                          tablet: 'auto',
                          laptop: undefined,
                          desktop: undefined
                        })
                      })
                    }}>
                      {currentView === 'discussion' ? (
                        <DiscussionBoard
                          venueId={venueId}
                          messages={messages}
                          loadMessages={loadDiscussionMessages}
                          setMessages={setMessages}
                        />
                      ) : (
                        renderCurrentView()
                      )}
                      <View style={[venueStyles.viewButtonsContainer, { marginTop: 'auto', paddingTop: 20 }]}>
                    <TouchableOpacity 
                      style={[
                            venueStyles.viewButton,
                            currentView === 'media' && { backgroundColor: '#3D7FE6' },
                            hoveredButton === 'media' && venueStyles.viewButtonHovered
                      ]}
                          onPress={() => setCurrentView('media')}
                          onMouseEnter={() => setHoveredButton('media')}
                          onMouseLeave={() => setHoveredButton(null)}
                    >
                          <Text style={[
                            venueStyles.viewButtonText,
                            currentView === 'media' && { color: '#DBC0FC' },
                            hoveredButton === 'media' && venueStyles.viewButtonTextHovered
                          ]}>USER UPLOADS</Text>
                    </TouchableOpacity>

                    <TouchableOpacity 
                      style={[
                            venueStyles.viewButton,
                            currentView === 'manuals' && { backgroundColor: '#3D7FE6' },
                            hoveredButton === 'manuals' && venueStyles.viewButtonHovered
                      ]}
                          onPress={() => setCurrentView('manuals')}
                          onMouseEnter={() => setHoveredButton('manuals')}
                          onMouseLeave={() => setHoveredButton(null)}
                    >
                          <Text style={[
                            venueStyles.viewButtonText,
                            currentView === 'manuals' && { color: '#DBC0FC' },
                            hoveredButton === 'manuals' && venueStyles.viewButtonTextHovered
                          ]}>LINKS</Text>
                    </TouchableOpacity>

                    <TouchableOpacity 
                      style={[
                            venueStyles.viewButton,
                            currentView === 'discussion' && { backgroundColor: '#3D7FE6' },
                            hoveredButton === 'discussion' && venueStyles.viewButtonHovered
                      ]}
                          onPress={() => setCurrentView('discussion')}
                          onMouseEnter={() => setHoveredButton('discussion')}
                          onMouseLeave={() => setHoveredButton(null)}
                    >
                          <Text style={[
                            venueStyles.viewButtonText,
                            currentView === 'discussion' && { color: '#DBC0FC' },
                            hoveredButton === 'discussion' && venueStyles.viewButtonTextHovered
                          ]}>DISCUSSION BOARD</Text>
                    </TouchableOpacity>
                  </View>
                    </View>
                  </View>
                )} */}
                    </View>
                  </View>
                  <View style={[venueStyles.venueGridCol3, { minHeight: 0 }]}>
                    <View style={{ paddingTop: 20 }}>
                      {renderMapCard()}
                      {chatroomOpen && (
                        <View id="venue-community-section" style={venueStyles.discussionsAndUploadsContainer}>
                          <View style={[venueStyles.discussionAndUploadsBox, isDarkMode && { backgroundColor: theme.background.tertiary, borderColor: theme.ui.borderStrong }]}>
                            {activeDiscussionTab === 'discussions' ? (
                              <DiscussionBoard
                                venueId={venueId}
                                discussions={discussionData?.discussions || []}
                                reports={discussionData?.reports || []}
                                textStyle={[venueStyles.discussionText, isDarkMode && { color: theme.text.primary }]}
                                inputStyle={[venueStyles.discussionInput, isDarkMode && { color: theme.input.text, backgroundColor: theme.input.background, borderColor: theme.input.border }]}
                                placeholderStyle={[venueStyles.discussionPlaceholder, isDarkMode && { color: theme.input.placeholder }]}
                                containerStyle={venueStyles.discussionSection}
                              />
                            ) : activeDiscussionTab === 'uploads' ? renderMedia() : renderRoomEqTab()}
                          </View>
                          <View style={venueStyles.discussionsAndUploadsTabs}>
                            <TouchableOpacity
                              style={[venueStyles.discussionsAndUploadsTab, isDarkMode && { backgroundColor: theme.background.tertiary }]}
                              onPress={() => setActiveDiscussionTab('discussions')}
                            >
                              <Text style={[venueStyles.discussionsAndUploadsTabText, { fontSize: activeDiscussionTab === 'discussions' ? 20 : 14 }, isDarkMode && { color: theme.text.primary }]}>NOTES</Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                              style={[venueStyles.discussionsAndUploadsTab, isDarkMode && { backgroundColor: theme.background.tertiary }]}
                              onPress={() => setActiveDiscussionTab('uploads')}
                            >
                              <Text style={[venueStyles.discussionsAndUploadsTabText, { fontSize: activeDiscussionTab === 'uploads' ? 20 : 14 }, isDarkMode && { color: theme.text.primary }]}>UPLOADS</Text>
                            </TouchableOpacity>
                            {/* <TouchableOpacity
                              style={[venueStyles.discussionsAndUploadsTab, isDarkMode && { backgroundColor: theme.background.tertiary }]}
                              onPress={() => setActiveDiscussionTab('eq')}
                            >
                              <Text style={[venueStyles.discussionsAndUploadsTabText, { fontSize: activeDiscussionTab === 'eq' ? 20 : 14 }, isDarkMode && { color: theme.text.primary }]}>EQ</Text>
                            </TouchableOpacity> */}
                          </View>
                        </View>
                      )}
                      <View style={[venueStyles.chatContainer, { flex: 1, width: '100%' }]}>
                        <View style={venueStyles.chatContainerHeader}>
                          <TouchableOpacity
                            onPress={() => setChatroomOpen((prev) => !prev)}
                            style={venueStyles.chatroomToggleButton}
                            activeOpacity={0.8}
                          >
                            <Text style={venueStyles.chatroomToggleButtonText}>
                              {chatroomOpen ? 'Chatroom ▲' : 'Chatroom ▼'}
                            </Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    </View>
                  </View>
                </View>
              </View>
            ) : (
              <>
                <View style={venueStyles.headerStyle}>
                  <View id="title-card" style={venueStyles.titleCard}>
                    <View style={venueStyles.titleCardFolderIconWrapper}>
                      <TouchableOpacity
                        onPress={handleFolderIconPress}
                        style={venueStyles.titleCardFolderIcon}
                        activeOpacity={0.8}
                      >
                        <Image
                          source={require('../../assets/icons/folder-modified.png')}
                          style={venueStyles.titleCardFolderIconImage}
                          resizeMode="contain"
                        />
                      </TouchableOpacity>
                      {folderDropdownOpen && (
                        <>
                          <TouchableWithoutFeedback onPress={() => setFolderDropdownOpen(false)}>
                            <View style={[StyleSheet.absoluteFill, { top: -3000, left: -3000, width: 6000, height: 6000, zIndex: 15 }]} />
                          </TouchableWithoutFeedback>
                          <View style={venueStyles.folderDropdown}>
                            <TouchableOpacity style={venueStyles.folderDropdownOption} onPress={handleAddToNewTour} activeOpacity={0.7}>
                              <Text style={venueStyles.folderDropdownOptionText}>Add to New Tour</Text>
                            </TouchableOpacity>
                            <TouchableOpacity style={venueStyles.folderDropdownOption} onPress={() => { setFolderDropdownOpen(false); handleAddToFolder(); }} activeOpacity={0.7}>
                              <Text style={venueStyles.folderDropdownOptionText}>Add to Existing Tour</Text>
                            </TouchableOpacity>
                            <TouchableOpacity style={venueStyles.folderDropdownOption} onPress={() => { setFolderDropdownOpen(false); setShowRoomEqModal(true); }} activeOpacity={0.7}>
                              <Text style={venueStyles.folderDropdownOptionText}>Save Room Info</Text>
                            </TouchableOpacity>
                            <TouchableOpacity style={venueStyles.folderDropdownOption} onPress={handleSaveVenue} activeOpacity={0.7}>
                              <Text style={venueStyles.folderDropdownOptionText}>Save Venue</Text>
                            </TouchableOpacity>
                          </View>
                        </>
                      )}
                    </View>
                    <View style={{
                      width: '100%',
                      display: 'flex',
                      marginBottom: 10,
                      flexDirection: 'row',
                      justifyContent: 'flex-start',
                      alignItems: 'flex-start',
                      gap: 20,
                      padding: 0
                    }}>
                    </View>
                    <View style={venueStyles.mobileTitleCard}>
                      <View style={venueStyles.venueNameContainer}>
                        <Text style={[venueStyles.venueName, getVenueTextStyle('venueName'), { fontWeight: '500' }]}>{venueData?.name || 'Venue Name'}</Text>
                      </View>
                      <Text style={[venueStyles.venueAddress, getVenueTextStyle('venueAddress')]}>
                        {venueData?.city}, {venueData?.state}
                      </Text>
                      {venueData?.website && (
                        <Text
                          style={[venueStyles.venueWebsite, getVenueTextStyle('website')]}
                          onPress={() => window.open(venueData.website, '_blank')}
                        >
                          {venueData.website}
                        </Text>
                      )}
                      {affiliateTokens.length > 0 && (
                        <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', gap: 6 }}>
                          {affiliateTokens.map((aff, idx) => (
                            <TouchableOpacity key={`m-${aff}-${idx}`} onPress={() => handleAffiliatePress(aff)}>
                              <Text style={[venueStyles.affiliateInfo, getVenueTextStyle('affiliate'), { textAlign: 'center' }]}>
                                {aff}
                                {idx < affiliateTokens.length - 1 ? ',' : ''}
                              </Text>
                            </TouchableOpacity>
                          ))}
                        </View>
                      )}
                      {approvedVenueInterestItems.length > 0 && (
                        <View style={{ alignItems: 'center', marginTop: 14, marginBottom: 2, zIndex: 50 }}>
                          <CardInterestsDisplay
                            items={approvedVenueInterestItems}
                            size="large"
                            popoverPlacement="above"
                            popoverAlign="center"
                          />
                        </View>
                      )}
                      {renderFeatureBadges()}
                    </View>
                    {isMobile && (
                      <View style={[venueStyles.overallGradeCard, { padding: 0 }]}>
                        <ReportCardChalkboard
                          grade={getLetterGrade(venueData?.approved?.overallGrade)}
                          reportCount={publicVenueReports.length}
                          onOpenReports={() => setShowReportsModal(true)}
                          onSubmitReport={handleSubmitReport}
                        />
                      </View>
                    )}
                    {isMobile && renderMapCard()}
                    {mobileOptionsToggle !== null && (
                      <TouchableWithoutFeedback
                        onPress={() => {
                          LayoutAnimation.configureNext(LayoutAnimation.create(200, LayoutAnimation.Types.easeInEaseOut, LayoutAnimation.Properties.opacity));
                          setMobileOptionsToggle(null);
                        }}
                      >
                        <View style={[StyleSheet.absoluteFill, { zIndex: 50, backgroundColor: 'transparent' }]} />
                      </TouchableWithoutFeedback>
                    )}
                    </View>
                  </View>
                  <View style={venueStyles.headerStyle}>
                  {/* <View style={venueStyles.leftColumnFiller}></View> */}
                  <View style={venueStyles.venueLayout}>
                    {renderActiveSection()}
                  </View>
                  <View
                    style={[
                      venueStyles.chatContainer,
                      {
                        zIndex: 200,
                      }
                    ]}
                  >
                    <View style={venueStyles.chatContainerHeader}>
                      <TouchableOpacity
                        onPress={() => setChatroomOpen((prev) => !prev)}
                        style={venueStyles.chatroomToggleButton}
                        activeOpacity={0.8}
                      >
                        <Text style={venueStyles.chatroomToggleButtonText}>
                          {chatroomOpen ? 'Chatroom ▲' : 'Chatroom ▼'}
                        </Text>
                      </TouchableOpacity>
                    </View>
                    {chatroomOpen && (
                      <View id="venue-community-section" style={venueStyles.discussionsAndUploadsContainer}>
                        <View style={[venueStyles.discussionAndUploadsBox, isDarkMode && { backgroundColor: theme.background.tertiary, borderColor: theme.ui.borderStrong }]}>
                          {activeDiscussionTab === 'discussions' ? (
                            <DiscussionBoard
                              venueId={venueId}
                              discussions={discussionData?.discussions || []}
                              reports={discussionData?.reports || []}
                              textStyle={[venueStyles.discussionText, isDarkMode && { color: theme.text.primary }]}
                              inputStyle={[venueStyles.discussionInput, isDarkMode && { color: theme.input.text, backgroundColor: theme.input.background, borderColor: theme.input.border }]}
                              placeholderStyle={[venueStyles.discussionPlaceholder, isDarkMode && { color: theme.input.placeholder }]}
                              containerStyle={venueStyles.discussionSection}
                            />
                          ) : activeDiscussionTab === 'uploads' ? renderMedia() : renderRoomEqTab()}
                        </View>
                        <View style={venueStyles.discussionsAndUploadsTabs}>
                          <TouchableOpacity
                            style={[venueStyles.discussionsAndUploadsTab, isDarkMode && { backgroundColor: theme.background.tertiary }]}
                            onPress={() => setActiveDiscussionTab('discussions')}
                          >
                            <Text style={[venueStyles.discussionsAndUploadsTabText, { fontSize: activeDiscussionTab === 'discussions' ? 20 : 14 }, isDarkMode && { color: theme.text.primary }]}>NOTES</Text>
                          </TouchableOpacity>
                          <TouchableOpacity
                            style={[venueStyles.discussionsAndUploadsTab, isDarkMode && { backgroundColor: theme.background.tertiary }]}
                            onPress={() => setActiveDiscussionTab('uploads')}
                          >
                            <Text style={[venueStyles.discussionsAndUploadsTabText, { fontSize: activeDiscussionTab === 'uploads' ? 20 : 14 }, isDarkMode && { color: theme.text.primary }]}>UPLOADS</Text>
                          </TouchableOpacity>
                        </View>
                      </View>
                    )}
                  </View>
                </View>
              </>
            )}
            {/* Voting Section - Moved outside the layout container */}
            <View style={venueStyles.votingContainer}>
              <Text style={[venueStyles.votingText, { color: venueAccentColor }]}>Is everything correct?</Text>

              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <TouchableOpacity
                  onPress={() => handleVote('venue', 'up')}
                  style={[venueStyles.voteButton, {
                    marginRight: 15,
                    opacity: userVote === 'up' ? 1 : 0.7,
                    flexDirection: 'row',
                    alignItems: 'center'
                  }]}
                >
                  <Ionicons
                    name={userVote === 'up' ? 'thumbs-up' : 'thumbs-up-outline'}
                    size={24}
                    color={venueAccentColor}
                  />
                  <Text style={[venueStyles.voteCount, { color: venueAccentColor }]}>{votes.up}</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  onPress={() => handleVote('venue', 'down')}
                  style={[venueStyles.voteButton, {
                    opacity: userVote === 'down' ? 1 : 0.7,
                    flexDirection: 'row',
                    alignItems: 'center'
                  }]}
                >
                  <Ionicons
                    name={userVote === 'down' ? 'thumbs-down' : 'thumbs-down-outline'}
                    size={24}
                    color={venueAccentColor}
                  />
                  <Text style={[venueStyles.voteCount, { color: venueAccentColor }]}>{votes.down}</Text>
                </TouchableOpacity>
              </View>
            </View>
            <View style={[venueStyles.votingContainer, { marginTop: -40, flexWrap: 'wrap' }]}>
              <Text style={[venueStyles.votingText, { color: venueAccentColor }]}>Archive Venue:</Text>
              {Object.entries(VENUE_ARCHIVE_REASON_LABELS).map(([reason, label]) => (
                <TouchableOpacity
                  key={reason}
                  onPress={() => setVenueArchiveReason(reason)}
                  onMouseEnter={() => setHoveredArchiveReason(reason)}
                  onMouseLeave={() => setHoveredArchiveReason(null)}
                  style={{ marginRight: 20 }}
                  accessibilityRole="link"
                >
                  <Text
                    style={[
                      venueStyles.votingText,
                      { color: venueAccentColor, marginRight: 0 },
                      hoveredArchiveReason === reason && { textDecorationLine: 'underline' },
                    ]}
                  >
                    {label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </View>
      </View>

      {/* Share EQ modal: tabs EQ | RF | Add Tab */}
      <Modal
        visible={showRoomEqModal}
        transparent
        animationType="fade"
        onRequestClose={() => { setShowRoomEqModal(false); setShareEqActiveTab('EQ'); setEditSubmissionId(null); setRoomEqIsPublic(false); }}
      >
        <TouchableWithoutFeedback onPress={() => { setShowRoomEqModal(false); setShareEqActiveTab('EQ'); setEditSubmissionId(null); setRoomEqIsPublic(false); }}>
          <View style={venueStyles.roomEqMacOverlay}>
            <TouchableWithoutFeedback onPress={() => {}}>
              <View style={venueStyles.roomEqMacWindowWrap}>
                <View style={venueStyles.roomEqMacWindow}>
                  {/* <View style={venueStyles.roomEqMacTitleBar}>
                    <View style={venueStyles.roomEqMacTraffic}>
                      <TouchableOpacity onPress={() => { setShowRoomEqModal(false); setShareEqActiveTab('EQ'); setEditSubmissionId(null); setRoomEqIsPublic(false); }}>
                        <View style={[venueStyles.roomEqMacDot, { backgroundColor: '#ff5f57' }]} />
                      </TouchableOpacity>
                      <View style={[venueStyles.roomEqMacDot, { backgroundColor: '#febc2e' }]} />
                      <View style={[venueStyles.roomEqMacDot, { backgroundColor: '#28c840' }]} />
                    </View>
                    <Text style={[venueStyles.roomEqMacTitleText, { flex: 1, marginLeft: 8, color: '#000', fontSize: 24, fontFamily: 'Computer Says No' }]}>Share EQ</Text>
                    <TouchableOpacity onPress={() => { setShowRoomEqModal(false); setShareEqActiveTab('EQ'); setEditSubmissionId(null); setRoomEqIsPublic(false); }} style={{ padding: 4 }}>
                      <Ionicons name="close" size={22} color="#000" />
                    </TouchableOpacity>
                  </View> */}
                  <View style={{ flexDirection: 'row', paddingHorizontal: 12, justifyContent: 'center', gap: 10 }}>
                    <TouchableOpacity onPress={() => setShareEqActiveTab('EQ')} style={{ paddingVertical: 12, paddingHorizontal: 16, borderBottomWidth: 2, borderBottomColor: shareEqActiveTab === 'EQ' ? '#000' : 'transparent', marginBottom: -1 }}>
                      <Text style={{ fontSize: 32, fontWeight: shareEqActiveTab === 'EQ' ? '600' : '400', color: '#000', fontFamily: 'Computer Says No' }}>EQ</Text>
                    </TouchableOpacity>
                    <TouchableOpacity onPress={() => setShareEqActiveTab('RF')} style={{ paddingVertical: 12, paddingHorizontal: 16, borderBottomWidth: 2, borderBottomColor: shareEqActiveTab === 'RF' ? '#000' : 'transparent', marginBottom: -1 }}>
                      <Text style={{ fontSize: 32, fontWeight: shareEqActiveTab === 'RF' ? '600' : '400', color: '#000', fontFamily: 'Computer Says No' }}>RF</Text>
                    </TouchableOpacity>
                    <TouchableOpacity onPress={() => setShareEqActiveTab('ADD')} style={{ paddingVertical: 12, paddingHorizontal: 16, borderBottomWidth: 2, borderBottomColor: shareEqActiveTab === 'ADD' ? '#000' : 'transparent', marginBottom: -1 }}>
                      <Text style={{ fontSize: 32, fontWeight: shareEqActiveTab === 'ADD' ? '600' : '400', color: '#000', fontFamily: 'Computer Says No' }}>{addTabConfirmed && addTabTitle ? addTabTitle : 'Add Tab'}</Text>
                    </TouchableOpacity>
                  </View>
                  <View style={[venueStyles.roomEqMacContent, { flex: 1, minHeight: 0, flexDirection: 'column', overflow: 'hidden' }]}>
                    {/* All tabs always mounted so state is preserved when switching */}
                    <View style={{ flex: 1, minHeight: 0, display: shareEqActiveTab === 'EQ' ? 'flex' : 'none' }}>
                      <RoomEqForm
                        ref={roomEqFormRef}
                        visible={showRoomEqModal}
                        onClose={() => { setShowRoomEqModal(false); setShareEqActiveTab('EQ'); setEditSubmissionId(null); setRoomEqIsPublic(false); }}
                        venueId={venueId}
                        onSaved={() => { setShowRoomEqModal(false); setShareEqActiveTab('EQ'); loadPublicRoomEqs(); }}
                        embedded
                        hideFooter
                        uploaderDisplayName={userData?.displayName || 'Anonymous'}
                      />
                    </View>
                    <View style={{ flex: 1, minHeight: 0, display: shareEqActiveTab === 'RF' ? 'flex' : 'none' }}>
                      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 16, paddingBottom: 24 }}>
                        <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginHorizontal: -6 }}>
                          {rfTextItems.map((value, index) => (
                            <View key={index} style={{ width: '50%', padding: 6 }}>
                              <TextInput
                                placeholder={"Add..."}
                                placeholderTextColor="#444"
                                value={value}
                                onChangeText={(t) => setRfTextItems((prev) => { const next = [...prev]; next[index] = t; return next; })}
                                style={{ borderWidth: 1, borderColor: '#000', borderRadius: 8, padding: 12, fontSize: 20, color: '#000', minHeight: 40, textAlignVertical: 'top', fontFamily: 'Computer Says No' }}
                                multiline
                              />
                            </View>
                          ))}
                        </View>
                        <TouchableOpacity onPress={() => setRfTextItems((prev) => [...prev, ''])} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 8, marginBottom: 20 }}>
                          <Ionicons name="add-circle-outline" size={28} color="#000" />
                          <Text style={{ fontSize: 20, color: '#000', fontFamily: 'Computer Says No' }}>Add field</Text>
                        </TouchableOpacity>
                        <View style={{ borderTopWidth: 1, borderTopColor: '#000', paddingTop: 16 }}>
                          <Text style={{ fontSize: 20, color: '#000', marginBottom: 8, fontFamily: 'Computer Says No' }}>Photos</Text>
                          <PhotoSlotsUpload photos={rfPhotos} onPhotosChange={setRfPhotos} maxPhotos={5} />
                        </View>
                      </ScrollView>
                    </View>
                    <View style={{ flex: 1, minHeight: 0, display: shareEqActiveTab === 'ADD' ? 'flex' : 'none' }}>
                      {!addTabConfirmed ? (
                        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 16, paddingBottom: 24 }}>
                          <Text style={{ fontSize: 20, color: '#000', marginBottom: 8, fontFamily: 'Computer Says No' }}>Tab title</Text>
                          <TextInput value={addTabTitle} onChangeText={setAddTabTitle} placeholder="Name of the tab" placeholderTextColor="#444" style={{ borderWidth: 1, borderColor: '#000', borderRadius: 8, padding: 12, fontSize: 20, color: '#000', fontFamily: 'Computer Says No', marginBottom: 20 }} />
                          <Text style={{ fontSize: 20, color: '#000', marginBottom: 12, fontFamily: 'Computer Says No' }}>Which layout works best for this?</Text>
                          <View style={{ flexDirection: 'row', gap: 12, marginBottom: 24 }}>
                            <TouchableOpacity onPress={() => setAddTabType('EQ')} style={{ flex: 1, paddingVertical: 14, paddingHorizontal: 16, borderWidth: 2, borderColor: addTabType === 'EQ' ? '#000' : '#ccc', borderRadius: 8, alignItems: 'center', backgroundColor: addTabType === 'EQ' ? '#f0f0f0' : 'transparent' }}>
                              <Text style={{ fontSize: 20, color: '#000', fontFamily: 'Computer Says No' }}>EQ</Text>
                            </TouchableOpacity>
                            <TouchableOpacity onPress={() => setAddTabType('RF')} style={{ flex: 1, paddingVertical: 14, paddingHorizontal: 16, borderWidth: 2, borderColor: addTabType === 'RF' ? '#000' : '#ccc', borderRadius: 8, alignItems: 'center', backgroundColor: addTabType === 'RF' ? '#f0f0f0' : 'transparent' }}>
                              <Text style={{ fontSize: 20, color: '#000', fontFamily: 'Computer Says No' }}>RF</Text>
                            </TouchableOpacity>
                          </View>
                          <TouchableOpacity onPress={() => (addTabTitle.trim() && addTabType) && setAddTabConfirmed(true)} disabled={!addTabTitle.trim() || !addTabType} style={{ paddingVertical: 12, paddingHorizontal: 24, backgroundColor: (addTabTitle.trim() && addTabType) ? '#000' : '#999', borderRadius: 8, alignItems: 'center' }}>
                            <Text style={{ fontSize: 20, color: '#fff', fontFamily: 'Computer Says No' }}>Proceed</Text>
                          </TouchableOpacity>
                        </ScrollView>
                      ) : addTabType === 'EQ' ? (
                        <RoomEqForm ref={addTabEqFormRef} key="addTabEq" visible={showRoomEqModal} onClose={() => { setShowRoomEqModal(false); setShareEqActiveTab('EQ'); setEditSubmissionId(null); setRoomEqIsPublic(false); }} venueId={venueId} onSaved={() => { setShowRoomEqModal(false); setShareEqActiveTab('EQ'); loadPublicRoomEqs(); }} embedded hideFooter saveTitle={addTabTitle} uploaderDisplayName={userData?.displayName || 'Anonymous'} />
                      ) : (
                        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 16, paddingBottom: 24 }}>
                          <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginHorizontal: -6 }}>
                            {addTabRfTextItems.map((value, index) => (
                              <View key={index} style={{ width: '50%', padding: 6 }}>
                                <TextInput placeholder=" " placeholderTextColor="#444" value={value} onChangeText={(t) => setAddTabRfTextItems((prev) => { const next = [...prev]; next[index] = t; return next; })} style={{ borderWidth: 1, borderColor: '#000', borderRadius: 8, padding: 12, fontSize: 20, color: '#000', minHeight: 40, textAlignVertical: 'top', fontFamily: 'Computer Says No' }} multiline />
                              </View>
                            ))}
                          </View>
                          <TouchableOpacity onPress={() => setAddTabRfTextItems((prev) => [...prev, ''])} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 8, marginBottom: 20 }}>
                            <Ionicons name="add-circle-outline" size={28} color="#000" />
                            <Text style={{ fontSize: 20, color: '#000', fontFamily: 'Computer Says No' }}>Add field</Text>
                          </TouchableOpacity>
                          <View style={{ borderTopWidth: 1, borderTopColor: '#000', paddingTop: 16 }}>
                            <Text style={{ fontSize: 20, color: '#000', marginBottom: 8, fontFamily: 'Computer Says No' }}>Photos</Text>
                            <PhotoSlotsUpload photos={addTabRfPhotos} onPhotosChange={setAddTabRfPhotos} maxPhotos={5} />
                          </View>
                        </ScrollView>
                      )}
                    </View>
                  </View>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 16, borderTopWidth: 1, borderTopColor: '#000' }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                      <Text style={{ fontSize: 18, color: '#000', fontFamily: 'Computer Says No' }}>Show publicly on venue page</Text>
                      <TouchableOpacity
                        style={{
                          width: getResponsiveValue({
                            mobile: 40,
                            tablet: 44,
                            laptop: 48,
                            desktop: 48
                          }),
                          height: getResponsiveValue({
                            mobile: 22,
                            tablet: 24,
                            laptop: 26,
                            desktop: 26
                          }),
                          borderRadius: getResponsiveValue({
                            mobile: 11,
                            tablet: 12,
                            laptop: 13,
                            desktop: 13,
                          }),
                          backgroundColor: roomEqIsPublic ? '#000' : '#ccc',
                          padding: 2,
                          justifyContent: 'center',
                        }}
                        onPress={() => setRoomEqIsPublic(prev => !prev)}
                      >
                        <View
                          style={{
                            width: getResponsiveValue({
                              mobile: 18,
                              tablet: 20,
                              laptop: 22,
                              desktop: 22
                            }),
                            height: getResponsiveValue({
                              mobile: 18,
                              tablet: 20,
                              laptop: 22,
                              desktop: 22
                            }),
                            borderRadius: getResponsiveValue({
                              mobile: 9,
                              tablet: 10,
                              laptop: 11,
                              desktop: 11,
                            }),
                            backgroundColor: '#fff',
                            alignSelf: roomEqIsPublic ? 'flex-end' : 'flex-start',
                          }}
                        />
                      </TouchableOpacity>
                    </View>
                    <View style={{ flexDirection: 'row', gap: getResponsiveValue({ mobile: 8, tablet: 12, laptop: 12, desktop: 12 }) }}>
                      <TouchableOpacity onPress={() => { setShowRoomEqModal(false); setShareEqActiveTab('EQ'); setEditSubmissionId(null); setRoomEqIsPublic(false); }} style={{ paddingVertical: 10, paddingHorizontal: getResponsiveValue({ mobile: 12, tablet: 16, laptop: 16, desktop: 16 }) }}>
                        <Text style={{ fontSize: 20, color: '#000', fontFamily: 'Computer Says No' }}>Cancel</Text>
                      </TouchableOpacity>
                      <TouchableOpacity onPress={saveAllRoomEq} disabled={saveAllLoading} style={{ paddingVertical: 10, paddingHorizontal: getResponsiveValue({ mobile: 12, tablet: 16, laptop: 16, desktop: 16 }), backgroundColor: '#000', borderRadius: 8, minWidth: 80, alignItems: 'center', flexDirection: 'row', gap: 6 }}>
                        {saveAllLoading ? <ActivityIndicator size="small" color="#fff" /> : null}
                        <Text style={{ fontSize: 20, color: '#fff', fontFamily: 'Computer Says No' }}>{saveAllLoading ? 'Saving…' : 'Save'}</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                </View>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>

      {/* Venue reports viewer (all approved) */}
      <Modal
        visible={showReportsModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowReportsModal(false)}
      >
        <TouchableWithoutFeedback onPress={() => setShowReportsModal(false)}>
          <View style={venueStyles.roomEqMacOverlay}>
            <TouchableWithoutFeedback onPress={() => {}}>
              <View style={venueStyles.roomEqMacWindowWrap}>
                <View style={[venueStyles.roomEqMacWindow, { backgroundColor: '#208c6a' }]}>
                  <ScrollView
                    style={{ flex: 1 }}
                    contentContainerStyle={{ padding: 20, paddingBottom: 24 }}
                  >
                    {publicVenueReports.length === 0 ? (
                      <Text style={{ color: '#ffffff', fontSize: 22, textAlign: 'center', fontFamily: 'VastagoGrotesk' }}>
                        No public reports yet for this venue.
                      </Text>
                    ) : (
                      publicVenueReports.map((report) => {
                        const userNorm = normalizeVenueReportUserType(report.userType);
                        const rawLower = (report.userType || '').toLowerCase();
                        const prettyType =
                          report.isTechSpecsOnly || rawLower === 'technical'
                            ? 'Technical specifications'
                            : userNorm === 'artist'
                              ? 'Artist'
                              : userNorm === 'touring'
                                ? 'Touring crew'
                                : userNorm === 'employee'
                                  ? 'Employee'
                                  : userNorm === 'other'
                                    ? 'Other'
                                    : 'Report';
                        const dateLabel =
                          (userNorm === 'artist' && report.dateOfPerformance) ||
                          (userNorm === 'employee' && report.datesWorked) ||
                          report.dateWorked ||
                          (report.createdAt?.toDate
                            ? report.createdAt.toDate().toLocaleDateString()
                            : '');

                        const hasTechSpecs = VENUE_REPORT_TECH_SPEC_FIELDS.some(({ field }) => {
                          const v = report[field];
                          if (v === undefined || v === null || v === '') return false;
                          if (isArtistBackstageGradeNotTechText(userNorm, field, v)) return false;
                          return true;
                        });
                        const ratingFields = [
                          ...getVenueReportRatingFields(userNorm),
                          ...extraGradeDisplayFields(report)
                            .filter((line) => line.value)
                            .map((line) => ({
                              label: line.label,
                              field: line.field,
                              value: line.value,
                            })),
                        ];
                        const textDetailFields = getVenueReportTextDetailFields(userNorm);
                        const isTechOnly = report.isTechSpecsOnly === true || rawLower === 'technical';

                        const gradeColorForLetter = (letter) =>
                          letter === 'A'
                            ? '#1f8a4d'
                            : letter === 'B'
                              ? '#4caf50'
                              : letter === 'C'
                                ? '#f39c12'
                                : '#e53935';

                        return (
                          <View
                            key={report.id}
                            style={{
                              backgroundColor: '#f8fff8',
                              borderRadius: 12,
                              padding: 16,
                              marginBottom: 16,
                            }}
                          >
                            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 }}>
                              <Text style={{ fontFamily: 'Computer Says No', fontSize: 26, color: '#208c6a' }}>
                                {prettyType}
                              </Text>
                              {dateLabel ? (
                                <Text style={{ fontFamily: 'Computer Says No', fontSize: 20, color: '#555' }}>
                                  {dateLabel}
                                </Text>
                              ) : null}
                            </View>

                            {isTechOnly ? (
                              <>
                                <View style={{ marginTop: 4, marginBottom: 8 }}>
                                  <Text style={{ fontFamily: 'Computer Says No', fontSize: 26, color: '#333', marginBottom: 6 }}>
                                    Technical specifications
                                  </Text>
                                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginHorizontal: -4 }}>
                                    {VENUE_REPORT_TECH_SPEC_FIELDS.map(({ label, field }) => {
                                      const v = report[field];
                                      if (v === undefined || v === null || v === '') return null;
                                      if (isArtistBackstageGradeNotTechText(userNorm, field, v)) return null;
                                      return (
                                        <View
                                          key={field}
                                          style={{ width: '50%', paddingHorizontal: 4, marginBottom: 10 }}
                                        >
                                          <Text style={{ fontFamily: 'Computer Says No', fontSize: 22, color: '#555' }}>
                                            {label}
                                          </Text>
                                          <Text
                                            style={{
                                              fontFamily: 'Computer Says No',
                                              fontSize: 25,
                                              color: '#111',
                                              lineHeight: 32,
                                            }}
                                          >
                                            {String(v)}
                                          </Text>
                                        </View>
                                      );
                                    })}
                                  </View>
                                </View>
                              </>
                            ) : (
                              <>
                                {textDetailFields.map(({ label, field }) => {
                                  const raw = report[field];
                                  const text = formatReportDetailValue(raw);
                                  if (!text) return null;
                                  return (
                                    <View key={field} style={{ marginBottom: 10 }}>
                                      <Text style={{ fontFamily: 'Computer Says No', fontSize: 22, color: '#555' }}>
                                        {label}
                                      </Text>
                                      <Text
                                        style={{
                                          fontFamily: 'Computer Says No',
                                          fontSize: 25,
                                          color: '#111',
                                          lineHeight: 32,
                                        }}
                                      >
                                        {text}
                                      </Text>
                                    </View>
                                  );
                                })}
                                {userNorm === 'employee' &&
                                (report.isUnionHouse === true || report.isUnionHouse === false) ? (
                                  <View style={{ marginBottom: 10 }}>
                                    <Text style={{ fontFamily: 'Computer Says No', fontSize: 22, color: '#555' }}>
                                      Union house
                                    </Text>
                                    <Text
                                      style={{
                                        fontFamily: 'Computer Says No',
                                        fontSize: 25,
                                        color: '#111',
                                        lineHeight: 32,
                                      }}
                                    >
                                      {report.isUnionHouse ? 'Yes' : 'No'}
                                    </Text>
                                  </View>
                                ) : null}

                                {report.overallExperience ? (
                                  <View style={{ marginTop: 6, marginBottom: 8 }}>
                                    <Text
                                      style={{
                                        fontFamily: 'Computer Says No',
                                        fontSize: 26,
                                        color: '#333',
                                        marginBottom: 6,
                                      }}
                                    >
                                      Overall experience
                                    </Text>
                                    <Text
                                      style={{
                                        fontFamily: 'Computer Says No',
                                        fontSize: 25,
                                        color: '#111',
                                        lineHeight: 34,
                                      }}
                                    >
                                      {report.overallExperience}
                                    </Text>
                                  </View>
                                ) : null}

                                {ratingFields.length > 0 ? (
                                  <View style={{ marginTop: 4, marginBottom: 8 }}>
                                    <Text
                                      style={{
                                        fontFamily: 'Computer Says No',
                                        fontSize: 26,
                                        color: '#333',
                                        marginBottom: 6,
                                      }}
                                    >
                                      Ratings
                                    </Text>
                                    <View
                                      style={{
                                        flexDirection: 'row',
                                        flexWrap: 'wrap',
                                        marginHorizontal: -4,
                                      }}
                                    >
                                      {ratingFields.map(({ label, field, value }) => {
                                        const raw = value != null && value !== ''
                                          ? value
                                          : getVenueReportRatingValue(report, field);
                                        const letter = getLetterGrade(raw);
                                        const gradeColor = letter ? gradeColorForLetter(letter) : '#999';
                                        return (
                                          <View
                                            key={field}
                                            style={{
                                              width: '50%',
                                              paddingHorizontal: 4,
                                              marginBottom: 10,
                                            }}
                                          >
                                            <Text style={{ fontFamily: 'Computer Says No', fontSize: 21, color: '#555' }}>
                                              {label}
                                            </Text>
                                            <Text
                                              style={{ fontFamily: 'Computer Says No', fontSize: 30, color: gradeColor }}
                                            >
                                              {letter || '—'}
                                            </Text>
                                          </View>
                                        );
                                      })}
                                    </View>
                                  </View>
                                ) : null}

                                {hasTechSpecs ? (
                                  <View style={{ marginTop: 4, marginBottom: 8 }}>
                                    <Text
                                      style={{
                                        fontFamily: 'Computer Says No',
                                        fontSize: 26,
                                        color: '#333',
                                        marginBottom: 6,
                                      }}
                                    >
                                      Technical details
                                    </Text>
                                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginHorizontal: -4 }}>
                                      {VENUE_REPORT_TECH_SPEC_FIELDS.map(({ label, field }) => {
                                        const v = report[field];
                                        if (v === undefined || v === null || v === '') return null;
                                        if (isArtistBackstageGradeNotTechText(userNorm, field, v)) return null;
                                        return (
                                          <View
                                            key={field}
                                            style={{ width: '50%', paddingHorizontal: 4, marginBottom: 10 }}
                                          >
                                            <Text style={{ fontFamily: 'Computer Says No', fontSize: 22, color: '#555' }}>
                                              {label}
                                            </Text>
                                            <Text
                                              style={{
                                                fontFamily: 'Computer Says No',
                                                fontSize: 25,
                                                color: '#111',
                                                lineHeight: 32,
                                              }}
                                            >
                                              {String(v)}
                                            </Text>
                                          </View>
                                        );
                                      })}
                                    </View>
                                  </View>
                                ) : null}
                              </>
                            )}

                            {report.notes ? (
                              <View style={{ marginTop: 4 }}>
                                <Text style={{ fontFamily: 'Computer Says No', fontSize: 26, color: '#333', marginBottom: 6 }}>
                                  Notes
                                </Text>
                                <Text
                                  style={{
                                    fontFamily: 'Computer Says No',
                                    fontSize: 25,
                                    color: '#222',
                                    lineHeight: 34,
                                  }}
                                >
                                  {report.notes}
                                </Text>
                              </View>
                            ) : null}

                            {Array.isArray(report.photos) && report.photos.length > 0 ? (
                              <View style={{ marginTop: 12 }}>
                                <Text style={{ fontFamily: 'Computer Says No', fontSize: 26, color: '#333', marginBottom: 8 }}>
                                  Photos
                                </Text>
                                <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                                  {report.photos.map((url, idx) => (
                                    <Image
                                      key={idx}
                                      source={{ uri: url }}
                                      style={{
                                        width: 120,
                                        height: 120,
                                        borderRadius: 8,
                                        marginRight: 10,
                                        backgroundColor: '#e0e0e0',
                                      }}
                                      resizeMode="cover"
                                    />
                                  ))}
                                </ScrollView>
                              </View>
                            ) : null}

                            {Array.isArray(report.videos) && report.videos.length > 0 ? (
                              <View style={{ marginTop: 12 }}>
                                <Text style={{ fontFamily: 'Computer Says No', fontSize: 26, color: '#333', marginBottom: 6 }}>
                                  Videos ({report.videos.length})
                                </Text>
                                {report.videos.map((url, idx) => (
                                  <Text
                                    key={idx}
                                    style={{
                                      fontFamily: 'VastagoGrotesk',
                                      fontSize: 14,
                                      color: '#166fff',
                                      marginBottom: 4,
                                    }}
                                    numberOfLines={2}
                                  >
                                    {url}
                                  </Text>
                                ))}
                              </View>
                            ) : null}

                            {Array.isArray(report.documents) && report.documents.length > 0 ? (
                              <View style={{ marginTop: 12 }}>
                                <Text style={{ fontFamily: 'Computer Says No', fontSize: 26, color: '#333', marginBottom: 6 }}>
                                  Documents ({report.documents.length})
                                </Text>
                                {report.documents.map((url, idx) => (
                                  <Text
                                    key={idx}
                                    style={{
                                      fontFamily: 'VastagoGrotesk',
                                      fontSize: 14,
                                      color: '#166fff',
                                      marginBottom: 4,
                                    }}
                                    numberOfLines={2}
                                  >
                                    {url}
                                  </Text>
                                ))}
                              </View>
                            ) : null}
                          </View>
                        );
                      })
                    )}
                  </ScrollView>
                  <View style={[venueStyles.roomEqMacViewerFooter, { backgroundColor: 'none' }]}>
                    <View style={{ flex: 1 }} />
                    <TouchableOpacity
                      onPress={() => setShowReportsModal(false)}
                      style={{
                        paddingVertical: 6,
                        paddingHorizontal: 18,
                        backgroundColor: '#000000',
                        borderRadius: 999,
                      }}
                    >
                      <Text
                        style={{
                          fontSize: 20,
                          color: '#ffffff',
                          fontFamily: 'Computer Says No',
                        }}
                      >
                        Close
                      </Text>
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>

      {/* Room EQ submission viewer: shared modal with Edit/Delete when we have submission docs */}
      {roomEqViewerVisible && roomEqViewerSubmissions.length > 0 && (
        <RoomEqViewerModal
          visible={roomEqViewerVisible}
          submissions={roomEqViewerSubmissions}
          initialIndex={roomEqViewerSubmissionIndex}
          onClose={() => setRoomEqViewerVisible(false)}
          onDeleteSubmission={handleDeleteRoomEqSubmission}
          onEditSubmission={handleEditRoomEqSubmission}
          onGoToVenue={(sub) => {
            setRoomEqViewerVisible(false);
            if (sub?.venueId) {
              navigation.navigate('Venue', { venueId: sub.venueId });
            }
          }}
        />
      )}

      {/* Room EQ MacBook-style viewer: slide-by-slide only when no submission docs (legacy) */}
      <Modal
        visible={roomEqViewerVisible && roomEqViewerSubmissions.length === 0}
        transparent
        animationType="fade"
        onRequestClose={() => setRoomEqViewerVisible(false)}
      >
        <TouchableWithoutFeedback onPress={() => setRoomEqViewerVisible(false)}>
          <View style={venueStyles.roomEqMacOverlay}>
            <TouchableWithoutFeedback onPress={() => {}}>
              <View style={venueStyles.roomEqMacWindowWrap}>
                <View style={venueStyles.roomEqMacViewerRow}>
                  {roomEqViewerMode === 'slides' ? (
                    <TouchableOpacity
                      style={venueStyles.roomEqMacViewerArrowOuter}
                      onPress={() => {
                        setRoomEqViewerIndex(roomEqViewerIndex > 0 ? roomEqViewerIndex - 1 : roomEqViewerSlides.length - 1);
                      }}
                    >
                      <Ionicons name="chevron-back" size={32} color="#FFFFFF" />
                    </TouchableOpacity>
                  ) : null}
                  <View style={venueStyles.roomEqMacWindow}>
                    <>
                        <View style={venueStyles.roomEqMacContent}>
                          {(() => {
                            const slide = roomEqViewerSlides[roomEqViewerIndex];
                            if (!slide) return null;
                            if (slide.type === 'rf') {
                              const entry = slide.entry;
                              const textItems = entry?.textItems || [];
                              const photos = entry?.photos || [];
                              return (
                                <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 16 }}>
                                  {textItems.length > 0 && (
                                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', marginHorizontal: -6, marginBottom: 12 }}>
                                      {textItems.map((t, i) => (
                                        <View key={i} style={{ width: '50%', padding: 6 }}>
                                          <Text style={{ borderWidth: 1, borderColor: '#000', borderRadius: 8, padding: 12, fontSize: 20, color: '#000', fontFamily: 'Computer Says No', minHeight: 60 }}>{t}</Text>
                                        </View>
                                      ))}
                                    </View>
                                  )}
                                  {photos.map((p, i) => (
                                    <View key={i} style={{ marginBottom: 12 }}>
                                      <RoomEqPhoto uri={p.url} description={p.description} style={{ width: '100%', height: 200, borderRadius: 8 }} />
                                      {p.description ? <Text style={{ fontSize: 20, color: '#000', marginTop: 4, fontFamily: 'Computer Says No' }}>{p.description}</Text> : null}
                                    </View>
                                  ))}
                                </ScrollView>
                              );
                            }
                            const current = slide.photo;
                            if (!current?.url) return null;
                            const isVideo = getRoomEqMediaType(current.url) === 'video';
                            if (isVideo && Platform.OS === 'web') {
                              return (
                                <View style={venueStyles.roomEqMacImageWrap}>
                                  <video src={current.url} style={{ width: '100%', height: 200, maxHeight: 280, objectFit: 'contain' }} controls playsInline />
                                </View>
                              );
                            }
                            if (isVideo && Platform.OS !== 'web') {
                              return (
                                <View style={venueStyles.roomEqMacImageWrap}>
                                  <TouchableOpacity style={venueStyles.roomEqMacVideoPlaceholder} onPress={() => { try { if (typeof window !== 'undefined') window.open(current.url, '_blank'); } catch (_) {} }}>
                                    <Ionicons name="videocam" size={48} color="#666" />
                                    <Text style={venueStyles.roomEqMacVideoPlaceholderText}>Video</Text>
                                    <Text style={venueStyles.roomEqMacVideoPlaceholderLink}>Tap to open</Text>
                                  </TouchableOpacity>
                                </View>
                              );
                            }
                            return (
                              <View style={venueStyles.roomEqMacImageWrap}>
                                <RoomEqPhoto uri={current.url} description={current.description} style={venueStyles.roomEqMacImage} />
                              </View>
                            );
                          })()}
                        </View>
                        <View style={venueStyles.roomEqMacViewerFooter}>
                          <View style={{ flex: 1 }} />
                          <Text style={venueStyles.roomEqMacViewerUploader} numberOfLines={1}>
                            {roomEqViewerSlides[roomEqViewerIndex]?.entry?.uploaderDisplayName || 'Anonymous'}
                          </Text>
                        </View>
                      </>
                  </View>
                  {roomEqViewerMode === 'slides' ? (
                    <TouchableOpacity
                      style={venueStyles.roomEqMacViewerArrowOuter}
                      onPress={() => setRoomEqViewerIndex(roomEqViewerIndex < roomEqViewerSlides.length - 1 ? roomEqViewerIndex + 1 : 0)}
                    >
                      <Ionicons name="chevron-forward" size={32} color="#FFFFFF" />
                    </TouchableOpacity>
                  ) : null}
                </View>
                <TouchableOpacity style={venueStyles.roomEqMacCloseBtn} onPress={() => setRoomEqViewerVisible(false)}>
                  <Text style={venueStyles.roomEqMacCloseText}>Close</Text>
                </TouchableOpacity>
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>

      {/* No Room EQ popup (VIEW -> Room EQ when there are no photos) */}
      <Modal
        visible={showNoRoomEqPopup}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowNoRoomEqPopup(false)}
      >
        <View style={mediaManagerStyles.modalOverlay}>
          <View style={[mediaManagerStyles.modalContent, mediaManagerStyles.successModal]}>
            <Ionicons name="information-circle" size={48} color="#166fff" />
            <Text style={[mediaManagerStyles.successTitle, { color: '#424b54' }]}>Room EQ</Text>
            <Text style={mediaManagerStyles.successMessage}>This venue has no room EQ available.</Text>
            <TouchableOpacity
              onPress={() => setShowNoRoomEqPopup(false)}
              style={[mediaManagerStyles.successButton, { backgroundColor: '#166fff' }]}
            >
              <Text style={mediaManagerStyles.successButtonText}>OK</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Add a Location modal (inline, no TourPlanner navigation) */}
      <Modal
        visible={showAddLocationPanel}
        transparent={true}
        animationType="fade"
        onRequestClose={closeAddLocationPanel}
      >
        <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center', padding: 20 }}>
          <View style={{ width: '100%', maxWidth: 420, maxHeight: '90%', backgroundColor: '#fff', borderRadius: 12, overflow: 'hidden' }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottomWidth: 1, borderBottomColor: '#eee' }}>
              <Text style={{ fontSize: 18, fontWeight: '600', color: '#1d1d1d' }}>Add New Location</Text>
              <TouchableOpacity onPress={closeAddLocationPanel} style={{ padding: 8 }}>
                <Text style={{ fontSize: 18, color: '#666' }}>✕</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={{ padding: 16, maxHeight: 480 }}>
              <View style={{ marginBottom: 16 }}>
                <Text style={{ fontSize: 12, fontWeight: '600', color: '#666', marginBottom: 8, textTransform: 'uppercase' }}>Search address or location</Text>
                <TextInput
                  style={{ backgroundColor: '#f5f5f5', borderRadius: 8, padding: 12, borderWidth: 1, borderColor: '#ddd', color: '#1d1d1d', fontSize: 14 }}
                  value={addressSearchQuery}
                  onChangeText={handleAddressSearchChange}
                  placeholder="Type an address or place (worldwide)..."
                  placeholderTextColor="#999"
                />
                {addressSearchResults.length > 0 && (
                  <ScrollView style={{ marginTop: 4, maxHeight: 180, backgroundColor: '#fff', borderRadius: 8, borderWidth: 1, borderColor: '#ddd' }}>
                    {addressSearchResults.map(result => (
                      <TouchableOpacity key={result.id} onPress={() => selectAddressResult(result)} style={{ padding: 12, borderBottomWidth: 1, borderBottomColor: '#eee' }}>
                        <Text style={{ fontSize: 13, color: '#1d1d1d' }}>{result.name}</Text>
                        <Text style={{ fontSize: 11, color: '#666', marginTop: 2 }} numberOfLines={1}>{result.fullAddress}</Text>
                      </TouchableOpacity>
                    ))}
                  </ScrollView>
                )}
                {isSearchingAddress && <Text style={{ padding: 8, color: '#666', fontSize: 12 }}>Searching...</Text>}
              </View>
              {addLocationCoords && (
                <View style={{ backgroundColor: '#f5f5f5', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                  <Text style={{ fontSize: 12, color: '#666', fontFamily: Platform.OS === 'web' ? 'monospace' : undefined }}>📍 {addLocationCoords.lat.toFixed(5)}, {addLocationCoords.lng.toFixed(5)}</Text>
                </View>
              )}
              <View style={{ marginBottom: 16 }}>
                <Text style={{ fontSize: 12, fontWeight: '600', color: '#666', marginBottom: 8, textTransform: 'uppercase' }}>Name *</Text>
                <TextInput
                  style={{ backgroundColor: '#f5f5f5', borderRadius: 8, padding: 12, borderWidth: 1, borderColor: '#ddd', color: '#1d1d1d', fontSize: 14 }}
                  value={addLocationFormData.name}
                  onChangeText={(text) => setAddLocationFormData(prev => ({ ...prev, name: text }))}
                  placeholder="Enter a name for this location"
                  placeholderTextColor="#999"
                />
              </View>
              <View style={{ marginBottom: 16 }}>
                <Text style={{ fontSize: 12, fontWeight: '600', color: '#666', marginBottom: 8, textTransform: 'uppercase' }}>Category</Text>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                  {LOCATION_CATEGORIES.map(cat => (
                    <TouchableOpacity
                      key={cat.id}
                      onPress={() => setAddLocationFormData(prev => ({ ...prev, category: cat.id }))}
                      style={{
                        flexDirection: 'row', alignItems: 'center', paddingVertical: 8, paddingHorizontal: 12, borderRadius: 20,
                        backgroundColor: addLocationFormData.category === cat.id ? '#00A575' : '#f0f0f0',
                        borderWidth: 1,
                        borderColor: addLocationFormData.category === cat.id ? '#00A575' : '#ddd',
                        gap: 6
                      }}
                    >
                      <Text style={{ fontSize: 14 }}>{cat.icon}</Text>
                      <Text style={{ fontSize: 12, fontWeight: '500', color: addLocationFormData.category === cat.id ? '#fff' : '#666' }}>{cat.label}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
              <View style={{ marginBottom: 16 }}>
                <Text style={{ fontSize: 12, fontWeight: '600', color: '#666', marginBottom: 8, textTransform: 'uppercase' }}>Description</Text>
                <TextInput
                  style={{ backgroundColor: '#f5f5f5', borderRadius: 8, padding: 12, borderWidth: 1, borderColor: '#ddd', color: '#1d1d1d', fontSize: 14, minHeight: 72, textAlignVertical: 'top' }}
                  value={addLocationFormData.notes}
                  onChangeText={(text) => setAddLocationFormData(prev => ({ ...prev, notes: text }))}
                  placeholder="Add a description (optional)"
                  placeholderTextColor="#999"
                  multiline
                  numberOfLines={3}
                />
              </View>
              <View style={{ marginBottom: 16 }}>
                <TouchableOpacity
                  onPress={() => setAddLocationFormData(prev => ({ ...prev, isPublic: !prev.isPublic }))}
                  style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}
                >
                  <View style={{
                    width: 22,
                    height: 22,
                    borderRadius: 4,
                    borderWidth: 2,
                    borderColor: addLocationFormData.isPublic ? '#27ae60' : '#ccc',
                    backgroundColor: addLocationFormData.isPublic ? '#27ae60' : 'transparent',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {addLocationFormData.isPublic && (
                      <Text style={{ color: '#fff', fontSize: 14, fontWeight: 'bold' }}>✓</Text>
                    )}
                  </View>
                  <View>
                    <Text style={{ fontSize: 14, fontWeight: '500', color: '#1d1d1d' }}>Make this location public</Text>
                    <Text style={{ fontSize: 11, color: '#666', marginTop: 2 }}>Other users will be able to see this location</Text>
                  </View>
                </TouchableOpacity>
              </View>
            </ScrollView>
            <View style={{ padding: 16, borderTopWidth: 1, borderTopColor: '#eee', backgroundColor: '#fafafa' }}>
              <TouchableOpacity
                onPress={saveNewLocationInline}
                disabled={!addLocationFormData.name.trim() || !addLocationCoords}
                style={{
                  backgroundColor: (!addLocationFormData.name.trim() || !addLocationCoords) ? '#95a5a6' : '#27ae60',
                  paddingVertical: 14, borderRadius: 8, alignItems: 'center', marginBottom: 10
                }}
              >
                <Text style={{ color: '#fff', fontSize: 15, fontWeight: '600' }}>Save Location</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={closeAddLocationPanel} style={{ paddingVertical: 12, borderRadius: 8, alignItems: 'center', borderWidth: 1, borderColor: '#ddd' }}>
                <Text style={{ color: '#666', fontSize: 14, fontWeight: '500' }}>Cancel</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* No Room EQ popup (MediaManager-style) */}
        <Modal
        visible={showNoRoomEqPopup}
          transparent={true}
          animationType="fade"
        onRequestClose={() => setShowNoRoomEqPopup(false)}
      >
        <View style={mediaManagerStyles.modalOverlay}>
          <View style={[mediaManagerStyles.modalContent, mediaManagerStyles.successModal]}>
            <Ionicons name="information-circle" size={48} color="#166fff" />
            <Text style={[mediaManagerStyles.successTitle, { color: '#424b54' }]}>Room EQ</Text>
            <Text style={mediaManagerStyles.successMessage}>This venue has no room EQ available.</Text>
                <TouchableOpacity
              onPress={() => setShowNoRoomEqPopup(false)}
              style={[mediaManagerStyles.successButton, { backgroundColor: '#166fff' }]}
                >
              <Text style={mediaManagerStyles.successButtonText}>OK</Text>
                </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Add a Location modal (inline, no TourPlanner navigation) */}
      <Modal
        visible={showAddLocationPanel}
        transparent={true}
        animationType="fade"
        onRequestClose={closeAddLocationPanel}
      >
        <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center', padding: 20 }}>
          <View style={{ width: '100%', maxWidth: 420, maxHeight: '90%', backgroundColor: '#fff', borderRadius: 12, overflow: 'hidden' }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottomWidth: 1, borderBottomColor: '#eee' }}>
              <Text style={{ fontSize: 18, fontWeight: '600', color: '#1d1d1d' }}>Add New Location</Text>
              <TouchableOpacity onPress={closeAddLocationPanel} style={{ padding: 8 }}>
                <Text style={{ fontSize: 18, color: '#666' }}>✕</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={{ padding: 16, maxHeight: 480 }}>
              <View style={{ marginBottom: 16 }}>
                <Text style={{ fontSize: 12, fontWeight: '600', color: '#666', marginBottom: 8, textTransform: 'uppercase' }}>Search address or location</Text>
                <TextInput
                  style={{ backgroundColor: '#f5f5f5', borderRadius: 8, padding: 12, borderWidth: 1, borderColor: '#ddd', color: '#1d1d1d', fontSize: 14 }}
                  value={addressSearchQuery}
                  onChangeText={handleAddressSearchChange}
                  placeholder="Type an address or place (worldwide)..."
                  placeholderTextColor="#999"
                />
                {addressSearchResults.length > 0 && (
                  <ScrollView style={{ marginTop: 4, maxHeight: 180, backgroundColor: '#fff', borderRadius: 8, borderWidth: 1, borderColor: '#ddd' }}>
                    {addressSearchResults.map(result => (
                      <TouchableOpacity key={result.id} onPress={() => selectAddressResult(result)} style={{ padding: 12, borderBottomWidth: 1, borderBottomColor: '#eee' }}>
                        <Text style={{ fontSize: 13, color: '#1d1d1d' }}>{result.name}</Text>
                        <Text style={{ fontSize: 11, color: '#666', marginTop: 2 }} numberOfLines={1}>{result.fullAddress}</Text>
                      </TouchableOpacity>
                    ))}
                  </ScrollView>
                )}
                {isSearchingAddress && <Text style={{ padding: 8, color: '#666', fontSize: 12 }}>Searching...</Text>}
              </View>
              {addLocationCoords && (
                <View style={{ backgroundColor: '#f5f5f5', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                  <Text style={{ fontSize: 12, color: '#666', fontFamily: Platform.OS === 'web' ? 'monospace' : undefined }}>📍 {addLocationCoords.lat.toFixed(5)}, {addLocationCoords.lng.toFixed(5)}</Text>
                </View>
              )}
              <View style={{ marginBottom: 16 }}>
                <Text style={{ fontSize: 12, fontWeight: '600', color: '#666', marginBottom: 8, textTransform: 'uppercase' }}>Name *</Text>
                <TextInput
                  style={{ backgroundColor: '#f5f5f5', borderRadius: 8, padding: 12, borderWidth: 1, borderColor: '#ddd', color: '#1d1d1d', fontSize: 14 }}
                  value={addLocationFormData.name}
                  onChangeText={(text) => setAddLocationFormData(prev => ({ ...prev, name: text }))}
                  placeholder="Enter a name for this location"
                  placeholderTextColor="#999"
                />
              </View>
              <View style={{ marginBottom: 16 }}>
                <Text style={{ fontSize: 12, fontWeight: '600', color: '#666', marginBottom: 8, textTransform: 'uppercase' }}>Category</Text>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                  {LOCATION_CATEGORIES.map(cat => (
                <TouchableOpacity
                      key={cat.id}
                      onPress={() => setAddLocationFormData(prev => ({ ...prev, category: cat.id }))}
                      style={{
                        flexDirection: 'row', alignItems: 'center', paddingVertical: 8, paddingHorizontal: 12, borderRadius: 20,
                        backgroundColor: addLocationFormData.category === cat.id ? '#00A575' : '#f0f0f0',
                        borderWidth: 1,
                        borderColor: addLocationFormData.category === cat.id ? '#00A575' : '#ddd',
                        gap: 6
                      }}
                    >
                      <Text style={{ fontSize: 14 }}>{cat.icon}</Text>
                      <Text style={{ fontSize: 12, fontWeight: '500', color: addLocationFormData.category === cat.id ? '#fff' : '#666' }}>{cat.label}</Text>
                </TouchableOpacity>
                  ))}
              </View>
              </View>
              <View style={{ marginBottom: 16 }}>
                <Text style={{ fontSize: 12, fontWeight: '600', color: '#666', marginBottom: 8, textTransform: 'uppercase' }}>Description</Text>
                <TextInput
                  style={{ backgroundColor: '#f5f5f5', borderRadius: 8, padding: 12, borderWidth: 1, borderColor: '#ddd', color: '#1d1d1d', fontSize: 14, minHeight: 72, textAlignVertical: 'top' }}
                  value={addLocationFormData.notes}
                  onChangeText={(text) => setAddLocationFormData(prev => ({ ...prev, notes: text }))}
                  placeholder="Add a description (optional)"
                  placeholderTextColor="#999"
                  multiline
                  numberOfLines={3}
                />
              </View>
              <View style={{ marginBottom: 16 }}>
              <TouchableOpacity
                  onPress={() => setAddLocationFormData(prev => ({ ...prev, isPublic: !prev.isPublic }))}
                  style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}
                >
                  <View style={{
                    width: 22,
                    height: 22,
                    borderRadius: 4,
                    borderWidth: 2,
                    borderColor: addLocationFormData.isPublic ? '#27ae60' : '#ccc',
                    backgroundColor: addLocationFormData.isPublic ? '#27ae60' : 'transparent',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {addLocationFormData.isPublic && (
                      <Text style={{ color: '#fff', fontSize: 14, fontWeight: 'bold' }}>✓</Text>
                    )}
                  </View>
                  <View>
                    <Text style={{ fontSize: 14, fontWeight: '500', color: '#1d1d1d' }}>Make this location public</Text>
                    <Text style={{ fontSize: 11, color: '#666', marginTop: 2 }}>Other users will be able to see this location</Text>
                  </View>
              </TouchableOpacity>
            </View>
            </ScrollView>
            <View style={{ padding: 16, borderTopWidth: 1, borderTopColor: '#eee', backgroundColor: '#fafafa' }}>
              <TouchableOpacity
                onPress={saveNewLocationInline}
                disabled={!addLocationFormData.name.trim() || !addLocationCoords}
                style={{
                  backgroundColor: (!addLocationFormData.name.trim() || !addLocationCoords) ? '#95a5a6' : '#27ae60',
                  paddingVertical: 14, borderRadius: 8, alignItems: 'center', marginBottom: 10
                }}
              >
                <Text style={{ color: '#fff', fontSize: 15, fontWeight: '600' }}>Save Location</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={closeAddLocationPanel} style={{ paddingVertical: 12, borderRadius: 8, alignItems: 'center', borderWidth: 1, borderColor: '#ddd' }}>
                <Text style={{ color: '#666', fontSize: 14, fontWeight: '500' }}>Cancel</Text>
              </TouchableOpacity>
            </View>
            </View>
          </View>
        </Modal>

      <FullscreenMapModal
        visible={mapFullscreen}
        onClose={() => setMapFullscreen(false)}
        center={venueMapCenter}
        label={venueData?.name}
        mapStyle={venueMapStyle}
        onMapStyleChange={setVenueMapStyle}
        showTourPlannerButton={showTourPlannerNav}
        onTourPlannerPress={() => {
          setMapFullscreen(false);
          navigation.navigate('TourPlanner');
        }}
      />

      {/* Folder Selection Modal */}
      <Modal
        visible={showFolderSelectModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => {
          setShowFolderSelectModal(false);
          setFolderModalOpenWithCreate(false);
        }}
      >
        <View style={{
          flex: 1,
          backgroundColor: 'rgba(0, 0, 0, 0.1)',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 20,
        }}>
          <View style={{
            width: '100%',
            maxWidth: 900,
            height: '90%',
            backgroundColor: theme.background.primary,
            borderRadius: 12,
            overflow: 'hidden'
          }}>
            {/* Modal Header */}
            <View style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: 20,
              borderBottomWidth: 1,
              borderBottomColor: 'rgba(0, 0, 0, 0.1)'
            }}>
              <Text style={{
                color: theme.text.primary,
                fontSize: 18,
                fontFamily: 'EduSAHand',
                fontWeight: 'bold'
              }}>
                {folderSelectModalLibraryTab === 'tours'
                  ? 'Select tour folder'
                  : 'Select folder to save venue'}
              </Text>
              <TouchableOpacity
                onPress={() => { setShowFolderSelectModal(false); setFolderModalOpenWithCreate(false); }}
                style={{
                  padding: 8,
                  borderRadius: 8,
                  backgroundColor: 'rgba(0, 0, 0, 0.1)'
                }}
              >
                <Ionicons name="close" size={24} color="#1d1d1d" />
              </TouchableOpacity>
            </View>

            <View style={{ flex: 1, margin: 20 }}>
              {userData && (
                <ProfileTabbedMediaManager
                  libraryTab={folderSelectModalLibraryTab}
                  allowFileUpload={false}
                  allowProfileDropImport
                  userData={userData}
                  setUser={setUser}
                  openWithCreateFolder={folderModalOpenWithCreate}
                  onEditReport={() => { }}
                  onDeleteReport={() => { }}
                  onViewVenue={(venue) => {
                    setShowFolderSelectModal(false);
                    navigation.navigate('Venue', { venueId: venue.id });
                  }}
                  onAddVenuePhoto={() => { }}
                  onDeleteVenuePhoto={() => { }}
                  onFolderSelect={handleSaveVenueToFolder}
                  onEditRoomEqSubmission={(sub) => {
                    setShowFolderSelectModal(false);
                    navigation.navigate('Venue', { venueId: sub.venueId, editRoomEqSubmissionId: sub.id, editRoomEqSubmission: sub });
                  }}
                  venueToSave={{ ...venueData, id: venueId }}
                />
              )}
            </View>
          </View>
        </View>
      </Modal>
    </View>

    {toast && (
      <Toast
        key={toast.id || toast.message}
        message={toast.message}
        type={toast.type}
        onHide={() => setToast(null)}
      />
    )}
    </>
  );
}
